import { createClient, type Session, type SupabaseClient } from "@supabase/supabase-js";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type Dispatch, type FormEvent, type SetStateAction } from "react";

import { ApiError, KitchenHubApi } from "./api";
import { demoDepartments, demoIdentityAdministration, demoProductDetails, demoReferences, demoSession } from "./demo-data";
import { Icon, Mark, type IconName } from "./icons";
import { getRuntimeConfig, type RuntimeConfig } from "./runtime";
import { InactivityLockController } from "./inactivity";
import { readSharedDeviceLock, writeSharedDeviceLock, readSwitchToken, writeSwitchToken } from "./lock-state";
import { isValidTotpCode, selectMfaGateMode, type MfaGateMode } from "./mfa";
import { readSelectedSite, writeSelectedSite } from "./selected-site";
import { REVIEW_PAGE_SIZE, REVIEW_STORAGE_STATES, countByStorageState, countInKitchenUse, countProductsByType, draftsByProduct, filterReviewRules, FOOD_TYPE_LABELS, kitchenUseLabel, type ReviewScope, type ReviewStorageFilter, type ReviewTypeFilter } from "./shelf-life-review";
import { readPinSession, readPinSessionOrgCode, readPinSessionSiteCode, writePinSession, writePinSessionOrgCode, writePinSessionSiteCode } from "./pin-session-state";
import { PinSignInApi, type PinSignInMember, type PinSignInOrganization, type PinSignInSite } from "./pin-sign-in-api";
import { PERMISSION_GROUPS, rolesForTable, unlistedPermissions } from "./role-permissions";
import { deviceAuthStorage, readRememberDevice, readRememberedEmail, writeRememberDevice } from "./remember-device";
import { hasStoredPasswordSession, matchPinSignInPath, pinSignInLink, readRememberedPinLink, rememberPinLink } from "./pin-sign-in-path";
import { newPasswordProblem, PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from "./password-change";
import { passwordCheckMessage } from "./password-check";
import { canReportNotPrinted, historyStatusLabel, outcomeConfirmedToast, printResultToast, reprintResultToast } from "./print-feedback";
import type {
  AllergenInput,
  Category,
  CalculationMethod,
  DateMarkType,
  DurationUnit,
  DraftShelfLifeRuleForReview,
  Department,
  IdentityAdministration,
  ManagedRole,
  ManagedRoleInput,
  MemberRecord,
  OrganizationRoleAssignment,
  LabelPreview as LabelPreviewData,
  Product,
  NewProductAllergen,
  ProductAllergen,
  ProductDetail,
  ProductInput,
  ReferenceData,
  SessionInfo,
  ShelfLifeDraftInput,
  ShelfLifeRevisionInput,
  ShelfLifeRule,
  StorageState,
  StandardLabelType,
  UnitOfMeasure,
  BridgeEnrolmentGrant,
  ManagedPrintBridge,
  OperationalPrinter,
  PrintOptions,
  PrintProduct,
  ProductDatePreview,
  PrepList,
  PrepListEligibleProduct,
  PrepListPrintPreview,
  PpdsLabelTemplate,
  PpdsLabelPreview,
  PpdsLabelMode,
  PpdsDateMarkType,
  PpdsShelfLifeUnit,
  Printer,
  LabelTemplateOption,
  PrinterTemplateAssignment,
  PrintJobHistoryEntry,
  OrganizationSettings,
  SiteSettings,
  BuffetMenu,
  BuffetEligibleProduct,
  BuffetLabelPreview,
  DashboardSnapshot,
  HealthStatus,
  PrinterSummary,
} from "./types";

const runtime = getRuntimeConfig();

/** Maximum product cards rendered at once. See the `visible` list in Workspace. */
const PRODUCT_CARD_LIMIT = 200;

// A mouse wheel only scrolls up and down, so on a desktop a sideways strip
// (the supplier chips) can't be reached without a trackpad. Turn vertical
// wheel movement into sideways scrolling while the pointer is over the strip,
// and let the page scroll as normal once the strip hits either end.
function wheelScrollsSideways(element: HTMLElement | null) {
  if (!element || element.dataset.wheelSideways) return;
  element.dataset.wheelSideways = "true";
  element.addEventListener("wheel", (event) => {
    if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
    const max = element.scrollWidth - element.clientWidth;
    if (max <= 0) return;
    const next = Math.min(max, Math.max(0, element.scrollLeft + event.deltaY));
    // scrollLeft can be fractional at the end, so allow a pixel of slack.
    if (Math.abs(next - element.scrollLeft) < 1) return;
    event.preventDefault();
    element.scrollLeft = next;
  }, { passive: false });
}

// Print History's status filter, in the words staff see on each job. A real
// label is stored as "uncertain" (Windows can't say it came out) but reads as
// "sent to printer" everywhere.
const PRINT_STATUS_FILTERS: [string, string][] = [
  ["uncertain", "Sent to printer"],
  ["completed", "Completed"],
  ["pending", "Waiting to send"],
  ["claimed", "Picked up by the PC"],
  ["rendered", "Preparing"],
  ["sent_to_spooler", "Sending"],
  ["retry_wait", "Waiting to retry"],
  ["failed", "Failed"],
  ["cancelled", "Cancelled"],
  ["expired", "Expired"],
];

// "Prep" heads every product list (matches the server and Quick Print).
function isPrep(item: { name: string }): boolean {
  return item.name.trim().toLowerCase() === "prep";
}

function byCatalogueOrder(a: { name: string }, b: { name: string }): number {
  return Number(isPrep(b)) - Number(isPrep(a)) || a.name.localeCompare(b.name);
}

function safeStorage(get: () => Storage): Storage | null {
  try { return get(); } catch { return null; }
}

/** A password (or PIN) box with an eye button to check what's been typed. */
function PasswordField({ id, label, value, onChange, autoComplete, autoFocus, minLength, maxLength, inputMode, pattern }: {
  id: string; label: string; value: string; onChange: (value: string) => void; autoComplete: string;
  autoFocus?: boolean; minLength?: number; maxLength?: number; inputMode?: "numeric"; pattern?: string;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <label htmlFor={id}>{label}
      <span className="password-field">
        <input
          id={id} type={visible ? "text" : "password"} autoComplete={autoComplete} autoFocus={autoFocus} required
          minLength={minLength} maxLength={maxLength} inputMode={inputMode} pattern={pattern}
          value={value} onChange={(event) => onChange(event.target.value)}
        />
        <button
          type="button" className="password-toggle" aria-controls={id} aria-pressed={visible}
          aria-label={visible ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
          onClick={() => setVisible((current) => !current)}
        ><Icon name={visible ? "eyeOff" : "eye"} size={18} /></button>
      </span>
    </label>
  );
}

function buildSupabase(config: RuntimeConfig): SupabaseClient | null {
  if (!config.supabaseUrl || !config.supabasePublishableKey) return null;
  return createClient(config.supabaseUrl, config.supabasePublishableKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      // "Remember this device": localStorage when ticked, sessionStorage when not.
      storage: deviceAuthStorage(safeStorage(() => window.localStorage), safeStorage(() => window.sessionStorage)),
    },
  });
}

// A throwaway client used only to re-verify a password (via signInWithPassword's own
// success/failure) without ever touching or replacing the app's real, already-AAL2 session.
function buildPasswordVerifier(config: RuntimeConfig): SupabaseClient | null {
  if (!config.supabaseUrl || !config.supabasePublishableKey) return null;
  return createClient(config.supabaseUrl, config.supabasePublishableKey, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
    },
  });
}

export default function App() {
  if (runtime.demoMode) {
    return <Workspace demoMode profile={demoSession} />;
  }
  const hasPinSession = readPinSession(window.localStorage) !== null;
  // Opened at `/` (e.g. the home-screen icon) with no PIN session: go back to
  // the PIN link this device last signed in from, unless an email-and-password
  // account is signed in here.
  const pinPath = matchPinSignInPath(window.location.pathname)
    ?? (!hasPinSession && !hasStoredPasswordSession(window.localStorage) && !hasStoredPasswordSession(safeStorage(() => window.sessionStorage))
      ? readRememberedPinLink(window.localStorage) : null);
  const orgCode = pinPath?.orgCode ?? (hasPinSession ? readPinSessionOrgCode(window.localStorage) : null);
  const siteCode = pinPath ? pinPath.siteCode : (hasPinSession ? readPinSessionSiteCode(window.localStorage) : null);
  if (orgCode) return <PinSignInBoundary orgCode={orgCode} siteCode={siteCode} memberId={pinPath?.memberId ?? null} />;
  const supabase = buildSupabase(runtime);
  if (!supabase) return <ConfigurationNeeded />;
  return <AuthenticationBoundary supabase={supabase} />;
}

/**
 * The direct PIN-only entry point for staff with no Supabase account —
 * reached by bookmarking a shared `/o/<org-code>` link rather than through
 * the email+password screen. Chefs stay signed in indefinitely on their own
 * phone once they've entered their PIN once; only Head Chef/Sous Chef and
 * the owner use the Supabase flow (AuthenticationBoundary), mainly on a
 * shared PC, for admin actions.
 */
function PinSignInBoundary({ orgCode, siteCode, memberId }: { orgCode: string; siteCode: string | null; memberId: string | null }) {
  const [session, setSession] = useState<{ token: string; expiresAt: string } | null>(() => {
    const token = readPinSession(window.localStorage);
    return token ? { token, expiresAt: "" } : null;
  });

  function acceptSession(token: string, expiresAt: string, signedInMemberId?: string) {
    if (signedInMemberId) rememberPinLink(window.localStorage, orgCode, siteCode, signedInMemberId);
    writePinSession(window.localStorage, token, expiresAt);
    writePinSessionOrgCode(window.localStorage, orgCode);
    writePinSessionSiteCode(window.localStorage, siteCode);
    setSession({ token, expiresAt });
  }

  function signOut() {
    writePinSession(window.localStorage, null);
    writePinSessionOrgCode(window.localStorage, null);
    writePinSessionSiteCode(window.localStorage, null);
    setSession(null);
  }

  if (session) {
    return <PinSessionWorkspace token={session.token} onTokenRefreshed={acceptSession} onSignOut={signOut} />;
  }
  return <PinSignInFlow orgCode={orgCode} siteCode={siteCode} memberId={memberId} onSignedIn={acceptSession} />;
}

type PinSignInStep =
  | { kind: "loading-organization" }
  | { kind: "organization-error"; message: string }
  | { kind: "choosing-member"; organization: PinSignInOrganization; members: PinSignInMember[] }
  | { kind: "entering-pin"; organization: PinSignInOrganization; members: PinSignInMember[]; member: PinSignInMember; pin: string; error: string | null; submitting: boolean };

/** For owners/admins who land on a PIN pad on a device that remembered a PIN
 * link: forget it and go to the email and password screen. */
function switchToEmailSignIn() {
  rememberPinLink(window.localStorage, null);
  writePinSessionOrgCode(window.localStorage, null);
  writePinSessionSiteCode(window.localStorage, null);
  window.location.assign("/");
}

function PinSignInFlow({ orgCode, siteCode, memberId, onSignedIn }: { orgCode: string; siteCode: string | null; memberId: string | null; onSignedIn: (token: string, expiresAt: string, signedInMemberId?: string) => void }) {
  const pinApi = useMemo(() => new PinSignInApi(runtime.apiUrl), []);
  const [step, setStep] = useState<PinSignInStep>({ kind: "loading-organization" });
  const [site, setSite] = useState<PinSignInSite | null>(null);
  const [showPin, setShowPin] = useState(false);
  const [search, setSearch] = useState("");
  const [linkNotice, setLinkNotice] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void pinApi.resolveOrganization(orgCode)
      .then(async (organization) => {
        const resolvedSite = siteCode ? await pinApi.resolveSite(organization.id, siteCode) : null;
        const members = await pinApi.listMembers(organization.id, resolvedSite?.id);
        if (!active) return;
        setSite(resolvedSite);
        // A personal link opens straight to that person's PIN pad. If they've
        // left, moved site or have no PIN yet, fall back to the name list.
        const linked = memberId ? members.find((member) => member.id === memberId) : undefined;
        if (linked) {
          setStep({ kind: "entering-pin", organization, members, member: linked, pin: "", error: null, submitting: false });
        } else {
          if (memberId) setLinkNotice("This personal sign-in link didn’t match anyone here — choose your name instead.");
          setStep({ kind: "choosing-member", organization, members });
        }
      })
      .catch((reason: unknown) => {
        if (active) setStep({ kind: "organization-error", message: messageFor(reason) });
      });
    return () => { active = false; };
  }, [orgCode, siteCode, memberId, pinApi]);

  if (step.kind === "loading-organization") return <LoadingScreen message="Finding your kitchen…" />;
  if (step.kind === "organization-error") {
    return (
      <main className="gate-screen">
        <section className="gate-card setup-card">
          <Icon name="warning" size={28} />
          <h1>We couldn’t find this kitchen.</h1>
          <p>{step.message}</p>
          <p className="muted">Ask your Head Chef or Sous Chef for the correct sign-in link.</p>
          <button type="button" className="text-button pin-email-link" onClick={switchToEmailSignIn}>Sign in with email and password instead</button>
        </section>
      </main>
    );
  }

  if (step.kind === "choosing-member") {
    const visible = step.members.filter((member) => member.display_name.toLowerCase().includes(search.toLowerCase()));
    return (
      <main className="gate-screen">
        <section className="gate-card">
          <Mark />
          <p className="eyebrow">{site ? `${step.organization.name} · ${site.name}` : step.organization.name}</p>
          <h1>Who’s printing?</h1>
          {linkNotice && <p className="muted" role="status">{linkNotice}</p>}
          <input
            className="member-search" type="search" placeholder="Search your name…"
            value={search} onChange={(event) => setSearch(event.target.value)}
          />
          <div className="site-choice-grid">
            {visible.map((member) => (
              <button
                key={member.id} type="button" className="site-choice"
                onClick={() => setStep({ kind: "entering-pin", organization: step.organization, members: step.members, member, pin: "", error: null, submitting: false })}
              >
                <strong>{member.display_name}</strong>
              </button>
            ))}
            {visible.length === 0 && <p className="muted">No one matches “{search}”.</p>}
          </div>
          <button type="button" className="text-button pin-email-link" onClick={switchToEmailSignIn}>Sign in with email and password instead</button>
        </section>
      </main>
    );
  }

  const { organization, members, member, pin, error, submitting } = step;

  function pressDigit(digit: string) {
    if (step.kind !== "entering-pin" || step.submitting || step.pin.length >= 6) return;
    const next = step.pin + digit;
    setStep({ ...step, pin: next, error: null });
    // PINs are 4-6 digits (validated server-side), so length alone can't say
    // a shorter PIN is complete — only auto-submit once no more digits fit.
    // Shorter PINs are confirmed explicitly with the "Sign in" button below.
    if (next.length === 6) void trySubmit(next);
  }

  function backspace() {
    if (step.kind !== "entering-pin") return;
    setStep({ ...step, pin: step.pin.slice(0, -1), error: null });
  }

  async function trySubmit(candidate: string) {
    setStep((current) => (current.kind === "entering-pin" ? { ...current, submitting: true } : current));
    try {
      const session = await pinApi.signIn(organization.id, member.id, candidate);
      // Signed in from a site's own link, so start them at that site.
      if (site) writeSelectedSite(window.localStorage, organization.id, site.id);
      onSignedIn(session.access_token, session.expires_at, member.id);
    } catch (reason) {
      setStep({ kind: "entering-pin", organization, members, member, pin: "", error: messageFor(reason), submitting: false });
    }
  }

  return (
    <main className="gate-screen">
      <section className="gate-card pin-pad-card">
        <Mark />
        <p className="eyebrow">{organization.name}</p>
        <h1>{member.display_name}</h1>
        <p className="muted">Enter your PIN</p>
        <div className="pin-dots-row">
          <div className="pin-dots" role="status" aria-label={showPin ? `PIN entered: ${pin.split("").join(" ") || "none"}` : `${pin.length} of up to 6 digits entered`}>
            {Array.from({ length: 6 }, (_, index) => (
              showPin && index < pin.length
                ? <span key={index} className="pin-dot shown">{pin[index]}</span>
                : <span key={index} className={index < pin.length ? "pin-dot filled" : "pin-dot"} />
            ))}
          </div>
          <button
            type="button" className="password-toggle pin-show-toggle" aria-pressed={showPin}
            aria-label={showPin ? "Hide PIN" : "Show PIN"} onClick={() => setShowPin((current) => !current)}
          ><Icon name={showPin ? "eyeOff" : "eye"} size={20} /></button>
        </div>
        {error && <p className="form-error" role="alert">{error}</p>}
        <div className="pin-pad-grid">
          {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((digit) => (
            <button key={digit} type="button" disabled={submitting} onClick={() => pressDigit(digit)}>{digit}</button>
          ))}
          <button type="button" className="text-button" disabled={submitting} onClick={() => setStep({ kind: "choosing-member", organization, members })}>Not you?</button>
          <button type="button" disabled={submitting} onClick={() => pressDigit("0")}>0</button>
          <button type="button" className="text-button" disabled={submitting || pin.length === 0} onClick={backspace} aria-label="Delete last digit">⌫</button>
        </div>
        <button
          type="button" className="primary-button wide pin-pad-submit"
          disabled={submitting || pin.length < 4} onClick={() => void trySubmit(pin)}
        >
          {submitting ? "Checking…" : "Sign in"}
        </button>
        <button type="button" className="text-button pin-email-link" disabled={submitting} onClick={switchToEmailSignIn}>Sign in with email and password instead</button>
      </section>
    </main>
  );
}

function PinSessionWorkspace({
  token, onTokenRefreshed, onSignOut,
}: {
  token: string;
  onTokenRefreshed: (token: string, expiresAt: string) => void;
  onSignOut: () => void;
}) {
  const [profile, setProfile] = useState<SessionInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const api = useMemo(() => new KitchenHubApi(runtime.apiUrl, token), [token]);

  useEffect(() => {
    void api.session().then(setProfile).catch((reason: unknown) => setError(messageFor(reason)));
  }, [api]);

  useEffect(() => {
    // Opportunistic refresh on load: this is what makes "effectively indefinite"
    // actually work — an in-use phone always renews before its 30-day token
    // would expire. A revoked session (PIN reset, deactivation, explicit
    // sign-out) simply fails to refresh; the session() call above then
    // surfaces the 401 as a normal FatalState.
    const pinApi = new PinSignInApi(runtime.apiUrl);
    void pinApi.refresh(token).then((session) => onTokenRefreshed(session.access_token, session.expires_at)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) return <FatalState message={error} onSignOut={onSignOut} />;
  if (!profile) return <LoadingScreen message="Checking your access…" />;
  if (!profile.organization_id) return <FatalState message="Your member account is not linked to an organisation." onSignOut={onSignOut} />;
  return <Workspace profile={profile} api={api} email={profile.member_display_name ?? "Kitchen Hub member"} onSignOut={onSignOut} />;
}

function ConfigurationNeeded() {
  return (
    <main className="gate-screen">
      <section className="gate-card setup-card">
        <Mark />
        <p className="eyebrow">Kitchen Hub 2.0</p>
        <h1>Connect the kitchen.</h1>
        <p>
          Add the Supabase URL and publishable key to <code>apps/web/.env.local</code>, or run the
          explicit preview command from the README to see the product with sample data.
        </p>
        <div className="config-list">
          <code>VITE_SUPABASE_URL</code>
          <code>VITE_SUPABASE_PUBLISHABLE_KEY</code>
        </div>
      </section>
    </main>
  );
}

function AuthenticationBoundary({ supabase }: { supabase: SupabaseClient }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    void supabase.auth.getSession().then(({ data }) => {
      if (active) {
        setSession(data.session);
        setLoading(false);
      }
    });
    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      setLoading(false);
    });
    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, [supabase]);

  if (loading) return <LoadingScreen message="Opening the kitchen…" />;
  if (!session) return <Login supabase={supabase} />;
  return <MfaBoundary supabase={supabase} session={session} onSession={setSession} />;
}

function MfaBoundary({
  supabase,
  session,
  onSession,
}: {
  supabase: SupabaseClient;
  session: Session;
  onSession: (session: Session | null) => void;
}) {
  const [mode, setMode] = useState<MfaGateMode | null>(null);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    void Promise.all([
      supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
      supabase.auth.mfa.listFactors(),
    ]).then(([assurance, factors]) => {
      if (!active) return;
      const failure = assurance.error ?? factors.error;
      if (failure || !assurance.data || !factors.data) {
        setError(failure?.message ?? "Kitchen Hub could not verify the account security level.");
        return;
      }
      const verified = factors.data.totp.filter((factor) => factor.status === "verified");
      setFactorId(verified[0]?.id ?? null);
      setMode(selectMfaGateMode(assurance.data.currentLevel, assurance.data.nextLevel, verified.length));
    });
    return () => { active = false; };
  }, [session.access_token, supabase]);

  async function enroll() {
    setSubmitting(true);
    setError(null);
    const result = await supabase.auth.mfa.enroll({
      factorType: "totp",
      friendlyName: `Kitchen Hub ${runtime.environment}`,
    });
    setSubmitting(false);
    if (result.error) {
      setError(result.error.message);
      return;
    }
    setFactorId(result.data.id);
    setQrCode(result.data.totp.qr_code);
  }

  async function verify(event: FormEvent) {
    event.preventDefault();
    if (!factorId || !isValidTotpCode(code)) {
      setError("Enter the current six-digit code from your authenticator app.");
      return;
    }
    setSubmitting(true);
    setError(null);
    const challenge = await supabase.auth.mfa.challenge({ factorId });
    if (challenge.error) {
      setCode("");
      setSubmitting(false);
      setError(challenge.error.message);
      return;
    }
    const verification = await supabase.auth.mfa.verify({
      factorId,
      challengeId: challenge.data.id,
      code,
    });
    setCode("");
    setSubmitting(false);
    if (verification.error) {
      setError(verification.error.message);
      return;
    }
    const refreshed = await supabase.auth.getSession();
    if (refreshed.error || !refreshed.data.session) {
      setError(refreshed.error?.message ?? "The verified session could not be refreshed.");
      return;
    }
    onSession(refreshed.data.session);
    setMode("ready");
  }

  if (mode === "ready") {
    return <LiveWorkspace
      accessToken={session.access_token}
      email={session.user.email ?? "Kitchen Hub member"}
      onSignOut={() => void supabase.auth.signOut({ scope: "local" })}
      onPasswordChanged={async () => {
        // The "must change password" flag is signed into the access token, so
        // fetch a fresh token now that the server has cleared it.
        const refreshed = await supabase.auth.refreshSession();
        if (refreshed.error || !refreshed.data.session) return false;
        onSession(refreshed.data.session);
        return true;
      }}
    />;
  }
  if (!mode && !error) return <LoadingScreen message="Checking account security…" />;

  return (
    <main className="gate-screen mfa-screen">
      <section className="gate-card mfa-card">
        <Mark />
        <p className="eyebrow">Administrator security</p>
        <h1>{mode === "enroll" ? "Secure your account" : "Verify it’s you"}</h1>
        {mode === "enroll" && !qrCode && <>
          <p>Kitchen Hub requires an authenticator app before protected administration is available.</p>
          <button className="primary-button wide" disabled={submitting} onClick={() => void enroll()}>
            {submitting ? "Preparing…" : "Set up authenticator"}
          </button>
        </>}
        {mode === "enroll" && qrCode && <>
          <p>Scan this QR code with your authenticator app. Keep the QR code and authenticator details private.</p>
          <div className="mfa-qr"><img src={qrCode} alt="Authenticator enrolment QR code" /></div>
        </>}
        {mode === "challenge" && <p>Enter the current code from your enrolled authenticator app.</p>}
        {factorId && (mode === "challenge" || qrCode) && <form onSubmit={verify}>
          <label htmlFor="mfa-code">Six-digit code<input id="mfa-code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required value={code} onChange={(event) => setCode(event.target.value)} /></label>
          {error && <p className="form-error" role="alert">{error}</p>}
          <button className="primary-button wide" disabled={submitting}>{submitting ? "Verifying…" : "Verify and continue"}</button>
        </form>}
        {error && !factorId && <p className="form-error" role="alert">{error}</p>}
        <button className="text-button" onClick={() => void supabase.auth.signOut({ scope: "local" })}>Sign out</button>
      </section>
    </main>
  );
}

function Login({ supabase }: { supabase: SupabaseClient }) {
  const [email, setEmail] = useState(() => readRememberedEmail(safeStorage(() => window.localStorage)));
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(() => readRememberDevice(safeStorage(() => window.localStorage)));
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    // Set before signing in so the new session lands in the right storage.
    writeRememberDevice(safeStorage(() => window.localStorage), remember, email);
    const result = await supabase.auth.signInWithPassword({ email, password });
    setSubmitting(false);
    if (result.error) setError(result.error.message);
  }

  return (
    <main className="gate-screen login-screen">
      <section className="login-story" aria-label="Kitchen Hub introduction">
        <div className="login-brand"><Mark /><span>Kitchen Hub</span></div>
        <div>
          <p className="eyebrow light">NCC Hotel Group</p>
          <h1>Every label.<br />Every detail.<br /><em>In its place.</em></h1>
          <p>One calm workspace for safer prep, clearer labelling and kitchen accountability.</p>
        </div>
        <div className="trust-line"><Icon name="shield" /> Secure named account</div>
      </section>
      <section className="gate-card login-card">
        <p className="eyebrow">Welcome back</p>
        <h2>Sign in to your kitchen</h2>
        <p className="muted">Use your Kitchen Hub manager account.</p>
        <form onSubmit={submit}>
          <label htmlFor="login-email">Email address<input id="login-email" type="email" autoComplete="email" required value={email} onChange={(event) => setEmail(event.target.value)} /></label>
          <PasswordField id="login-password" label="Password" autoComplete="current-password" value={password} onChange={setPassword} />
          <label className="remember-device" htmlFor="login-remember">
            <input id="login-remember" type="checkbox" checked={remember} onChange={(event) => setRemember(event.target.checked)} />
            <span><strong>Remember this device</strong><small>Stay signed in and keep your email here. Untick on a shared kitchen PC — you'll be signed out when the browser closes.</small></span>
          </label>
          {error && <p className="form-error">{error}</p>}
          <button className="primary-button wide" disabled={submitting}>{submitting ? "Signing in…" : "Sign in"}</button>
        </form>
        <p className="login-note"><Icon name="shield" size={16} /> Permissions and membership are verified by Kitchen Hub after sign-in.</p>
      </section>
    </main>
  );
}

function LiveWorkspace({
  accessToken, email, onSignOut, onPasswordChanged,
}: {
  accessToken: string;
  email: string;
  onSignOut: () => void;
  onPasswordChanged: () => Promise<boolean>;
}) {
  const [profile, setProfile] = useState<SessionInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const api = useMemo(() => {
    const instance = new KitchenHubApi(runtime.apiUrl, accessToken);
    const persistedSwitchToken = readSwitchToken(window.sessionStorage);
    if (persistedSwitchToken) instance.restoreSwitchToken(persistedSwitchToken);
    return instance;
  }, [accessToken]);

  useEffect(() => {
    void api.session().then(setProfile).catch((reason: unknown) => setError(messageFor(reason)));
  }, [api]);

  if (error) return <FatalState message={error} onSignOut={onSignOut} />;
  if (!profile) return <LoadingScreen message="Checking your access…" />;
  if (!profile.organization_id) return <FatalState message="Your member account is not linked to an organisation." onSignOut={onSignOut} />;
  if (profile.password_change_required) {
    return <PasswordChangeScreen api={api} email={email} onSignOut={onSignOut} onPasswordChanged={onPasswordChanged} />;
  }
  return <Workspace profile={profile} api={api} email={email} onSignOut={onSignOut} />;
}

/**
 * Shown on first sign-in (and after an admin reset): the password the admin
 * handed over is replaced with one only the member knows. The server refuses
 * every other request until this is done, so it can't be skipped.
 */
function PasswordChangeScreen({
  api, email, onSignOut, onPasswordChanged,
}: {
  api: KitchenHubApi;
  email: string;
  onSignOut: () => void;
  onPasswordChanged: () => Promise<boolean>;
}) {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const problem = newPasswordProblem(password, confirmation);
    if (problem) {
      setError(problem);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.changeOwnPassword(password);
    } catch (reason) {
      setSubmitting(false);
      setError(messageFor(reason));
      return;
    }
    if (!(await onPasswordChanged())) {
      setSubmitting(false);
      setError("Your new password is saved, but Kitchen Hub couldn’t refresh your sign-in. Sign out and sign back in with your new password.");
    }
  }

  return (
    <main className="gate-screen">
      <section className="gate-card login-card">
        <Mark />
        <p className="eyebrow">First sign-in</p>
        <h1>Choose your own password</h1>
        <p className="muted">You signed in as {email} with a password someone else set. Replace it with one only you know.</p>
        <form onSubmit={submit}>
          <PasswordField id="new-password" label="New password" autoComplete="new-password" minLength={PASSWORD_MIN_LENGTH} maxLength={PASSWORD_MAX_LENGTH} value={password} onChange={setPassword} />
          <PasswordField id="confirm-password" label="Type it again" autoComplete="new-password" value={confirmation} onChange={setConfirmation} />
          <p className="muted">At least {PASSWORD_MIN_LENGTH} characters.</p>
          {error && <p className="form-error" role="alert">{error}</p>}
          <button className="primary-button wide" disabled={submitting}>{submitting ? "Saving…" : "Save password and continue"}</button>
        </form>
        <button className="text-button" onClick={onSignOut}>Sign out</button>
      </section>
    </main>
  );
}

interface WorkspaceProps {
  profile: SessionInfo;
  demoMode?: boolean;
  api?: KitchenHubApi;
  email?: string;
  onSignOut?: () => void;
}

/**
 * The site chosen once at sign-in. Every site-scoped screen reads it from here
 * rather than asking again with its own dropdown.
 */
const SelectedSiteContext = createContext<string>("");

// Shown at the foot of every page. The sign-in and PIN screens show the same
// text via .gate-screen::after in styles.css -- keep the two in step.
const COPYRIGHT_NOTICE = "Kitchen Hub · © 2026 Callum Keane";

/** Most labels a single print from the catalogue can ask for. */
const MAX_LABEL_QUANTITY = 20;

/** Keeps a typed quantity in range, and leaves the last value while the box
 * is empty mid-edit instead of snapping back to 1. */
function clampLabelQuantity(typed: string, current: number): number {
  const digits = typed.replace(/\D/g, "");
  if (!digits) return current;
  return Math.max(1, Math.min(MAX_LABEL_QUANTITY, Number(digits)));
}

function useSelectedSite(): string {
  return useContext(SelectedSiteContext);
}

function SitePickerScreen({
  sites,
  current,
  onChoose,
  onCancel,
}: {
  sites: [string, string][];
  current: string;
  onChoose: (siteId: string) => void;
  onCancel?: () => void;
}) {
  return (
    <main className="gate-screen">
      <section className="gate-card">
        <Mark />
        <p className="eyebrow">Kitchen Hub 2.0</p>
        <h1>Which site are you in?</h1>
        <p className="muted">
          This sets the printers, labels and print history for the whole session. It is remembered on
          this device until you change it.
        </p>
        <div className="site-choice-grid">
          {sites.map(([id, name]) => (
            <button
              key={id}
              type="button"
              className={id === current ? "site-choice site-choice-current" : "site-choice"}
              onClick={() => onChoose(id)}
            >
              <strong>{name}</strong>
              {id === current && <small>Current site</small>}
            </button>
          ))}
        </div>
        {onCancel && <button className="text-button" onClick={onCancel}>Keep current site</button>}
      </section>
    </main>
  );
}

function Workspace({ profile, demoMode = false, api, email = "Alex Morgan", onSignOut }: WorkspaceProps) {
  const [activeProfile, setActiveProfile] = useState(profile);
  // A site-level role (e.g. Head Chef at one hotel) arrives in site_permissions,
  // not permissions. Show a control if the member holds the permission at any
  // site -- the server still checks the exact site on every action.
  const grantedPermissions = useMemo(
    () => new Set([...activeProfile.permissions, ...Object.values(activeProfile.site_permissions ?? {}).flat()]),
    [activeProfile],
  );
  const [locked, setLocked] = useState(
    () => !demoMode && readSharedDeviceLock(window.sessionStorage),
  );
  const [switchableMembers, setSwitchableMembers] = useState(
    demoMode ? demoIdentityAdministration.members.filter((member) => member.active).map(({ id, display_name }) => ({ id, display_name })) : [],
  );
  const inactivity = useRef<InactivityLockController | null>(null);
  const [references, setReferences] = useState<ReferenceData | null>(demoMode ? demoReferences : null);
  const [departments, setDepartments] = useState<Department[]>(demoMode ? structuredClone(demoDepartments) : []);
  const [headerPrinters, setHeaderPrinters] = useState<OperationalPrinter[]>([]);
  const [products, setProducts] = useState<Product[]>(demoMode ? demoProductDetails.map((item) => item.product) : []);
  const [details, setDetails] = useState<Record<string, ProductDetail>>(
    demoMode ? Object.fromEntries(demoProductDetails.map((item) => [item.product.id, item])) : {},
  );
  const [containsAllergens, setContainsAllergens] = useState<Record<string, string[]>>(
    demoMode
      ? Object.fromEntries(demoProductDetails.map((item) => [
          item.product.id,
          item.allergens.filter((allergen) => allergen.relation === "contains").map((allergen) => allergen.name),
        ]))
      : {},
  );
  const [selectedId, setSelectedId] = useState<string | null>(demoMode ? demoProductDetails[0].product.id : null);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<string>("all");
  const [catalogueStatus, setCatalogueStatus] = useState<"active" | "archived">("active");
  const [detailTab, setDetailTab] = useState<"overview" | "allergens" | "shelf-life">("overview");
  const [editor, setEditor] = useState<"create" | "edit" | null>(null);
  const [allergenEditor, setAllergenEditor] = useState<ProductAllergen | "new" | null>(null);
  const [shelfLifeEditor, setShelfLifeEditor] = useState(false);
  const [shelfLifeRevision, setShelfLifeRevision] = useState<ShelfLifeRule | null>(null);
  const [categoryManager, setCategoryManager] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PRODUCT_CARD_LIMIT);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(!demoMode);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [workspaceView, setWorkspaceView] = useState<WorkspaceView>("catalogue");
  const [exportingCatalogue, setExportingCatalogue] = useState(false);
  const organizationId = activeProfile.organization_id;
  const sites = useMemo<[string, string][]>(
    () => [...new Map(departments.map((item) => [item.site_id, item.site_name])).entries()],
    [departments],
  );
  const [siteId, setSiteId] = useState<string>(() =>
    demoMode ? "" : readSelectedSite(window.localStorage, organizationId ?? "") ?? "",
  );
  const [changingSite, setChangingSite] = useState(false);

  const chooseSite = useCallback((next: string) => {
    setSiteId(next);
    if (!demoMode) writeSelectedSite(window.localStorage, organizationId ?? "", next);
    setChangingSite(false);
  }, [demoMode, organizationId]);

  useEffect(() => {
    if (!sites.length) return;
    // One site is not a question worth asking.
    if (sites.length === 1) {
      if (siteId !== sites[0][0]) chooseSite(sites[0][0]);
      return;
    }
    // A remembered site that has since been removed must not silently scope
    // printing to a site this member can no longer see.
    if (siteId && !sites.some(([id]) => id === siteId)) {
      setSiteId("");
      if (!demoMode) writeSelectedSite(window.localStorage, organizationId ?? "", null);
    }
  }, [chooseSite, demoMode, organizationId, siteId, sites]);

  const siteName = sites.find(([id]) => id === siteId)?.[1] ?? "";

  useEffect(() => {
    const controller = new InactivityLockController(
      activeProfile.inactivity_timeout_minutes * 60_000,
      () => {
        api?.clearSharedDeviceUser();
        if (!demoMode) {
          writeSharedDeviceLock(window.sessionStorage, true);
          writeSwitchToken(window.sessionStorage, null);
        }
        setLocked(true);
        if (!demoMode && api && organizationId) {
          void api.switchableMembers(organizationId).then(setSwitchableMembers).catch((reason: unknown) => setError(messageFor(reason)));
        }
      },
    );
    inactivity.current = controller;
    controller.start();
    return () => controller.stop();
  }, [activeProfile.inactivity_timeout_minutes, api, demoMode, organizationId]);

  useEffect(() => {
    if (demoMode || !api || !organizationId) return;
    void api.switchableMembers(organizationId).then(setSwitchableMembers).catch((reason: unknown) => setError(messageFor(reason)));
  }, [api, demoMode, organizationId]);

  // Departments decide which sites this member can choose between, so they
  // load on their own: bundled with the product list, one refusal left a
  // site-level member with no sites and no way to pick one.
  useEffect(() => {
    if (demoMode || !api || !organizationId) return;
    let active = true;
    void api.references(organizationId)
      .then((referenceData) => active && setReferences(referenceData))
      .catch((reason: unknown) => active && setError(messageFor(reason)));
    // Never keep the previous person's list after Switch user: clear it, then
    // load what this member's own sites allow (the server filters by site).
    setDepartments([]);
    if (grantedPermissions.has("departments.view")) {
      void api.departments(organizationId)
        .then((departmentData) => active && setDepartments(departmentData))
        .catch((reason: unknown) => active && setError(messageFor(reason)));
    }
    return () => { active = false; };
  }, [grantedPermissions, api, demoMode, organizationId]);

  // Without an organisation-wide products.view (a Head Chef at one or two
  // hotels), the whole-catalogue list is refused, so wait for a site: it is
  // chosen automatically for one site, or on the site picker for several.
  const productsNeedSite = !activeProfile.permissions.includes("products.view");
  useEffect(() => {
    if (demoMode || !api || !organizationId) return;
    if (productsNeedSite && !siteId) {
      setLoading(false);
      return;
    }
    let active = true;
    void api.allProducts(organizationId, catalogueStatus, siteId || undefined)
      .then((productData) => {
        if (!active) return;
        // Keep the server's A-Z order, just lift "Prep" to the top.
        const items = [...productData.items].sort((a, b) => Number(isPrep(b)) - Number(isPrep(a)));
        setProducts(items);
        setContainsAllergens(productData.containsAllergens);
        setSelectedId(items[0]?.id ?? null);
      })
      .catch((reason: unknown) => active && setError(messageFor(reason)))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [api, catalogueStatus, demoMode, organizationId, productsNeedSite, siteId]);

  useEffect(() => {
    if (demoMode || !api || !organizationId || !selectedId || details[selectedId]) return;
    let active = true;
    void api.product(organizationId, selectedId)
      .then((detail) => active && setDetails((current) => ({ ...current, [selectedId]: detail })))
      .catch((reason: unknown) => active && setError(messageFor(reason)));
    return () => { active = false; };
  }, [api, demoMode, details, organizationId, selectedId]);

  const selected = selectedId ? products.find((product) => product.id === selectedId) ?? null : null;
  const selectedDetail = selectedId ? details[selectedId] ?? null : null;
  const categoryMap = useMemo(
    () => new Map((references?.categories ?? []).map((item) => [item.id, item.name])),
    [references],
  );
  const filtered = products.filter((product) => {
    const matchesSearch = product.name.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase());
    const matchesCategory = category === "all" || product.category_id === category;
    const matchesStatus = catalogueStatus === "archived" ? !product.active : product.active;
    return matchesSearch && matchesCategory && matchesStatus;
  });
  // A full supplier catalogue runs to thousands of lines; rendering every card
  // at once makes the list unusable on a kitchen tablet, so cards are revealed
  // in pages of PRODUCT_CARD_LIMIT as the shopper scrolls (see onProductGridScroll).
  useEffect(() => {
    setVisibleCount(PRODUCT_CARD_LIMIT);
  }, [search, category, catalogueStatus, siteId]);
  const visible = filtered.slice(0, visibleCount);
  const filteredLength = filtered.length;
  useEffect(() => {
    if (workspaceView !== "catalogue") return;
    function onScroll() {
      const nearBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 300;
      if (!nearBottom) return;
      setVisibleCount((current) => (current < filteredLength ? Math.min(current + PRODUCT_CARD_LIMIT, filteredLength) : current));
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [workspaceView, filteredLength]);
  const canCreate =
    grantedPermissions.has("products.create") && activeProfile.assurance_level === "aal2";
  const canEdit =
    grantedPermissions.has("products.edit") && activeProfile.assurance_level === "aal2";
  const canArchive =
    grantedPermissions.has("products.archive") && activeProfile.assurance_level === "aal2";
  const canEditAllergens =
    grantedPermissions.has("allergens.edit") && activeProfile.assurance_level === "aal2";
  const canEditShelfLife =
    grantedPermissions.has("shelf_life.edit") && activeProfile.assurance_level === "aal2";
  const canApproveShelfLife =
    grantedPermissions.has("shelf_life.approve") && activeProfile.assurance_level === "aal2";
  const canManageTeam =
    grantedPermissions.has("roles.manage") && activeProfile.assurance_level === "aal2";
  const canViewDepartments = grantedPermissions.has("departments.view");
  const canManageDepartments =
    grantedPermissions.has("departments.manage") && activeProfile.assurance_level === "aal2";
  const canManageBridge =
    grantedPermissions.has("printers.manage") && activeProfile.assurance_level === "aal2";
  const canPrint = grantedPermissions.has("labels.print");
  // Prep lists and buffet menus don't need MFA (PIN sign-in is enough);
  // PPDS labels still do.
  const canManagePrepLists = grantedPermissions.has("prep_lists.manage");
  const canManagePpdsLabels =
    grantedPermissions.has("ppds_labels.manage") && activeProfile.assurance_level === "aal2";
  const canReprint =
    grantedPermissions.has("labels.reprint") && activeProfile.assurance_level === "aal2";
  const canManageBuffet = grantedPermissions.has("buffet.manage");
  useEffect(() => {
    if (demoMode || !api || !organizationId || !siteId || !canPrint) { setHeaderPrinters([]); return; }
    let active = true;
    void api.printOptions(organizationId, siteId)
      .then((result) => { if (active) setHeaderPrinters(result.printers); })
      .catch(() => { if (active) setHeaderPrinters([]); });
    return () => { active = false; };
  }, [api, canPrint, demoMode, organizationId, siteId]);
  const canManageSites =
    grantedPermissions.has("tenancy.manage") && activeProfile.assurance_level === "aal2";

  async function switchUser(memberId: string, pin: string) {
    if (!organizationId) return;
    if (demoMode) {
      const expectedPin = ({ [demoSession.member_id!]: "1001", "member-2": "1002", "member-3": "1003" } as Record<string, string>)[memberId];
      const member = demoIdentityAdministration.members.find((item) => item.id === memberId);
      if (!member || !member.active || pin !== expectedPin) throw new Error("PIN is invalid or temporarily locked");
      const roleIds = demoIdentityAdministration.assignments.filter((item) => item.member_id === member.id).map((item) => item.role_id);
      const permissions = [...new Set(demoIdentityAdministration.roles.filter((role) => role.active && roleIds.includes(role.id)).flatMap((role) => role.permissions))];
      setActiveProfile({ ...activeProfile, member_id: member.id, permissions, assurance_level: "aal1" });
    } else {
      const switched = await api!.switchSharedDeviceUser(organizationId, memberId, pin);
      writeSwitchToken(window.sessionStorage, switched.switch_token, switched.expires_at);
      setActiveProfile(await api!.session());
    }
    if (!demoMode) writeSharedDeviceLock(window.sessionStorage, false);
    setWorkspaceView("catalogue");
    setLocked(false);
    inactivity.current?.unlock();
    setToast("User switched · permissions refreshed");
  }

  async function resumeSession(password: string) {
    if (demoMode) {
      setLocked(false);
      inactivity.current?.unlock();
      setToast("Session resumed");
      return;
    }
    const verifier = buildPasswordVerifier(runtime);
    if (!verifier) throw new Error("Kitchen Hub could not verify your password right now.");
    const result = await verifier.auth.signInWithPassword({ email, password });
    if (result.error) throw new Error(passwordCheckMessage(result.error));
    // scope: "local" is essential here — the default "global" scope signs the
    // account out on every device, which would kill the real session we're trying to resume.
    try { await verifier.auth.signOut({ scope: "local" }); } catch { /* best-effort cleanup of the throwaway check session */ }
    if (api) setActiveProfile(await api.session());
    writeSharedDeviceLock(window.sessionStorage, false);
    setLocked(false);
    inactivity.current?.unlock();
    setToast("Session resumed");
  }

  function signOut() {
    if (!demoMode) {
      writeSharedDeviceLock(window.sessionStorage, false);
      writeSwitchToken(window.sessionStorage, null);
      api?.clearSharedDeviceUser();
    }
    onSignOut?.();
  }

  function selectProduct(productId: string) {
    setSelectedId(productId);
    setDetailTab("overview");
    if (window.matchMedia("(max-width: 820px)").matches) {
      window.setTimeout(
        // Straight to the label and its print bar, which is what a phone user
        // picked the product for; the product's details are just above. The
        // section's bottom lines up just above the bottom menu, so the whole
        // label and the Print button are on screen together. Below a long
        // product list, smooth scrolling takes seconds, so jump instead.
        () => {
          const target = document.getElementById("label-preview-section") ?? document.getElementById("product-inspector");
          if (!target) return;
          const far = Math.abs(target.getBoundingClientRect().top) > window.innerHeight * 3;
          target.scrollIntoView({ behavior: far ? "auto" : "smooth", block: "end" });
        },
        0,
      );
      return;
    }
    // On a PC the panel is taller than the screen, so bring its top to the
    // top of the window: it then sticks there with the print bar at its foot.
    window.setTimeout(() => {
      const inspector = document.getElementById("product-inspector");
      if (!inspector) return;
      inspector.scrollTop = 0;
      const top = inspector.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top, behavior: Math.abs(top - window.scrollY) > window.innerHeight * 3 ? "auto" : "smooth" });
    }, 0);
  }

  async function createCategory(name: string): Promise<Category> {
    if (!organizationId) throw new Error("No organisation selected");
    const trimmed = name.trim();
    if (!trimmed) throw new Error("Category name is required");
    const created: Category = demoMode
      ? { id: `preview-category-${crypto.randomUUID()}`, organization_id: organizationId, name: trimmed, sort_order: 0, active: true, version: 1 }
      : await api!.createCategory(organizationId, { name: trimmed });
    setReferences((current) => current ? {
      ...current,
      categories: [...current.categories, created].sort((a, b) => a.name.localeCompare(b.name)),
    } : current);
    return created;
  }

  async function saveProduct(input: ProductInput) {
    if (!organizationId) return;
    try {
      if (editor === "create") {
        const { allergens: newAllergens = [], shelf_lives: newShelfLives = [], ...productFields } = input;
        const created: Product = demoMode
          ? { ...productFields, id: `preview-${crypto.randomUUID()}`, organization_id: organizationId, active: true, version: 1 }
          : await api!.createProduct(organizationId, input);
        setProducts((current) => [...current, created].sort(byCatalogueOrder));
        const productDepartments = departments.filter((item) => input.site_department_ids.includes(item.id)).map(toProductDepartment);
        const detail: ProductDetail = !demoMode && (newAllergens.length || newShelfLives.length)
          ? await api!.product(organizationId, created.id)
          : { product: created, departments: productDepartments, allergens: [], shelf_life_rules: [] };
        setDetails((current) => ({ ...current, [created.id]: detail }));
        setContainsAllergens((current) => ({
          ...current,
          [created.id]: detail.allergens.filter((item) => item.relation === "contains").map((item) => item.name),
        }));
        setSelectedId(created.id);
        const approvedCount = detail.shelf_life_rules.filter((rule) => rule.approval_status === "approved").length;
        setToast(demoMode ? "Preview product added locally"
          : !newShelfLives.length ? "Product created"
          : approvedCount ? `Product created · ${approvedCount} shelf ${approvedCount === 1 ? "life" : "lives"} approved`
          : "Product created · shelf lives waiting for approval");
      } else if (selected) {
        const updated = demoMode
          ? { ...selected, ...input, version: selected.version + 1 }
          : await api!.updateProduct(organizationId, selected, input);
        setProducts((current) => current.map((item) => item.id === updated.id ? updated : item));
        setDetails((current) => ({
          ...current,
          [updated.id]: {
            ...(current[updated.id] ?? { departments: [], allergens: [], shelf_life_rules: [] }),
            product: updated,
            departments: departments.filter((item) => input.site_department_ids.includes(item.id)).map(toProductDepartment),
          },
        }));
        setToast(demoMode ? "Preview product updated locally" : "Product updated");
      }
      setEditor(null);
    } catch (reason) {
      setError(messageFor(reason));
    }
  }

  async function archiveSelected() {
    if (!organizationId || !api || !selectedIds.length) return;
    if (!window.confirm(
      `Archive ${selectedIds.length} product${selectedIds.length === 1 ? "" : "s"}? ` +
      "They stay available for audit and can be made active again from the Archived filter."
    )) return;
    try {
      const result = await api.archiveProducts(organizationId, selectedIds);
      const archived = new Set(result.archived_ids);
      setProducts((current) => current.filter((item) => !archived.has(item.id)));
      if (selectedId && archived.has(selectedId)) setSelectedId(null);
      setSelectedIds([]);
      setSelectionMode(false);
      setToast(`${result.archived} product${result.archived === 1 ? "" : "s"} archived`);
    } catch (reason) {
      setError(messageFor(reason));
    }
  }

  async function reactivateSelected() {
    if (!organizationId || !api || !selectedIds.length) return;
    const undeclaredCount = selectedIds.filter((id) => !containsAllergens[id]?.length).length;
    const question =
      `Make ${selectedIds.length} product${selectedIds.length === 1 ? "" : "s"} active again?` +
      (undeclaredCount
        ? `\n\n${undeclaredCount} of them have no allergen declaration recorded and will print as containing none` +
          `${activeProfile.physical_printing_enabled ? ", on real labels in this environment" : ""}. ` +
          "Only continue if you have checked the packaging and they genuinely contain none."
        : "");
    if (!window.confirm(question)) return;
    const ids = [...selectedIds];
    const succeeded: string[] = [];
    const failed: string[] = [];
    for (const id of ids) {
      const product = products.find((item) => item.id === id);
      if (!product) continue;
      try {
        const detail = details[id] ?? (await api.product(organizationId, id));
        const input: ProductInput = {
          name: product.name,
          category_id: product.category_id,
          storage_instructions: product.storage_instructions,
          unit_of_measure: product.unit_of_measure,
          site_department_ids: detail.departments.map((item) => item.site_department_id),
          site_id: product.site_id,
        };
        await api.updateProduct(organizationId, product, input, true);
        succeeded.push(id);
      } catch (reason) {
        failed.push(product.name);
      }
    }
    const succeededSet = new Set(succeeded);
    setProducts((current) => current.filter((item) => !succeededSet.has(item.id)));
    setDetails((current) => {
      const next = { ...current };
      succeeded.forEach((id) => delete next[id]);
      return next;
    });
    setContainsAllergens((current) => {
      const next = { ...current };
      succeeded.forEach((id) => delete next[id]);
      return next;
    });
    if (selectedId && succeededSet.has(selectedId)) setSelectedId(null);
    setSelectedIds(ids.filter((id) => !succeededSet.has(id)));
    setSelectionMode(failed.length > 0);
    setToast(
      failed.length
        ? `${succeeded.length} product${succeeded.length === 1 ? "" : "s"} made active, ${failed.length} failed: ${failed.join(", ")}`
        : `${succeeded.length} product${succeeded.length === 1 ? "" : "s"} made active again`
    );
  }

  function toggleSelected(productId: string) {
    setSelectedIds((current) =>
      current.includes(productId)
        ? current.filter((id) => id !== productId)
        : [...current, productId],
    );
  }

  async function reactivateProduct() {
    if (!organizationId || !selected || !selectedDetail || !api) return;
    // An empty allergen list is not the same as "no allergens": the schema has
    // no positive "declared allergen-free" flag, so a product nobody has
    // assessed looks identical to one that genuinely contains nothing. The
    // catalogue import deactivated every product whose supplier declared
    // nothing at all. Reactivating one makes it printable, and its label then
    // renders as carrying no allergens - so the person doing it has to say so.
    const undeclared = selectedDetail.allergens.length === 0;
    const question = undeclared
      ? `${selected.name} has no allergen declaration recorded.

` +
        `If you make it active, its label will print as containing no allergens${
          activeProfile.physical_printing_enabled ? ", on real labels in this environment" : ""
        }. Only continue if you have checked the packaging and it genuinely contains none.

` +
        "Make it active anyway?"
      : `Make ${selected.name} active again?`;
    if (!window.confirm(question)) return;
    try {
      const input: ProductInput = {
        name: selected.name,
        category_id: selected.category_id,
        storage_instructions: selected.storage_instructions,
        unit_of_measure: selected.unit_of_measure,
        site_department_ids: selectedDetail.departments.map((item) => item.site_department_id),
        site_id: selected.site_id,
      };
      const updated = await api.updateProduct(organizationId, selected, input, true);
      setProducts((current) => current.filter((item) => item.id !== updated.id));
      setDetails((current) => {
        const next = { ...current };
        delete next[updated.id];
        return next;
      });
      setContainsAllergens((current) => {
        const next = { ...current };
        delete next[updated.id];
        return next;
      });
      setSelectedId(null);
      setToast(undeclared ? "Product active - allergens still undeclared" : "Product is active again");
    } catch (reason) {
      setError(messageFor(reason));
    }
  }

  async function archiveProduct() {
    if (!organizationId || !selected || !selectedDetail || !api) return;
    if (!window.confirm(`Archive ${selected.name}? It will remain available for audit and recovery.`)) return;
    try {
      const input: ProductInput = {
        name: selected.name,
        category_id: selected.category_id,
        storage_instructions: selected.storage_instructions,
        unit_of_measure: selected.unit_of_measure,
        site_department_ids: selectedDetail.departments.map((item) => item.site_department_id),
        site_id: selected.site_id,
      };
      const updated = await api.updateProduct(organizationId, selected, input, false);
      setProducts((current) => current.filter((item) => item.id !== updated.id));
      setDetails((current) => {
        const next = { ...current };
        delete next[updated.id];
        return next;
      });
      setContainsAllergens((current) => {
        const next = { ...current };
        delete next[updated.id];
        return next;
      });
      setSelectedId(null);
      setToast("Product archived");
    } catch (reason) {
      setError(messageFor(reason));
    }
  }

  async function duplicateProduct() {
    if (!organizationId || !selected || !selectedDetail) return;
    try {
      const copy: ProductDetail = demoMode
        ? {
            product: { ...selected, id: `preview-${crypto.randomUUID()}`, name: `${selected.name} Copy`, version: 1 },
            departments: selectedDetail.departments,
            allergens: selectedDetail.allergens,
            shelf_life_rules: selectedDetail.shelf_life_rules.map((rule) => ({
              ...rule, id: crypto.randomUUID(), approval_status: "draft" as const, version: 1,
            })),
          }
        : await api!.duplicateProduct(organizationId, selected.id);
      setProducts((current) => [...current, copy.product].sort(byCatalogueOrder));
      setDetails((current) => ({ ...current, [copy.product.id]: copy }));
      setContainsAllergens((current) => ({
        ...current,
        [copy.product.id]: copy.allergens.filter((item) => item.relation === "contains").map((item) => item.name),
      }));
      setSelectedId(copy.product.id);
      setToast(`Duplicated as "${copy.product.name}" — review before it's used for real`);
    } catch (reason) {
      setError(messageFor(reason));
    }
  }

  async function exportCatalogueCsv() {
    if (!api || !organizationId) return;
    setExportingCatalogue(true);
    try {
      const blob = await api.exportProductsCsv(organizationId, siteId || undefined);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `kitchen-hub-catalogue-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (reason) {
      setError(messageFor(reason));
    } finally {
      setExportingCatalogue(false);
    }
  }

  async function saveAllergen(allergenId: string, input: AllergenInput) {
    if (!organizationId || !selected || !references) return;
    try {
      if (demoMode) {
        const definition = references.allergens.find((item) => item.id === allergenId);
        if (!definition) throw new Error("Choose a valid allergen.");
        const declaration: ProductAllergen = {
          allergen_id: definition.id,
          code: definition.code,
          name: definition.name,
          relation: input.relation,
          evidence: input.evidence,
        };
        setDetails((current) => {
          const detail = current[selected.id];
          if (!detail) return current;
          const allergens = [
            ...detail.allergens.filter((item) => item.allergen_id !== allergenId),
            declaration,
          ].sort((left, right) => left.name.localeCompare(right.name));
          setContainsAllergens((allergenNames) => ({
            ...allergenNames,
            [selected.id]: allergens.filter((item) => item.relation === "contains").map((item) => item.name),
          }));
          return { ...current, [selected.id]: { ...detail, allergens } };
        });
      } else {
        await api!.setProductAllergen(organizationId, selected.id, allergenId, input);
        const refreshed = await api!.product(organizationId, selected.id);
        setDetails((current) => ({ ...current, [selected.id]: refreshed }));
        setContainsAllergens((current) => ({
          ...current,
          [selected.id]: refreshed.allergens.filter((item) => item.relation === "contains").map((item) => item.name),
        }));
      }
      setAllergenEditor(null);
      setToast(demoMode ? "Preview allergen declaration saved locally" : "Allergen declaration saved");
    } catch (reason) {
      setError(messageFor(reason));
    }
  }

  async function removeAllergen(allergenId: string) {
    if (!organizationId || !selected) return;
    try {
      if (demoMode) {
        setDetails((current) => {
          const detail = current[selected.id];
          if (!detail) return current;
          const allergens = detail.allergens.filter((item) => item.allergen_id !== allergenId);
          setContainsAllergens((allergenNames) => ({
            ...allergenNames,
            [selected.id]: allergens.filter((item) => item.relation === "contains").map((item) => item.name),
          }));
          return { ...current, [selected.id]: { ...detail, allergens } };
        });
      } else {
        await api!.removeProductAllergen(organizationId, selected.id, allergenId);
        const refreshed = await api!.product(organizationId, selected.id);
        setDetails((current) => ({ ...current, [selected.id]: refreshed }));
        setContainsAllergens((current) => ({
          ...current,
          [selected.id]: refreshed.allergens.filter((item) => item.relation === "contains").map((item) => item.name),
        }));
      }
      setAllergenEditor(null);
      setToast(demoMode ? "Preview allergen declaration removed locally" : "Allergen declaration removed");
    } catch (reason) {
      setError(messageFor(reason));
    }
  }

  async function saveShelfLifeDraft(input: ShelfLifeDraftInput) {
    if (!organizationId || !selected || !references) return;
    try {
      if (demoMode) {
        const labelType = references.label_types.find((item) => item.id === input.label_type_id);
        if (!labelType) throw new Error("Choose a valid label type.");
        setDetails((current) => {
          const detail = current[selected.id];
          if (!detail) return current;
          return {
            ...current,
            [selected.id]: {
              ...detail,
              shelf_life_rules: [{
                id: `preview-rule-${crypto.randomUUID()}`,
                site_id: input.site_id,
                label_type_id: labelType.id,
                label_type_code: labelType.code,
                label_type_name: labelType.name,
                storage_state: input.storage_state,
                duration_value: input.duration_value,
                duration_unit: input.duration_unit,
                calculation_method: input.calculation_method,
                date_mark_type: input.date_mark_type,
                approval_status: "draft",
                valid_from: input.valid_from,
                valid_until: input.valid_until,
                version: 1,
              }, ...detail.shelf_life_rules],
            },
          };
        });
      } else {
        await api!.createShelfLifeDraft(organizationId, selected.id, input);
        const refreshed = await api!.product(organizationId, selected.id);
        setDetails((current) => ({ ...current, [selected.id]: refreshed }));
      }
      setShelfLifeEditor(false);
      setToast(demoMode ? "Preview shelf-life draft added locally" : "Shelf-life draft created");
    } catch (reason) {
      setError(messageFor(reason));
    }
  }

  async function transitionShelfLife(rule: ShelfLifeRule, action: "approve" | "retire") {
    if (!organizationId || !selected) return;
    const verb = action === "approve" ? "approve" : "retire";
    if (!window.confirm(`${verb[0].toUpperCase()}${verb.slice(1)} this shelf-life rule? This action is audited.`)) return;
    try {
      if (demoMode) {
        const detail = details[selected.id];
        if (!detail) return;
        if (action === "approve") {
          const conflict = detail.shelf_life_rules.some((item) =>
            item.id !== rule.id
            && item.approval_status === "approved"
            && item.valid_until === null
            && item.site_id === rule.site_id
            && item.label_type_id === rule.label_type_id
            && item.storage_state === rule.storage_state
          );
          if (conflict) throw new Error("Retire the current approved rule before approving this draft.");
        }
        setDetails((current) => ({
          ...current,
          [selected.id]: {
            ...detail,
            shelf_life_rules: detail.shelf_life_rules.map((item) => item.id === rule.id
              ? { ...item, approval_status: action === "approve" ? "approved" : "retired", version: item.version + 1 }
              : item),
          },
        }));
      } else {
        if (action === "approve") {
          await api!.approveShelfLifeRule(organizationId, selected.id, rule);
        } else {
          await api!.retireShelfLifeRule(organizationId, selected.id, rule);
        }
        const refreshed = await api!.product(organizationId, selected.id);
        setDetails((current) => ({ ...current, [selected.id]: refreshed }));
      }
      setToast(`Shelf-life rule ${action === "approve" ? "approved" : "retired"}`);
    } catch (reason) {
      setError(messageFor(reason));
    }
  }

  async function saveShelfLifeRevision(input: ShelfLifeRevisionInput) {
    if (!organizationId || !selected || !shelfLifeRevision) return;
    try {
      if (demoMode) {
        const rule = shelfLifeRevision;
        setDetails((current) => {
          const detail = current[selected.id];
          if (!detail) return current;
          return {
            ...current,
            [selected.id]: {
              ...detail,
              shelf_life_rules: detail.shelf_life_rules.map((item) => item.id === rule.id
                ? {
                  ...item,
                  duration_value: input.duration_value,
                  duration_unit: input.duration_unit,
                  calculation_method: input.calculation_method,
                  date_mark_type: input.date_mark_type,
                  valid_from: input.valid_from,
                  valid_until: input.valid_until,
                  version: item.version + 1,
                }
                : item),
            },
          };
        });
      } else {
        await api!.reviseShelfLifeRule(organizationId, selected.id, shelfLifeRevision.id, input);
        const refreshed = await api!.product(organizationId, selected.id);
        setDetails((current) => ({ ...current, [selected.id]: refreshed }));
      }
      setShelfLifeRevision(null);
      setToast("Shelf-life rule revised");
    } catch (reason) {
      setError(messageFor(reason));
    }
  }

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 2800);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (!demoMode && sites.length > 1 && (!siteId || changingSite)) {
    return (
      <SitePickerScreen
        sites={sites}
        current={siteId}
        onChoose={chooseSite}
        onCancel={changingSite && siteId ? () => setChangingSite(false) : undefined}
      />
    );
  }

  return (
    <SelectedSiteContext.Provider value={siteId}>
    <div className="app-shell">
      <Sidebar siteName={siteName} canChangeSite={!demoMode && sites.length > 1} onChangeSite={() => setChangingSite(true)} demoMode={demoMode} email={email} displayName={activeProfile.member_display_name} onSignOut={onSignOut ? signOut : undefined} onSwitch={() => inactivity.current?.lockNow()} activeView={workspaceView} canManageTeam={canManageTeam} canManageBridge={canManageBridge} canViewDepartments={canViewDepartments} canPrint={canPrint} canApproveShelfLife={canApproveShelfLife} physicalPrintingEnabled={activeProfile.physical_printing_enabled} onView={setWorkspaceView} />
      <main className="workspace">
        {demoMode && <div className="preview-banner"><Icon name="spark" size={17} /><strong>Local product preview</strong><span>Sample data only · nothing will print or sync</span></div>}
        <header className="workspace-header">
          <div><p className="eyebrow">Kitchen Hub</p><h1>{workspaceView === "dashboard" ? "Dashboard" : workspaceView === "system-info" ? "System Info" : workspaceView === "catalogue" ? "Food catalogue" : workspaceView === "departments" ? "Departments" : workspaceView === "team" ? "Team & roles" : workspaceView === "bridge" ? "Print Bridge enrolment" : workspaceView === "printer-admin" ? "Printers & routing" : workspaceView === "print-history" ? "Print History" : workspaceView === "settings" ? "Settings" : workspaceView === "buffet" ? "Buffet Menus" : workspaceView === "shelf-life-review" ? "Shelf-life review" : workspaceView === "prep-lists" ? "Prep Lists" : workspaceView === "ppds-labels" ? "PPDS / Takeaway labels" : workspaceView === "quick" ? "Quick Print" : "Batch Print"}</h1></div>
          <div className="header-actions">
            <span className="shadow-chip"><span /> {headerPrinters.some((printer) => printer.physical_printing_enabled) ? "Physical printing enabled" : "Shadow mode"}</span>
            {workspaceView === "catalogue" && <button className="secondary-button" onClick={() => void exportCatalogueCsv()} disabled={!api || exportingCatalogue} title={api ? "Download the current catalogue as a CSV backup" : "Not available in local preview"}>{exportingCatalogue ? "Exporting…" : "Export CSV"}</button>}
            {workspaceView === "catalogue" && <button className="primary-button" onClick={() => setEditor("create")} disabled={!canCreate} title={canCreate ? undefined : "Product changes require MFA and create permission"}><Icon name="plus" /> New product</button>}
          </div>
        </header>

        {error && <div className="inline-error"><Icon name="warning" /><span>{error}</span><button onClick={() => setError(null)}>Dismiss</button></div>}
        {workspaceView === "dashboard" ? (
          <DashboardWorkspace departments={departments} organizationId={organizationId!} api={api} canView={canPrint} physicalPrintingEnabled={activeProfile.physical_printing_enabled} onError={setError} />
        ) : workspaceView === "system-info" ? (
          <SystemInfoWorkspace departments={departments} organizationId={organizationId!} api={api} canView={canPrint} physicalPrintingEnabled={activeProfile.physical_printing_enabled} onError={setError} />
        ) : workspaceView === "quick" || workspaceView === "batch" ? (
          <OperationalPrintWorkspace mode={workspaceView} departments={departments} organizationId={organizationId!} api={api} canPrint={canPrint} canResetRanking={canManageBridge} physicalPrintingEnabled={activeProfile.physical_printing_enabled} onError={setError} onToast={setToast} />
        ) : workspaceView === "prep-lists" ? (
          <PrepListsWorkspace departments={departments} organizationId={organizationId!} api={api} canPrint={canPrint} canManage={canManagePrepLists} physicalPrintingEnabled={activeProfile.physical_printing_enabled} onError={setError} onToast={setToast} />
        ) : workspaceView === "ppds-labels" ? (
          <PpdsLabelsWorkspace departments={departments} references={references} organizationId={organizationId!} api={api} canPrint={canPrint} canManage={canManagePpdsLabels} physicalPrintingEnabled={activeProfile.physical_printing_enabled} onError={setError} onToast={setToast} />
        ) : workspaceView === "bridge" ? (
          <BridgeAdministration departments={departments} organizationId={organizationId!} api={api} canManage={canManageBridge} physicalPrintingEnabled={activeProfile.physical_printing_enabled} onError={setError} />
        ) : workspaceView === "printer-admin" ? (
          <PrinterAdministration departments={departments} references={references} organizationId={organizationId!} api={api} canManage={canManageBridge} physicalPrintingEnabled={activeProfile.physical_printing_enabled} onError={setError} onToast={setToast} />
        ) : workspaceView === "print-history" ? (
          <PrintHistoryWorkspace departments={departments} organizationId={organizationId!} api={api} canPrint={canPrint} canReprint={canReprint} physicalPrintingEnabled={activeProfile.physical_printing_enabled} onError={setError} onToast={setToast} />
        ) : workspaceView === "settings" ? (
          <SettingsWorkspace departments={departments} organizationId={organizationId!} api={api} canManage={canManageBridge} canManageOrganization={canManageSites} physicalPrintingEnabled={activeProfile.physical_printing_enabled} onError={setError} onToast={setToast} />
        ) : workspaceView === "buffet" ? (
          <BuffetWorkspace departments={departments} organizationId={organizationId!} api={api} canPrint={canPrint} canManage={canManageBuffet} physicalPrintingEnabled={activeProfile.physical_printing_enabled} onError={setError} onToast={setToast} />
        ) : workspaceView === "shelf-life-review" ? (
          <ShelfLifeReviewWorkspace departments={departments} organizationId={organizationId!} api={api} canApprove={canApproveShelfLife} onError={setError} onToast={setToast} />
        ) : workspaceView === "team" ? (
          <TeamAdministration profile={activeProfile} organizationId={organizationId!} api={api} demoMode={demoMode} sites={sites} onError={setError} onToast={setToast} />
        ) : workspaceView === "departments" ? (
          <DepartmentAdministration departments={departments} setDepartments={setDepartments} organizationId={organizationId!} api={api} demoMode={demoMode} canManage={canManageDepartments} canManageSites={canManageSites} onError={setError} onToast={setToast} />
        ) : loading ? <LoadingPanel /> : (
          <div className="catalogue-layout">
            <section className="catalogue-panel">
              <div className="catalogue-toolbar">
                <label className="search-box"><Icon name="search" /><input aria-label="Search products" placeholder="Search products…" value={search} onChange={(event) => setSearch(event.target.value)} /></label>
                <div className="toolbar-actions">
                  <label className="status-filter">
                    <span>Showing</span>
                    <select value={catalogueStatus} onChange={(event) => setCatalogueStatus(event.target.value as "active" | "archived")}>
                      <option value="active">Active products</option>
                      <option value="archived">Archived products</option>
                    </select>
                  </label>
                  {((catalogueStatus === "active" && canArchive) || (catalogueStatus === "archived" && canEdit)) && api && (
                    <button
                      className={selectionMode ? "secondary-button compact-button active-toggle" : "secondary-button compact-button"}
                      onClick={() => { setSelectionMode((on) => !on); setSelectedIds([]); }}
                    >
                      {selectionMode ? "Done" : "Select"}
                    </button>
                  )}
                  {canEdit && api && (
                    <button className="secondary-button compact-button" onClick={() => setCategoryManager(true)} title="Rename or hide categories">
                      Categories
                    </button>
                  )}
                  <div className="count-block"><strong>{filtered.length}</strong><span>{catalogueStatus === "archived" ? "archived" : "products"}</span></div>
                </div>
              </div>
              <div className="category-strip" aria-label="Product categories" ref={wheelScrollsSideways}>
                <button className={category === "all" ? "active" : ""} onClick={() => setCategory("all")}>All products</button>
                {references?.categories.map((item) => <button key={item.id} className={category === item.id ? "active" : ""} onClick={() => setCategory(item.id)}>{item.name}</button>)}
              </div>
              {selectionMode && (
                <div className="bulk-bar">
                  <strong>{selectedIds.length} selected</strong>
                  <button
                    className="secondary-button compact-button"
                    onClick={() => setSelectedIds(visible.map((item) => item.id))}
                  >
                    Select all {visible.length} shown
                  </button>
                  <button
                    className="secondary-button compact-button"
                    disabled={!selectedIds.length}
                    onClick={() => setSelectedIds([])}
                  >
                    Clear
                  </button>
                  {catalogueStatus === "archived" ? (
                    <button
                      className="secondary-button compact-button"
                      disabled={!selectedIds.length}
                      onClick={() => void reactivateSelected()}
                    >
                      Restore selected
                    </button>
                  ) : (
                    <button
                      className="danger-button compact-button"
                      disabled={!selectedIds.length}
                      onClick={() => void archiveSelected()}
                    >
                      Archive selected
                    </button>
                  )}
                </div>
              )}
              <div className="product-grid">
                {visible.map((product, index) => {
                  return (
                    <button
                      key={product.id}
                      className={`product-card ${(selectionMode ? selectedIds.includes(product.id) : selectedId === product.id) ? "selected" : ""}`}
                      onClick={() => (selectionMode ? toggleSelected(product.id) : selectProduct(product.id))}
                      aria-pressed={selectionMode ? selectedIds.includes(product.id) : undefined}
                    >
                      {selectionMode
                        ? <span className={selectedIds.includes(product.id) ? "select-box select-box-on" : "select-box"} aria-hidden="true">{selectedIds.includes(product.id) ? "✓" : ""}</span>
                        : <span className={`food-swatch tone-${index % 5}`}><Icon name={index % 3 === 0 ? "prep" : "label"} size={24} /></span>}
                      <span className="product-card-copy"><strong>{product.name}</strong><small>{categoryMap.get(product.category_id ?? "") ?? "Uncategorised"}</small><span className="allergen-line">{allergenSummary(containsAllergens[product.id])}</span></span>
                      {!selectionMode && <Icon name="chevron" size={18} />}
                    </button>
                  );
                })}
                {filtered.length > visible.length && <div className="empty-list"><Icon name="search" size={28} /><strong>Showing {visible.length} of {filtered.length}</strong><span>Scroll down for more, or search to narrow the list.</span></div>}
                {!filtered.length && <div className="empty-list"><Icon name="search" size={28} /><strong>No products found</strong><span>Try a different search or category.</span></div>}
              </div>
            </section>

            <ProductInspector
              api={api}
              organizationId={organizationId!}
              demoMode={demoMode}
              product={selected}
              detail={selectedDetail}
              categoryName={selected ? categoryMap.get(selected.category_id ?? "") ?? "Uncategorised" : ""}
              activeTab={detailTab}
              onTab={setDetailTab}
              onEdit={() => setEditor("edit")}
              onArchive={() => void archiveProduct()}
              onReactivate={() => void reactivateProduct()}
              onDuplicate={() => void duplicateProduct()}
              canDuplicate={canCreate}
              canEdit={canEdit}
              canEditAllergens={canEditAllergens}
              canEditShelfLife={canEditShelfLife}
              canApproveShelfLife={canApproveShelfLife}
              canPrint={canPrint}
              physicalPrintingEnabled={activeProfile.physical_printing_enabled}
              onAddAllergen={() => setAllergenEditor("new")}
              onEditAllergen={setAllergenEditor}
              onNewShelfLife={() => setShelfLifeEditor(true)}
              onApproveShelfLife={(rule) => void transitionShelfLife(rule, "approve")}
              onRetireShelfLife={(rule) => void transitionShelfLife(rule, "retire")}
              onEditShelfLife={setShelfLifeRevision}
              onToast={setToast}
            />
          </div>
        )}
        <footer className="site-footer">{COPYRIGHT_NOTICE}</footer>
      </main>
      <MobileNav siteName={siteName} canChangeSite={!demoMode && sites.length > 1} onChangeSite={() => setChangingSite(true)} activeView={workspaceView} canManageTeam={canManageTeam} canManageBridge={canManageBridge} canViewDepartments={canViewDepartments} canPrint={canPrint} canApproveShelfLife={canApproveShelfLife} onView={setWorkspaceView} onSwitch={() => inactivity.current?.lockNow()} onSignOut={onSignOut ? signOut : undefined} />
      {workspaceView === "catalogue" && editor && references && (
        <ProductEditor
          mode={editor}
          product={editor === "edit" ? selected : null}
          productDepartments={editor === "edit" ? selectedDetail?.departments ?? [] : []}
          categories={references.categories}
          departments={departments}
          allergens={references.allergens}
          labelTypes={references.label_types}
          defaultSiteId={siteId}
          canEditAllergens={canEditAllergens}
          canEditShelfLife={canEditShelfLife}
          canApproveShelfLife={canApproveShelfLife}
          canCreateCategory={canCreate}
          onClose={() => setEditor(null)}
          onSave={saveProduct}
          onCreateCategory={createCategory}
        />
      )}
      {allergenEditor && references && selected && (
        <AllergenEditor
          allergens={references.allergens}
          existing={allergenEditor === "new" ? null : allergenEditor}
          onClose={() => setAllergenEditor(null)}
          onSave={saveAllergen}
          onRemove={removeAllergen}
        />
      )}
      {shelfLifeEditor && references && selected && (
        <ShelfLifeEditor
          labelTypes={references.label_types}
          onClose={() => setShelfLifeEditor(false)}
          onSave={saveShelfLifeDraft}
        />
      )}
      {shelfLifeRevision && (
        <ShelfLifeRevisionEditor
          rule={shelfLifeRevision}
          onClose={() => setShelfLifeRevision(null)}
          onSave={saveShelfLifeRevision}
        />
      )}
      {categoryManager && api && organizationId && (
        <CategoryManager
          api={api}
          organizationId={organizationId}
          onClose={() => setCategoryManager(false)}
          onChanged={async () => {
            const refreshed = await api.references(organizationId);
            setReferences(refreshed);
            if (!refreshed.categories.some((item) => item.id === category)) setCategory("all");
          }}
          onToast={setToast}
        />
      )}
      {toast && <div className="toast"><Icon name="shield" />{toast}</div>}
      {locked && <SharedDeviceLock members={switchableMembers} displayName={activeProfile.member_display_name} email={email} onResume={resumeSession} onUnlock={switchUser} onCancel={onSignOut ? signOut : undefined} demoMode={demoMode} />}
    </div>
    </SelectedSiteContext.Provider>
  );
}

type WorkspaceView = "dashboard" | "catalogue" | "departments" | "quick" | "batch" | "prep-lists" | "ppds-labels" | "buffet" | "shelf-life-review" | "team" | "bridge" | "printer-admin" | "print-history" | "settings" | "system-info";

function Sidebar({ siteName, canChangeSite, onChangeSite, demoMode, email, displayName, onSignOut, onSwitch, activeView, canManageTeam, canManageBridge, canViewDepartments, canPrint, canApproveShelfLife, physicalPrintingEnabled, onView }: { siteName: string; canChangeSite: boolean; onChangeSite: () => void; demoMode: boolean; email: string; displayName: string | null; onSignOut?: () => void; onSwitch: () => void; activeView: WorkspaceView; canManageTeam: boolean; canManageBridge: boolean; canViewDepartments: boolean; canPrint: boolean; canApproveShelfLife: boolean; physicalPrintingEnabled: boolean; onView: (view: WorkspaceView) => void }) {
  const resolvedName = demoMode ? "Alex Morgan" : displayName ?? email.split("@")[0];
  const initials = demoMode ? "AM" : displayName ? initialsFromName(displayName) : initialsFromEmail(email);
  const adminGateAllowed = { print: canPrint, team: canManageTeam, bridge: canManageBridge, shelfLife: canApproveShelfLife, departments: canViewDepartments };
  const adminItemActive = ADMIN_NAV_ITEMS.some((item) => item.view === activeView);
  const [adminOpen, setAdminOpen] = useState(adminItemActive);
  useEffect(() => { if (adminItemActive) setAdminOpen(true); }, [adminItemActive]);
  return (
    <aside className="sidebar">
      <div className="sidebar-brand"><Mark /><div><strong>Kitchen Hub</strong><span>NCC Hotel Group</span></div></div>
      <nav>
        <button className={activeView === "dashboard" ? "active" : ""} onClick={() => onView("dashboard")} disabled={!canPrint}><Icon name="spark" /><span>Dashboard</span></button>
        <button className={activeView === "catalogue" ? "active" : ""} onClick={() => onView("catalogue")}><Icon name="catalogue" /><span>Catalogue</span></button>
        <button className={activeView === "quick" ? "active" : ""} onClick={() => onView("quick")} disabled={!canPrint}><Icon name="label" /><span>Quick Print</span></button>
        <button className={activeView === "batch" ? "active" : ""} onClick={() => onView("batch")} disabled={!canPrint}><Icon name="catalogue" /><span>Batch Print</span></button>
        <button className={activeView === "prep-lists" ? "active" : ""} onClick={() => onView("prep-lists")} disabled={!canPrint}><Icon name="prep" /><span>Prep Lists</span></button>
        <button className={activeView === "ppds-labels" ? "active" : ""} onClick={() => onView("ppds-labels")} disabled={!canPrint}><Icon name="label" /><span>PPDS Labels</span></button>
        <button className={activeView === "buffet" ? "active" : ""} onClick={() => onView("buffet")} disabled={!canPrint}><Icon name="label" /><span>Buffet Menus</span></button>
        <button className={`admin-toggle${adminOpen ? " open" : ""}${adminItemActive ? " active" : ""}`} onClick={() => setAdminOpen((current) => !current)} aria-expanded={adminOpen}><Icon name="shield" /><span>Admin</span><Icon name="chevron" size={15} /></button>
        {adminOpen && <div className="nav-subgroup">
          {ADMIN_NAV_ITEMS.map((item) => (
            <button
              key={item.view}
              className={activeView === item.view ? "active" : ""}
              onClick={() => onView(item.view)}
              disabled={!adminGateAllowed[item.gate]}
              title={adminGateAllowed[item.gate] ? undefined : "Requires additional permission"}
            >
              <Icon name={item.icon} /><span>{item.label}</span>
            </button>
          ))}
        </div>}
      </nav>
      <div className="sidebar-spacer" />
      <section className="safety-card"><div><Icon name="shield" /><span>Print safety</span></div><strong>{physicalPrintingEnabled ? "Physical printing on" : "Shadow only"}</strong><p>{physicalPrintingEnabled ? "This environment can send real labels to a mapped printer. If a label doesn't come out, report it in Print History." : "Jobs can be previewed, but physical output stays locked."}</p></section>
      {canChangeSite && <button className="secondary-button site-switch-button" onClick={onChangeSite} title="Change the site for this session"><Icon name="label" size={16} /> <span><small>Site</small><strong>{siteName || "Choose site"}</strong></span></button>}
      <button className="secondary-button" onClick={onSwitch}><Icon name="shield" size={16} /> Switch user</button>
      <div className="profile-row"><span className="avatar">{initials}</span><span><strong>{resolvedName}</strong><small>{demoMode ? "Head Chef · Preview" : "Kitchen Hub member"}</small></span>{onSignOut && <button className="profile-sign-out" onClick={onSignOut} title="Sign out on this device">Sign out</button>}</div>
    </aside>
  );
}

function BridgeAdministration({ departments, organizationId, api, canManage, physicalPrintingEnabled, onError }: { departments: Department[]; organizationId: string; api?: KitchenHubApi; canManage: boolean; physicalPrintingEnabled: boolean; onError: (message: string) => void }) {
  const siteId = useSelectedSite();
  const [requestedName, setRequestedName] = useState("Pilot Kitchen Print Bridge");
  const [printerId] = useState(() => crypto.randomUUID());
  const [grant, setGrant] = useState<BridgeEnrolmentGrant | null>(null);
  const [saving, setSaving] = useState(false);
  const [bridges, setBridges] = useState<ManagedPrintBridge[]>([]);

  async function refreshBridges() {
    if (!api || !canManage) return;
    try {
      setBridges(await api.listPrintBridges(organizationId));
    } catch (reason) {
      onError(messageFor(reason));
    }
  }

  useEffect(() => { void refreshBridges(); }, [api, canManage, organizationId]);

  async function create(event: FormEvent) {
    event.preventDefault();
    if (!api || !canManage || !siteId || !requestedName.trim()) return;
    setSaving(true);
    onError("");
    try {
      setGrant(await api.createBridgeEnrolment(organizationId, {
        site_id: siteId,
        requested_name: requestedName.trim(),
        ttl_minutes: 10,
      }));
    } catch (reason) {
      onError(messageFor(reason));
    } finally {
      setSaving(false);
    }
  }

  async function revoke(bridge: ManagedPrintBridge) {
    if (!api || !canManage || bridge.status === "disabled") return;
    if (!window.confirm(`Deactivate ${bridge.name}? Its credential will stop working immediately.`)) return;
    setSaving(true);
    onError("");
    try {
      await api.revokePrintBridge(organizationId, bridge.id);
      await refreshBridges();
    } catch (reason) {
      onError(messageFor(reason));
    } finally {
      setSaving(false);
    }
  }

  // Must match the version in the currently distributed package (packages/bridge/host.py BRIDGE_VERSION
  // and artifacts/bridge-windows/KitchenHubPrintBridge-<version>-windows-x64/manifest.json) — Install-Bridge.ps1
  // installs to versions\<manifest.version>\, so this drifts if the package is rebuilt without updating it here.
  // The installed package's version folder; keep in step with BRIDGE_VERSION
  // in packages/bridge/host.py whenever a new bridge package is built.
  const BRIDGE_PACKAGE_VERSION = "0.5.1";
  const command = `& "C:\\Program Files\\Kitchen Hub\\Print Bridge\\versions\\${BRIDGE_PACKAGE_VERSION}\\KitchenHubPrintBridgeService.exe" enrol --token-stdin --base-url "${runtime.apiUrl}" --printer-id "${printerId}"`;
  return <div className="team-layout"><section className="admin-card"><div className="admin-card-heading"><div><p className="section-label">Pilot-PC secure enrolment</p><h2>Outbound bridge connection</h2></div><span className="safe-pill">{physicalPrintingEnabled ? "Real printing" : "Simulated only"}</span></div><p>The one-time token is issued only to this AAL2-protected session, expires after ten minutes and is never placed in the command line.{physicalPrintingEnabled ? "" : " Physical printer submission remains unavailable."}</p>{grant ? <div className="bridge-grant"><label htmlFor="bridge-enrolment-token">One-time token · enter locally<input id="bridge-enrolment-token" aria-label="One-time bridge enrolment token" readOnly autoComplete="off" spellCheck={false} value={grant.enrolment_token} onFocus={(event) => event.currentTarget.select()} onClick={(event) => event.currentTarget.select()} /></label><p>Click the token field, then press Ctrl+C. Verify the Windows clipboard length is 64 before enrolment. Expires {new Date(grant.expires_at).toLocaleString()}. Never paste it into chat, tickets or scripts.</p><button className="secondary-button" type="button" onClick={() => setGrant(null)}>Clear token from screen</button></div> : <form onSubmit={create}><label>Bridge name<input required maxLength={120} value={requestedName} onChange={(event) => setRequestedName(event.target.value)} /></label><button className="primary-button" disabled={!canManage || saving || !siteId || !requestedName.trim()}>{saving ? "Issuing…" : "Issue one-time enrolment token"}</button></form>}<div className="admin-list"><h3>Enrolled bridges</h3>{bridges.map((bridge) => <div className="admin-list-row" key={bridge.id}><div><strong>{bridge.name}</strong><p>{bridge.status} · {bridge.bridge_version} · {bridge.last_heartbeat_at ? `last seen ${new Date(bridge.last_heartbeat_at).toLocaleString()}` : "never connected"}</p></div><button className="danger-button" type="button" disabled={saving || bridge.status === "disabled"} onClick={() => void revoke(bridge)}>{bridge.status === "disabled" ? "Deactivated" : "Deactivate"}</button></div>)}</div></section><aside className="team-controls"><section className="admin-card"><p className="section-label">Local installation command</p><h3>Run only this command in elevated PowerShell</h3><code>{command}</code><button className="secondary-button" type="button" onClick={() => void navigator.clipboard.writeText(command)}>Copy command only</button><p className="muted">The token is read through standard input and never placed on the command line. The local printer identifier is <code>{printerId}</code>.</p></section><section className="safety-card"><div><Icon name="shield" /><span>Physical safety lock</span></div>{physicalPrintingEnabled
        ? <><strong>Physical printing on</strong><p>A bridge enrolled here sends real labels to its mapped printers.</p></>
        : <><strong>Disabled</strong><p>Transport remains simulated. Nothing is sent to a physical printer.</p></>}</section></aside></div>;
}

function PrinterAdministration({ departments, references, organizationId, api, canManage, physicalPrintingEnabled, onError, onToast }: { departments: Department[]; references: ReferenceData | null; organizationId: string; api?: KitchenHubApi; canManage: boolean; physicalPrintingEnabled: boolean; onError: (message: string) => void; onToast: (message: string) => void }) {
  const siteId = useSelectedSite();
  const [printers, setPrinters] = useState<Printer[]>([]);
  const [templates, setTemplates] = useState<LabelTemplateOption[]>([]);
  const [selectedPrinterId, setSelectedPrinterId] = useState("");
  const [assignments, setAssignments] = useState<PrinterTemplateAssignment[]>([]);
  const [renameDrafts, setRenameDrafts] = useState<Record<string, string>>({});
  const [labelTypeId, setLabelTypeId] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [priority, setPriority] = useState(50);
  const [saving, setSaving] = useState(false);

  async function refreshPrinters() {
    if (!api || !canManage || !siteId) { setPrinters([]); return; }
    try {
      setPrinters(await api.listPrinters(organizationId, siteId));
    } catch (reason) {
      onError(messageFor(reason));
    }
  }

  async function refreshAssignments(printerId: string) {
    if (!api || !canManage || !siteId || !printerId) { setAssignments([]); return; }
    try {
      setAssignments(await api.listPrinterTemplateAssignments(organizationId, siteId, printerId));
    } catch (reason) {
      onError(messageFor(reason));
    }
  }

  useEffect(() => {
    setSelectedPrinterId("");
    setAssignments([]);
    void refreshPrinters();
    if (api && canManage && siteId) {
      api.listLabelTemplateOptions(organizationId, siteId)
        .then(setTemplates)
        .catch((reason: unknown) => onError(messageFor(reason)));
    }
  }, [api, canManage, organizationId, siteId]);

  useEffect(() => { void refreshAssignments(selectedPrinterId); }, [api, canManage, organizationId, siteId, selectedPrinterId]);

  async function saveRename(printer: Printer) {
    if (!api || !canManage) return;
    const draft = renameDrafts[printer.id];
    if (draft === undefined) return;
    setSaving(true);
    onError("");
    try {
      await api.renamePrinter(organizationId, siteId, printer.id, {
        display_name: draft.trim() || null, expected_version: printer.version,
      });
      onToast(`Renamed printer to "${draft.trim() || printer.name}"`);
      setRenameDrafts((current) => { const next = { ...current }; delete next[printer.id]; return next; });
      await refreshPrinters();
    } catch (reason) {
      onError(messageFor(reason));
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(printer: Printer) {
    if (!api || !canManage) return;
    if (printer.active && !window.confirm(`Deactivate ${printer.display_name || printer.name}? It will stop appearing as a print destination.`)) return;
    setSaving(true);
    onError("");
    try {
      await api.setPrinterActive(organizationId, siteId, printer.id, {
        active: !printer.active, expected_version: printer.version,
      });
      await refreshPrinters();
    } catch (reason) {
      onError(messageFor(reason));
    } finally {
      setSaving(false);
    }
  }

  async function addAssignment(event: FormEvent) {
    event.preventDefault();
    if (!api || !canManage || !selectedPrinterId || !labelTypeId || !templateId) return;
    setSaving(true);
    onError("");
    try {
      await api.createPrinterTemplateAssignment(organizationId, siteId, selectedPrinterId, {
        label_type_id: labelTypeId, template_id: templateId, priority,
      });
      onToast("Template assignment saved");
      setLabelTypeId(""); setTemplateId(""); setPriority(50);
      await refreshAssignments(selectedPrinterId);
    } catch (reason) {
      onError(messageFor(reason));
    } finally {
      setSaving(false);
    }
  }

  async function toggleAssignment(assignment: PrinterTemplateAssignment) {
    if (!api || !canManage) return;
    setSaving(true);
    onError("");
    try {
      await api.updatePrinterTemplateAssignment(organizationId, siteId, assignment.id, {
        priority: assignment.priority, active: !assignment.active,
      });
      await refreshAssignments(selectedPrinterId);
    } catch (reason) {
      onError(messageFor(reason));
    } finally {
      setSaving(false);
    }
  }

  async function removeAssignment(assignment: PrinterTemplateAssignment) {
    if (!api || !canManage) return;
    if (!window.confirm(`Remove the ${assignment.template_name} assignment for ${assignment.label_type_name}?`)) return;
    setSaving(true);
    onError("");
    try {
      await api.deletePrinterTemplateAssignment(organizationId, siteId, assignment.id);
      await refreshAssignments(selectedPrinterId);
    } catch (reason) {
      onError(messageFor(reason));
    } finally {
      setSaving(false);
    }
  }

  const selectedPrinter = printers.find((item) => item.id === selectedPrinterId) ?? null;
  const labelTypes = references?.label_types ?? [];

  return <div className="team-layout">
    <section className="admin-card">
      <div className="admin-card-heading"><div><p className="section-label">Printer fleet</p><h2>Printers &amp; routing</h2></div><span className="safe-pill">{physicalPrintingEnabled ? "Real printing" : "Simulated only"}</span></div>
      <div className="admin-list">
        <h3>Discovered printers</h3>
        {printers.length === 0 && <p className="muted">No printers discovered yet at this site. Printers appear automatically once a bridge reports a mapped Windows queue.</p>}
        {printers.map((printer) => (
          <div className="admin-list-row" key={printer.id}>
            <div>
              <strong>{printer.display_name || printer.name}</strong>
              <p>{printer.name} · {printer.model} · {printer.dpi} dpi · {printer.bridge_status} · queue {printer.queue_availability}{!printer.active && " · deactivated"}</p>
              <input
                aria-label={`Friendly name for ${printer.name}`}
                placeholder="Friendly name (optional)"
                value={renameDrafts[printer.id] ?? printer.display_name ?? ""}
                onChange={(event) => setRenameDrafts((current) => ({ ...current, [printer.id]: event.target.value }))}
              />
              <button className="secondary-button" type="button" disabled={!canManage || saving || renameDrafts[printer.id] === undefined} onClick={() => void saveRename(printer)}>Save name</button>
              <button className={selectedPrinterId === printer.id ? "primary-button" : "secondary-button"} type="button" onClick={() => setSelectedPrinterId(printer.id)}>Manage templates</button>
            </div>
            <button className="danger-button" type="button" disabled={!canManage || saving} onClick={() => void toggleActive(printer)}>{printer.active ? "Deactivate" : "Reactivate"}</button>
          </div>
        ))}
      </div>
    </section>
    <aside className="team-controls">
      <section className="admin-card">
        <p className="section-label">Template routing</p>
        <h3>{selectedPrinter ? (selectedPrinter.display_name || selectedPrinter.name) : "Select a printer"}</h3>
        {selectedPrinter ? <>
          <div className="admin-list">
            {assignments.length === 0 && <p className="muted">No admin-managed assignments yet. The bridge auto-provisions a default preview template on first heartbeat.</p>}
            {assignments.map((assignment) => (
              <div className="admin-list-row" key={assignment.id}>
                <div><strong>{assignment.label_type_name}</strong><p>{assignment.template_name} · priority {assignment.priority}{!assignment.active && " · inactive"}</p></div>
                <div>
                  <button className="secondary-button" type="button" disabled={saving} onClick={() => void toggleAssignment(assignment)}>{assignment.active ? "Disable" : "Enable"}</button>
                  <button className="danger-button" type="button" disabled={saving} onClick={() => void removeAssignment(assignment)}>Remove</button>
                </div>
              </div>
            ))}
          </div>
          <form onSubmit={addAssignment}>
            <label>Label type<select required value={labelTypeId} onChange={(event) => setLabelTypeId(event.target.value)}><option value="">Choose label type</option>{labelTypes.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
            <label>Template<select required value={templateId} onChange={(event) => setTemplateId(event.target.value)}><option value="">Choose template</option>{templates.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
            <label>Priority (lower wins)<input type="number" min={1} max={1000} value={priority} onChange={(event) => setPriority(Number(event.target.value))} /></label>
            <button className="primary-button" disabled={!canManage || saving || !labelTypeId || !templateId}>{saving ? "Saving…" : "Assign template"}</button>
          </form>
        </> : <p className="muted">Choose a printer on the left to view or change its template routing.</p>}
      </section>
      <section className="safety-card"><div><Icon name="shield" /><span>Physical safety lock</span></div>{physicalPrintingEnabled
        ? <><strong>Physical printing on</strong><p>Routing changes here decide which template real labels print with. Check a label after changing one.</p></>
        : <><strong>Disabled</strong><p>Routing changes here only affect which template a shadow print job resolves to. No physical output exists.</p></>}</section>
    </aside>
  </div>;
}

function SettingsWorkspace({ departments, organizationId, api, canManage, canManageOrganization, physicalPrintingEnabled, onError, onToast }: { departments: Department[]; organizationId: string; api?: KitchenHubApi; canManage: boolean; canManageOrganization: boolean; physicalPrintingEnabled: boolean; onError: (message: string) => void; onToast: (message: string) => void }) {
  const siteId = useSelectedSite();
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [printers, setPrinters] = useState<Printer[]>([]);
  const [defaultPrinterId, setDefaultPrinterId] = useState("");
  const [labelWidthMm, setLabelWidthMm] = useState(62);
  const [labelHeightMm, setLabelHeightMm] = useState(100);
  const [saving, setSaving] = useState(false);
  const [orgSettings, setOrgSettings] = useState<OrganizationSettings | null>(null);
  const [autoLogoutMinutes, setAutoLogoutMinutes] = useState("");
  const [savingAutoLogout, setSavingAutoLogout] = useState(false);

  useEffect(() => {
    if (!api || !canManageOrganization) return;
    void api.getOrganizationSettings(organizationId).then((result) => {
      setOrgSettings(result);
      setAutoLogoutMinutes(result.inactivity_timeout_minutes === null ? "" : String(result.inactivity_timeout_minutes));
    }).catch((reason: unknown) => onError(messageFor(reason)));
  }, [api, canManageOrganization, organizationId, onError]);

  async function saveAutoLogout(event: FormEvent) {
    event.preventDefault();
    if (!api || !orgSettings) return;
    setSavingAutoLogout(true);
    try {
      const minutes = autoLogoutMinutes.trim() === "" ? null : Number(autoLogoutMinutes);
      const updated = await api.updateOrganizationInactivityTimeout(organizationId, {
        expected_version: orgSettings.version, inactivity_timeout_minutes: minutes,
      });
      setOrgSettings(updated);
      onToast("Auto-logout timeout saved — takes effect on next sign-in");
    } catch (reason) { onError(messageFor(reason)); } finally { setSavingAutoLogout(false); }
  }

  useEffect(() => {
    if (!api || !canManage || !siteId) { setSettings(null); return; }
    let active = true;
    void Promise.all([
      api.getSiteSettings(organizationId, siteId),
      api.listPrinters(organizationId, siteId),
    ]).then(([site, sitePrinters]) => {
      if (!active) return;
      setSettings(site);
      setPrinters(sitePrinters);
      setDefaultPrinterId(site.default_printer_id ?? "");
      setLabelWidthMm(site.label_width_mm);
      setLabelHeightMm(site.label_height_mm);
    }).catch((reason: unknown) => onError(messageFor(reason)));
    return () => { active = false; };
  }, [api, canManage, organizationId, siteId]);

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!api || !canManage || !settings) return;
    setSaving(true);
    onError("");
    try {
      const updated = await api.updateSitePrintSettings(organizationId, siteId, {
        expected_version: settings.version,
        default_printer_id: defaultPrinterId || null,
        label_width_mm: labelWidthMm, label_height_mm: labelHeightMm,
      });
      setSettings(updated);
      onToast("Print settings saved");
    } catch (reason) {
      onError(messageFor(reason));
    } finally {
      setSaving(false);
    }
  }

  return <div className="team-layout">
    <section className="admin-card">
      <div className="admin-card-heading"><div><p className="section-label">Site settings</p><h2>Default printer &amp; label size</h2></div><span className="safe-pill">{physicalPrintingEnabled ? "Real printing" : "Simulated only"}</span></div>
      {settings ? <form onSubmit={save}>
        <label>Default printer<select value={defaultPrinterId} onChange={(event) => setDefaultPrinterId(event.target.value)}><option value="">No default</option>{printers.map((printer) => <option key={printer.id} value={printer.id}>{printer.display_name || printer.name}</option>)}</select></label>
        <label>Label width (mm, 20–120)<input type="number" min={20} max={120} value={labelWidthMm} onChange={(event) => setLabelWidthMm(Number(event.target.value))} /></label>
        <label>Label height (mm, 20–200)<input type="number" min={20} max={200} value={labelHeightMm} onChange={(event) => setLabelHeightMm(Number(event.target.value))} /></label>
        <button className="primary-button" disabled={!canManage || saving}>{saving ? "Saving…" : "Save settings"}</button>
      </form> : <p className="muted">Choose a site to view or change its print settings.</p>}
    </section>
    <aside className="team-controls">
      {canManageOrganization && <section className="admin-card"><p className="section-label">Auto-logout</p><h3>Shared-device inactivity timeout</h3>{orgSettings ? <form onSubmit={saveAutoLogout}><label>Minutes idle before lock (blank = platform default)<input type="number" min={1} max={480} placeholder="Platform default" value={autoLogoutMinutes} onChange={(event) => setAutoLogoutMinutes(event.target.value.replace(/\D/g, ""))} /></label><button className="primary-button" disabled={savingAutoLogout}>{savingAutoLogout ? "Saving…" : "Save auto-logout"}</button></form> : <p className="muted">{api ? "Loading…" : "Not available in local preview."}</p>}<p className="muted">Applies to every site in this organisation. Staff are asked to sign back in with PIN or password once idle this long.</p></section>}
      <section className="safety-card"><div><Icon name="shield" /><span>Physical safety lock</span></div>{physicalPrintingEnabled
        ? <><strong>Physical printing on</strong><p>The default printer and label size decide where and how real labels print at this site.</p></>
        : <><strong>Disabled</strong><p>Label size and default printer only steer which shadow print jobs a site defaults to. No physical output exists.</p></>}</section>
    </aside>
  </div>;
}

const SOURCE_TYPE_LABELS: Record<string, string> = {
  quick_print: "Quick Print", batch_print: "Batch Print",
  prep_list_print: "Prep List", ppds_label_print: "PPDS / Takeaway",
};

function PrintHistoryWorkspace({ departments, organizationId, api, canPrint, canReprint, physicalPrintingEnabled, onError, onToast }: { departments: Department[]; organizationId: string; api?: KitchenHubApi; canPrint: boolean; canReprint: boolean; physicalPrintingEnabled: boolean; onError: (message: string) => void; onToast: (message: string) => void }) {
  const siteId = useSelectedSite();
  const [sourceType, setSourceType] = useState("");
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [entries, setEntries] = useState<PrintJobHistoryEntry[]>([]);
  const [nextCursor, setNextCursor] = useState<{ createdAt: string; id: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [reprinting, setReprinting] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  function applyPreset(preset: "today" | "week") {
    const today = new Date();
    const toDateInput = (date: Date) => date.toLocaleDateString("en-CA");
    if (preset === "today") {
      setDateFrom(toDateInput(today));
      setDateTo(toDateInput(today));
    } else {
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 6);
      setDateFrom(toDateInput(weekAgo));
      setDateTo(toDateInput(today));
    }
  }

  async function loadPage(reset: boolean) {
    if (!api || !siteId) return;
    setLoading(true);
    onError("");
    try {
      const page = await api.searchPrintHistory(organizationId, siteId, {
        sourceType: sourceType || undefined, status: status || undefined,
        createdFrom: dateFrom ? startOfLocalDay(dateFrom) : undefined,
        createdTo: dateTo ? endOfLocalDay(dateTo) : undefined,
        beforeCreatedAt: reset ? undefined : nextCursor?.createdAt,
        beforeId: reset ? undefined : nextCursor?.id,
      });
      setEntries((current) => reset ? page.items : [...current, ...page.items]);
      setNextCursor(
        page.next_cursor_created_at && page.next_cursor_id
          ? { createdAt: page.next_cursor_created_at, id: page.next_cursor_id }
          : null,
      );
    } catch (reason) {
      onError(messageFor(reason));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void loadPage(true); }, [api, organizationId, siteId, sourceType, status, dateFrom, dateTo]);

  async function reprint(entry: PrintJobHistoryEntry) {
    if (!api || !canReprint) return;
    if (!window.confirm(`Reprint this ${SOURCE_TYPE_LABELS[entry.source_type] ?? entry.source_type} job from ${new Date(entry.created_at).toLocaleString()}?`)) return;
    setReprinting(entry.id);
    onError("");
    try {
      const result = await api.reprintPrintJob(organizationId, entry.id, `reprint-${entry.id}-${Date.now()}`);
      onToast(reprintResultToast(result));
      await loadPage(true);
    } catch (reason) {
      onError(messageFor(reason));
    } finally {
      setReprinting(null);
    }
  }

  async function reportNotPrinted(entry: PrintJobHistoryEntry) {
    if (!api || !canPrint) return;
    if (!window.confirm(`Report that the label from ${entry.printer_name} didn't come out? This can't be undone — reprint it if it's still needed.`)) return;
    setConfirming(entry.id);
    onError("");
    try {
      const result = await api.confirmPrintOutcome(organizationId, entry.id, false);
      onToast(outcomeConfirmedToast(result.confirmed_outcome));
      await loadPage(true);
    } catch (reason) {
      onError(messageFor(reason));
    } finally {
      setConfirming(null);
    }
  }

  async function exportCsv() {
    if (!api || !siteId) return;
    setExporting(true);
    onError("");
    try {
      const blob = await api.exportPrintHistoryCsv(organizationId, siteId, {
        sourceType: sourceType || undefined, status: status || undefined,
        createdFrom: dateFrom ? startOfLocalDay(dateFrom) : undefined,
        createdTo: dateTo ? endOfLocalDay(dateTo) : undefined,
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `print-history-${siteId}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (reason) {
      onError(messageFor(reason));
    } finally {
      setExporting(false);
    }
  }

  function itemSummary(entry: PrintJobHistoryEntry): string {
    if (entry.items.length === 0) return "No items";
    const [first, ...rest] = entry.items;
    const firstLabel = `${first.product_name ?? "Unknown product"} ×${first.quantity}`;
    return rest.length ? `${firstLabel} + ${rest.length} more` : firstLabel;
  }

  return <div className="team-layout">
    <section className="admin-card">
      <div className="admin-card-heading"><div><p className="section-label">Print history</p><h2>{physicalPrintingEnabled ? "Every print job, searchable" : "Every shadow job, searchable"}</h2></div><div style={{ display: "flex", gap: "8px", alignItems: "center" }}><button className="secondary-button" type="button" disabled={exporting || !siteId} onClick={() => void exportCsv()}>{exporting ? "Exporting…" : "Export CSV"}</button><span className="safe-pill">{physicalPrintingEnabled ? "Physical printing on" : "Simulated only"}</span></div></div>
      <label>Print type<select value={sourceType} onChange={(event) => setSourceType(event.target.value)}><option value="">All types</option>{Object.entries(SOURCE_TYPE_LABELS).map(([code, name]) => <option key={code} value={code}>{name}</option>)}</select></label>
      <label>Status<select value={status} onChange={(event) => setStatus(event.target.value)}><option value="">All statuses</option>{PRINT_STATUS_FILTERS.map(([code, label]) => <option key={code} value={code}>{label}</option>)}</select></label>
      <div className="date-range-row">
        <label>From<input type="date" value={dateFrom} max={dateTo || undefined} onChange={(event) => setDateFrom(event.target.value)} /></label>
        <label>To<input type="date" value={dateTo} min={dateFrom || undefined} onChange={(event) => setDateTo(event.target.value)} /></label>
        <div className="date-presets">
          <button type="button" className="secondary-button compact-button" onClick={() => applyPreset("today")}>Today</button>
          <button type="button" className="secondary-button compact-button" onClick={() => applyPreset("week")}>Last 7 days</button>
          {(dateFrom || dateTo) && <button type="button" className="secondary-button compact-button" onClick={() => { setDateFrom(""); setDateTo(""); }}>Clear dates</button>}
        </div>
      </div>
      <div className="admin-list">
        {entries.length === 0 && !loading && <p className="muted">No print jobs match these filters at this site yet.</p>}
        {entries.map((entry) => (
          <div className="admin-list-row" key={entry.id}>
            <div>
              <strong>{SOURCE_TYPE_LABELS[entry.source_type] ?? entry.source_type} · {entry.printer_name}</strong>
              <p>{new Date(entry.created_at).toLocaleString()} · {historyStatusLabel(entry)} · {entry.actor_display_name ?? "Unknown user"}{entry.reprint_of_job_id && " · reprint"}</p>
              <p>{itemSummary(entry)}</p>
              {entry.confirmed_outcome && <p className="muted">Checked by {entry.confirmed_by_display_name ?? "unknown user"}{entry.confirmed_at && ` · ${new Date(entry.confirmed_at).toLocaleString()}`}</p>}
            </div>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", justifyContent: "flex-end" }}>
              {canReportNotPrinted(entry) && canPrint && <button className="secondary-button" type="button" disabled={confirming === entry.id} onClick={() => void reportNotPrinted(entry)}>Didn't print</button>}
              {entry.source_type !== "ppds_label_print" && <button className="secondary-button" type="button" disabled={!canReprint || reprinting === entry.id} onClick={() => void reprint(entry)} title={canReprint ? undefined : "Reprint requires MFA and labels.reprint"}>{reprinting === entry.id ? "Reprinting…" : "Reprint"}</button>}
            </div>
          </div>
        ))}
      </div>
      {nextCursor && <button className="secondary-button" type="button" disabled={loading} onClick={() => void loadPage(false)}>{loading ? "Loading…" : "Load more"}</button>}
    </section>
    <aside className="team-controls">
      <section className="safety-card"><div><Icon name="shield" /><span>Physical safety lock</span></div>{physicalPrintingEnabled
        ? <><strong>Physical printing on</strong><p>Real labels print straight away — there's nothing to confirm. If one doesn't come out, tap Didn't print on that job so it's on record, then Reprint.</p></>
        : <><strong>Disabled</strong><p>Reprinting only creates a new shadow job with a fresh audit trail. No physical output exists.</p></>}</section>
      <section className="admin-card"><p className="section-label">Regulated labels</p><h3>PPDS / Takeaway</h3><p>Direct reprint is refused for PPDS/takeaway jobs — return to the PPDS Labels screen so current allergen and business-identity validation runs again.</p></section>
    </aside>
  </div>;
}

const SHELF_LIFE_REVIEW_DEPARTMENT_KEY = "kitchen-hub.shelf-life-review.department";
const BULK_APPROVE_BATCH = 100;

function ShelfLifeReviewWorkspace({ departments, organizationId, api, canApprove, onError, onToast }: { departments: Department[]; organizationId: string; api?: KitchenHubApi; canApprove: boolean; onError: (message: string) => void; onToast: (message: string) => void }) {
  const activeDepartments = useMemo(() => departments.filter((item) => item.active), [departments]);
  const multiSite = new Set(activeDepartments.map((item) => item.site_id)).size > 1;
  const [departmentId, setDepartmentId] = useState("");
  const [rules, setRules] = useState<DraftShelfLifeRuleForReview[]>([]);
  // Nothing is ticked for the reviewer: every approval is a deliberate choice.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [actingOn, setActingOn] = useState("");
  const [bulkProgress, setBulkProgress] = useState("");
  const [editing, setEditing] = useState<DraftShelfLifeRuleForReview | null>(null);
  const [search, setSearch] = useState("");
  const [storageFilter, setStorageFilter] = useState<ReviewStorageFilter>("all");
  const [scope, setScope] = useState<ReviewScope>("all");
  const [typeFilter, setTypeFilter] = useState<ReviewTypeFilter>("all");
  const [visibleCount, setVisibleCount] = useState(REVIEW_PAGE_SIZE);
  const filteredRules = useMemo(() => filterReviewRules(rules, search, storageFilter, scope, typeFilter), [rules, search, storageFilter, scope, typeFilter]);
  // Counted within the current Show choice, so the numbers match what picking a type will list.
  const typeCounts = useMemo(() => countProductsByType(scope === "in-use" ? rules.filter((rule) => kitchenUseLabel(rule)) : rules), [rules, scope]);
  const storageCounts = useMemo(() => countByStorageState(scope === "in-use" ? rules.filter((rule) => kitchenUseLabel(rule)) : rules), [rules, scope]);
  const inUse = useMemo(() => countInKitchenUse(rules), [rules]);
  const productDrafts = useMemo(() => draftsByProduct(rules), [rules]);

  // A tick on a row the filter has hidden would be approved unseen, so changing
  // what's shown clears the selection as well as the page.
  useEffect(() => {
    setVisibleCount(REVIEW_PAGE_SIZE);
    setSelected(new Set());
  }, [search, storageFilter, scope, typeFilter, departmentId]);

  const selectedDepartment = activeDepartments.find((item) => item.id === departmentId);

  // Several sites each have a "Kitchen", so remember the reviewer's last choice rather than guessing again.
  useEffect(() => {
    if (departmentId || !activeDepartments.length) return;
    let remembered = "";
    try { remembered = localStorage.getItem(SHELF_LIFE_REVIEW_DEPARTMENT_KEY) ?? ""; } catch { /* storage unavailable */ }
    setDepartmentId((activeDepartments.find((item) => item.id === remembered)
      ?? activeDepartments.find((item) => item.name.trim().toLowerCase() === "kitchen")
      ?? activeDepartments[0]).id);
  }, [activeDepartments, departmentId]);

  useEffect(() => {
    if (!departmentId) return;
    try { localStorage.setItem(SHELF_LIFE_REVIEW_DEPARTMENT_KEY, departmentId); } catch { /* storage unavailable */ }
  }, [departmentId]);

  const load = useCallback(async () => {
    if (!api || !departmentId) return;
    setLoading(true);
    onError("");
    try {
      const loaded = await api.draftShelfLifeRulesForReview(organizationId, departmentId);
      setRules(loaded);
      // Start on the short list when there is one; the whole supplier
      // catalogue is one click away.
      setScope(countInKitchenUse(loaded).rules > 0 ? "in-use" : "all");
      setSelected(new Set());
    } catch (reason) {
      onError(messageFor(reason));
    } finally {
      setLoading(false);
    }
  }, [api, departmentId, onError, organizationId]);

  useEffect(() => { void load(); }, [load]);

  // Placeholders can be bulk-approved; the confirmation spells out every
  // storage state and value being approved so nothing goes through unseen.
  const bulkEligible = filteredRules;
  const placeholderCount = rules.filter((rule) => rule.generated_default).length;

  async function decide(rule: DraftShelfLifeRuleForReview, action: "approve" | "retire") {
    if (!api) return;
    const value = `${titleCase(rule.storage_state)} · ${durationLabel(rule.duration_value, rule.duration_unit)}`;
    const question = action === "approve"
      ? `Approve ${rule.product_name}: ${value}? This date will be printed on real labels.`
      : `Retire the draft for ${rule.product_name} (${value})? It won't be offered for approval again.`;
    if (!window.confirm(question)) return;
    setActingOn(rule.id);
    onError("");
    try {
      if (action === "approve") await api.approveShelfLifeRule(organizationId, rule.product_id, rule);
      else await api.retireShelfLifeRule(organizationId, rule.product_id, rule);
      onToast(`${rule.product_name}: shelf-life rule ${action === "approve" ? "approved" : "retired"}`);
      // Drop just this row rather than refetching thousands, so the reviewer
      // keeps their search, filter and place in the list.
      setRules((current) => current.filter((item) => item.id !== rule.id));
      setSelected((current) => {
        const next = new Set(current);
        next.delete(rule.id);
        return next;
      });
    } catch (reason) {
      onError(messageFor(reason));
    } finally {
      setActingOn("");
    }
  }

  // One decision per product: every draft it has, listed in the confirmation,
  // including any the storage filter is hiding.
  async function approveProduct(rule: DraftShelfLifeRuleForReview) {
    if (!api) return;
    const drafts = productDrafts.get(rule.product_id) ?? [rule];
    const lines = drafts.map((item) => `• ${titleCase(item.storage_state)} · ${durationLabel(item.duration_value, item.duration_unit)}`);
    const shown = filteredRules.filter((item) => item.product_id === rule.product_id).length;
    const question = [
      `Approve all ${drafts.length} shelf-life rules for ${rule.product_name}? These dates will be printed on real labels.`,
      "",
      ...lines,
      ...(shown < drafts.length ? ["", `${drafts.length - shown} of these are hidden by the storage filter.`] : []),
    ].join("\n");
    if (!window.confirm(question)) return;
    setActingOn(rule.product_id);
    onError("");
    try {
      const result = await api.bulkApproveShelfLifeRules(
        organizationId,
        drafts.map((item) => ({ product_id: item.product_id, rule_id: item.id, expected_version: item.version })),
      );
      const approvedIds = new Set(result.approved_rule_ids);
      setRules((current) => current.filter((item) => !approvedIds.has(item.id)));
      setSelected((current) => new Set([...current].filter((id) => !approvedIds.has(id))));
      onToast(
        `${rule.product_name}: ${approvedIds.size} shelf-life rule${approvedIds.size === 1 ? "" : "s"} approved`
        + (result.skipped.length ? ` · ${result.skipped.length} skipped (changed since this list loaded — refreshed below)` : ""),
      );
      if (result.skipped.length) await load();
    } catch (reason) {
      onError(messageFor(reason));
    } finally {
      setActingOn("");
    }
  }

  async function saveEdit(input: ShelfLifeRevisionInput) {
    if (!api || !editing) return;
    const rule = editing;
    onError("");
    try {
      await api.reviseShelfLifeRule(organizationId, rule.product_id, rule.id, input);
      onToast(`${rule.product_name}: ${titleCase(rule.storage_state)} set to ${durationLabel(input.duration_value, input.duration_unit)} and approved`);
      setRules((current) => current.filter((item) => item.id !== rule.id));
      setSelected((current) => new Set([...current].filter((id) => id !== rule.id)));
    } catch (reason) {
      onError(messageFor(reason));
    } finally {
      setEditing(null);
    }
  }

  function toggle(id: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setSelected((current) => (current.size === bulkEligible.length && bulkEligible.length > 0 ? new Set() : new Set(bulkEligible.map((rule) => rule.id))));
  }

  async function approveSelected() {
    if (!api || selected.size === 0) return;
    const chosen = bulkEligible.filter((rule) => selected.has(rule.id));
    if (!chosen.length) return;
    const groups = new Map<string, number>();
    for (const rule of chosen) {
      const key = `${titleCase(rule.storage_state)} at ${durationLabel(rule.duration_value, rule.duration_unit)}`;
      groups.set(key, (groups.get(key) ?? 0) + 1);
    }
    const lines = [...groups.entries()].sort((a, b) => b[1] - a[1]).map(([key, count]) => `• ${count} × ${key}`);
    const shownLines = lines.length > 8 ? [...lines.slice(0, 8), `• …and ${lines.length - 8} other values`] : lines;
    const placeholders = chosen.filter((rule) => rule.generated_default).length;
    const ambientPlaceholders = chosen.filter((rule) => rule.generated_default && rule.storage_state === "ambient").length;
    const question = [
      `Approve ${chosen.length} shelf-life rule${chosen.length === 1 ? "" : "s"}? These dates will be printed on real labels.`,
      "",
      ...shownLines,
      ...(placeholders ? ["", `${placeholders} of these are still at the kitchen default. Check any product that keeps for a different time.`] : []),
      ...(ambientPlaceholders ? [`${ambientPlaceholders} are 90 days ambient, the default for dry goods once opened.`] : []),
    ].join("\n");
    if (!window.confirm(question)) return;
    setApproving(true);
    onError("");
    let approvedCount = 0;
    let skippedCount = 0;
    try {
      // Sent in batches so thousands of approvals never hit a request timeout,
      // and whatever has gone through already stays approved if one batch fails.
      for (let index = 0; index < chosen.length; index += BULK_APPROVE_BATCH) {
        const batch = chosen.slice(index, index + BULK_APPROVE_BATCH);
        setBulkProgress(`Approving ${index + batch.length} of ${chosen.length}…`);
        const result = await api.bulkApproveShelfLifeRules(
          organizationId,
          batch.map((rule) => ({ product_id: rule.product_id, rule_id: rule.id, expected_version: rule.version })),
        );
        approvedCount += result.approved_rule_ids.length;
        skippedCount += result.skipped.length;
        const approvedIds = new Set(result.approved_rule_ids);
        setRules((current) => current.filter((rule) => !approvedIds.has(rule.id)));
        setSelected((current) => new Set([...current].filter((id) => !approvedIds.has(id))));
      }
      onToast(
        `${approvedCount} shelf-life rule${approvedCount === 1 ? "" : "s"} approved`
        + (skippedCount ? ` · ${skippedCount} skipped (changed since this list loaded — refreshed below)` : ""),
      );
      if (skippedCount) await load();
    } catch (reason) {
      onError(`${approvedCount ? `${approvedCount} approved before this stopped. ` : ""}${messageFor(reason)}`);
    } finally {
      setApproving(false);
      setBulkProgress("");
    }
  }

  return <div className="team-layout">
    <section className="admin-card">
      <div className="admin-card-heading">
        <div><p className="section-label">Draft shelf-life rules · active products</p><h2>{rules.length} awaiting a decision{selectedDepartment && multiSite ? ` · ${selectedDepartment.name}, ${selectedDepartment.site_name}` : ""}</h2></div>
        <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
          <select aria-label="Department" value={departmentId} onChange={(event) => setDepartmentId(event.target.value)} disabled={!activeDepartments.length}>
            {activeDepartments.map((item) => <option key={item.id} value={item.id}>{item.name}{multiSite ? ` — ${item.site_name}` : ""}</option>)}
          </select>
          <button className="secondary-button" type="button" onClick={toggleAll} disabled={!canApprove || approving || !bulkEligible.length}>{selected.size === bulkEligible.length && bulkEligible.length > 0 ? "Clear all" : `Select all ${bulkEligible.length}`}</button>
          <button className="primary-button" type="button" disabled={!canApprove || approving || selected.size === 0} title={canApprove ? undefined : "Approving shelf-life rules requires MFA and shelf_life.approve"} onClick={() => void approveSelected()}>{approving ? (bulkProgress || "Approving…") : `Approve selected (${selected.size})`}</button>
        </div>
      </div>
      <p className="muted">{rules.length === 0 ? "" : "Nothing is ticked for you. Select all ticks every rule matching your search and storage filter, including ones further down the list. "}Products already covered by an approved rule, and archived products, aren't listed.</p>
      {placeholderCount > 0 && <p className="warning-note review-flag-note">{placeholderCount} of these are still at the kitchen default: chilled 3 days, frozen 90, defrosted 2, and 90 days ambient for dry goods once opened. Use Edit for any product that keeps for a different time, then approve the rest. Approve product decides all of a product's rules at once.</p>}
      <div className="catalogue-toolbar">
        <label className="search-box"><Icon name="search" /><input aria-label="Search draft rules by product" placeholder="Search products…" value={search} onChange={(event) => setSearch(event.target.value)} /></label>
        <div className="toolbar-actions">
          <label className="status-filter">
            <span>Show</span>
            <select aria-label="Which products" value={scope} onChange={(event) => setScope(event.target.value as ReviewScope)}>
              <option value="in-use">Used by the kitchen ({inUse.products} products · {inUse.rules} rules)</option>
              <option value="all">Whole catalogue ({rules.length} rules)</option>
            </select>
          </label>
          <label className="status-filter">
            <span>Type</span>
            <select aria-label="Food type" value={typeFilter} onChange={(event) => setTypeFilter(event.target.value as ReviewTypeFilter)}>
              <option value="all">All types</option>
              {FOOD_TYPE_LABELS.filter(([kind]) => typeCounts.has(kind)).map(([kind, label]) => <option key={kind} value={kind}>{label} ({typeCounts.get(kind)})</option>)}
              {typeCounts.has("none") && <option value="none">Chef's own and other ({typeCounts.get("none")})</option>}
            </select>
          </label>
          <label className="status-filter">
            <span>Storage</span>
            <select aria-label="Storage state" value={storageFilter} onChange={(event) => setStorageFilter(event.target.value as ReviewStorageFilter)}>
              <option value="all">All ({scope === "in-use" ? inUse.rules : rules.length})</option>
              {REVIEW_STORAGE_STATES.map((state) => <option key={state} value={state}>{titleCase(state)} ({storageCounts[state]})</option>)}
            </select>
          </label>
        </div>
      </div>
      {loading ? <LoadingPanel /> : (
        <div className="admin-list">
          {rules.length === 0 && <p className="muted">No draft shelf-life rules are waiting for a decision in this department.</p>}
          {rules.length > 0 && filteredRules.length === 0 && (scope === "in-use" && inUse.rules === 0
            ? <p className="muted">Every draft for a product the kitchen uses has been decided. Choose <strong>Whole catalogue</strong> to carry on with the rest.</p>
            : <p className="muted">Nothing matches that search, type and storage state.</p>)}
          {filteredRules.slice(0, visibleCount).map((rule, index, shown) => {
            const productDraftCount = productDrafts.get(rule.product_id)?.length ?? 1;
            // Offered once per product, on its first row, and only when there is more than one rule to decide.
            const firstOfProduct = index === 0 || shown[index - 1].product_id !== rule.product_id;
            const busy = !canApprove || approving || actingOn === rule.id || actingOn === rule.product_id;
            return (
            <div className="admin-list-row" key={rule.id}>
              <label style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, minWidth: 0 }}>
                <input type="checkbox" disabled={!canApprove || approving} checked={selected.has(rule.id)} onChange={() => toggle(rule.id)} aria-label={`Select ${rule.product_name} for bulk approval`} />
                <div>
                  <strong>{rule.product_name}</strong>
                  <p>{titleCase(rule.storage_state)} · {durationLabel(rule.duration_value, rule.duration_unit)} · {rule.date_mark_type.replaceAll("_", " ")}</p>
                  {kitchenUseLabel(rule) && <p className="muted">{kitchenUseLabel(rule)}</p>}
                  {rule.generated_default && <p className="review-flag">{rule.storage_state === "ambient"
                    ? "Kitchen default for dry goods once opened: 90 days ambient. Edit it if this product is different."
                    : "Kitchen default. Edit it if this product keeps for a different time."}</p>}
                </div>
              </label>
              <div className="member-row-actions">
                {firstOfProduct && productDraftCount > 1 && (
                  <button className="row-action" type="button" disabled={busy} onClick={() => void approveProduct(rule)}>Approve product ({productDraftCount})</button>
                )}
                <button className="row-action" type="button" disabled={busy} onClick={() => void decide(rule, "approve")}>Approve</button>
                <button className="row-action" type="button" disabled={busy} onClick={() => setEditing(rule)}>Edit</button>
                <button className="row-action" type="button" disabled={busy} onClick={() => void decide(rule, "retire")}>Retire</button>
              </div>
            </div>
            );
          })}
          {filteredRules.length > 0 && (
            <div style={{ display: "flex", gap: "12px", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", paddingTop: "8px" }}>
              <p className="muted">Showing {Math.min(visibleCount, filteredRules.length)} of {filteredRules.length}{filteredRules.length !== rules.length ? ` matching (${rules.length} in total)` : ""}</p>
              {filteredRules.length > visibleCount && <button className="secondary-button" type="button" onClick={() => setVisibleCount((count) => count + REVIEW_PAGE_SIZE)}>Show {Math.min(REVIEW_PAGE_SIZE, filteredRules.length - visibleCount)} more</button>}
            </div>
          )}
        </div>
      )}
    </section>
    {editing && (
      <ShelfLifeRevisionEditor
        rule={editing}
        title={`${editing.product_name} · ${titleCase(editing.storage_state)}`}
        note="Saves and approves in one step: this draft is retired and a new rule is approved with these values. The date will be printed on real labels."
        saveLabel="Save & approve"
        onClose={() => setEditing(null)}
        onSave={saveEdit}
      />
    )}
  </div>;
}

function DashboardWorkspace({ departments, organizationId, api, canView, physicalPrintingEnabled, onError }: { departments: Department[]; organizationId: string; api?: KitchenHubApi; canView: boolean; physicalPrintingEnabled: boolean; onError: (message: string) => void }) {
  const siteId = useSelectedSite();
  const [snapshot, setSnapshot] = useState<DashboardSnapshot | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!api || !canView || !siteId) { setSnapshot(null); return; }
    let active = true;
    setLoading(true);
    void api.getDashboard(organizationId, siteId)
      .then((next) => { if (active) setSnapshot(next); })
      .catch((reason: unknown) => onError(messageFor(reason)))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [api, canView, organizationId, siteId, onError]);

  return <div className="team-layout">
    <section className="admin-card">
      <div className="admin-card-heading"><div><p className="section-label">Today at a glance</p><h2>Kitchen Hub Dashboard</h2></div><span className="safe-pill">{physicalPrintingEnabled ? "Real printing" : "Simulated only"}</span></div>
      {loading && <p className="muted">Loading…</p>}
      {snapshot && <>
        <div className="stat-row">
          <div className="stat-card"><strong>{snapshot.labels_today}</strong><span>Labels today</span></div>
          <div className="stat-card"><strong>{snapshot.active_products}</strong><span>Active products</span></div>
          <div className="stat-card"><strong>{snapshot.active_staff}</strong><span>Active staff</span></div>
        </div>
        <div className="admin-list">
          <h3>Printer status</h3>
          {snapshot.printers.length === 0 && <p className="muted">No printers discovered yet at this site.</p>}
          {snapshot.printers.map((printer) => <div className="admin-list-row" key={printer.id}>
            <div><strong>{printer.display_name || printer.name}</strong><p>{printer.bridge_status} · queue {printer.queue_availability}{!printer.active && " · deactivated"}</p></div>
          </div>)}
        </div>
      </>}
    </section>
    <aside className="team-controls">
      <section className="admin-card">
        <p className="section-label">Recent activity</p>
        <h3>Last 5 print jobs</h3>
        {snapshot?.recent_activity.length === 0 && <p className="muted">No print jobs yet at this site.</p>}
        {snapshot?.recent_activity.map((entry) => <div className="admin-list-row" key={entry.id}>
          <div><strong>{SOURCE_TYPE_LABELS[entry.source_type] ?? entry.source_type}</strong><p>{new Date(entry.created_at).toLocaleString()} · {entry.actor_display_name ?? "Unknown user"} · {historyStatusLabel(entry)}</p></div>
        </div>)}
      </section>
      <section className="safety-card"><div><Icon name="shield" /><span>Physical safety lock</span></div>{physicalPrintingEnabled
        ? <><strong>Physical printing on</strong><p>Real labels are printing at this site. If one doesn't come out, report it from Print History.</p></>
        : <><strong>Disabled</strong><p>All figures reflect simulated shadow print jobs. Nothing has physically printed.</p></>}</section>
    </aside>
  </div>;
}

function SystemInfoWorkspace({ departments, organizationId, api, canView, physicalPrintingEnabled, onError }: { departments: Department[]; organizationId: string; api?: KitchenHubApi; canView: boolean; physicalPrintingEnabled: boolean; onError: (message: string) => void }) {
  const siteId = useSelectedSite();
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [printerSummary, setPrinterSummary] = useState<PrinterSummary | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!api || !canView) return;
    setLoading(true);
    onError("");
    try {
      const [nextHealth, nextSummary] = await Promise.all([
        api.getHealth(),
        siteId ? api.getPrinterSummary(organizationId, siteId) : Promise.resolve(null),
      ]);
      setHealth(nextHealth);
      setPrinterSummary(nextSummary);
    } catch (reason) {
      onError(messageFor(reason));
    } finally {
      setLoading(false);
    }
  }, [api, canView, organizationId, siteId, onError]);

  useEffect(() => { void refresh(); }, [refresh]);

  return <div className="team-layout">
    <section className="admin-card">
      <div className="admin-card-heading"><div><p className="section-label">Support diagnostics</p><h2>System Info</h2></div><button className="secondary-button" type="button" disabled={loading} onClick={() => void refresh()}>{loading ? "Refreshing…" : "Refresh"}</button></div>
      <div className="stat-row">
        <div className="stat-card"><strong>{health?.version ?? "—"}</strong><span>App version</span></div>
        <div className="stat-card"><strong>{health?.environment ?? "—"}</strong><span>Environment</span></div>
        <div className="stat-card"><strong>{health?.database ?? "—"}</strong><span>Database</span></div>
      </div>
      {printerSummary && <p className="muted">{printerSummary.online} of {printerSummary.total} printer{printerSummary.total === 1 ? "" : "s"} online at this site.</p>}
    </section>
    <aside className="team-controls">
      <section className="safety-card"><div><Icon name="shield" /><span>Physical safety lock</span></div>{physicalPrintingEnabled
        ? <><strong>Physical printing on</strong><p>This screen is for troubleshooting connectivity. Real labels print from the print screens.</p></>
        : <><strong>Disabled</strong><p>This screen is for troubleshooting connectivity only. Nothing prints physically in this environment.</p></>}</section>
    </aside>
  </div>;
}

function OperationalPrintWorkspace({ mode, departments, organizationId, api, canPrint, canResetRanking, physicalPrintingEnabled, onError, onToast }: { mode: "quick" | "batch"; departments: Department[]; organizationId: string; api?: KitchenHubApi; canPrint: boolean; canResetRanking: boolean; physicalPrintingEnabled: boolean; onError: (message: string) => void; onToast: (message: string) => void }) {
  // Only the departments of the site chosen for this session -- printing for
  // another hotel means changing site first, not picking its department here.
  const selectedSiteId = useSelectedSite();
  const activeDepartments = useMemo(
    () => departments.filter((item) => item.active && (!selectedSiteId || item.site_id === selectedSiteId)),
    [departments, selectedSiteId],
  );
  const multiSite = new Set(activeDepartments.map((item) => item.site_id)).size > 1;
  const [departmentId, setDepartmentId] = useState("");
  const selectedDepartment = activeDepartments.find((item) => item.id === departmentId);
  const siteId = selectedSiteId || (selectedDepartment?.site_id ?? activeDepartments[0]?.site_id ?? "");
  const [options, setOptions] = useState<PrintOptions | null>(null);
  const [items, setItems] = useState<PrintProduct[]>([]);
  const [printerId, setPrinterId] = useState("");
  const [search, setSearch] = useState("");
  const [labelType, setLabelType] = useState<StandardLabelType>("use_by");
  const [storageState, setStorageState] = useState<StorageState>("chilled");
  const [preparedOn, setPreparedOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [saving, setSaving] = useState(false);
  const [datePreviews, setDatePreviews] = useState<Record<string, ProductDatePreview>>({});

  useEffect(() => {
    // Drop a department left over from another site, then pick this site's first for a batch.
    if (departmentId && !activeDepartments.some((item) => item.id === departmentId)) {
      setDepartmentId(""); setQuantities({}); return;
    }
    if (mode === "batch" && !departmentId && activeDepartments[0]) setDepartmentId(activeDepartments[0].id);
  }, [mode, departmentId, activeDepartments]);

  useEffect(() => {
    if (!api || !canPrint || !siteId) return;
    let active = true;
    void Promise.all([
      api.printOptions(organizationId, siteId),
      api.printProducts(organizationId, siteId, departmentId || undefined),
    ]).then(([nextOptions, nextItems]) => {
      if (!active) return;
      setOptions(nextOptions); setItems(nextItems);
      setPrinterId((current) => nextOptions.printers.some((item) => item.id === current) ? current : nextOptions.printers[0]?.id ?? "");
      if (mode === "batch") setQuantities(Object.fromEntries(nextItems.map((item) => [item.id, 1])));
    }).catch((reason: unknown) => active && onError(messageFor(reason)));
    return () => { active = false; };
  }, [api, canPrint, departmentId, mode, organizationId, siteId]);

  useEffect(() => {
    if (mode !== "batch" || !api || !siteId || !departmentId || !preparedOn) {
      setDatePreviews({}); return;
    }
    let active = true;
    const timer = window.setTimeout(() => {
      void api.batchDatePreviews(
        organizationId, siteId, departmentId, labelType, storageState, preparedOn,
      ).then((previews) => {
        if (active) setDatePreviews(Object.fromEntries(previews.map((item) => [item.product_id, item])));
      }).catch((reason: unknown) => active && onError(messageFor(reason)));
    }, 180);
    return () => { active = false; window.clearTimeout(timer); };
  }, [api, departmentId, labelType, mode, onError, organizationId, preparedOn, siteId, storageState]);

  const visible = items.filter((item) => item.name.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase())).slice(0, mode === "quick" ? 50 : undefined);
  const selected = items.filter((item) => (quantities[item.id] ?? 0) > 0);
  const selectedPrinter = options?.printers.find((item) => item.id === printerId);
  const labelCount = selected.reduce((sum, item) => sum + (quantities[item.id] ?? 0), 0);

  function quantity(productId: string, next: number) {
    setQuantities((current) => ({ ...current, [productId]: Math.max(0, Math.min(100, next)) }));
  }

  async function createJob(event: FormEvent) {
    event.preventDefault();
    if (!api || !siteId || !printerId || !selected.length || !selectedPrinter?.available) return;
    setSaving(true);
    try {
      const result = await api.createOperationalPrintJob(organizationId, mode, {
        site_id: siteId, printer_id: printerId, prepared_on: preparedOn,
        idempotency_key: `${mode}-${crypto.randomUUID()}`,
        site_department_id: departmentId || null,
        selections: selected.map((item) => ({
          product_id: item.id, quantity: quantities[item.id], label_type: labelType,
          storage_state: storageState,
        })),
      });
      setQuantities({});
      onToast(printResultToast(result));
      setItems(await api.printProducts(organizationId, siteId, departmentId || undefined));
    } catch (reason) { onError(messageFor(reason)); } finally { setSaving(false); }
  }

  async function resetRanking() {
    if (!api || !siteId || !canResetRanking || !window.confirm("Reset Quick Print usage ranking for this site? Print-job history is preserved.")) return;
    setSaving(true);
    try {
      const result = await api.resetQuickPrintRanking(organizationId, siteId);
      setItems(await api.printProducts(organizationId, siteId, departmentId || undefined));
      onToast(`Quick Print ranking reset · ${result.affected_rows} state rows · history preserved`);
    } catch (reason) { onError(messageFor(reason)); } finally { setSaving(false); }
  }

  return <form className="print-workspace" onSubmit={createJob}><section className="admin-card print-products"><div className="admin-card-heading"><div><p className="section-label">{mode === "quick" ? "Most used first" : "Department batch"}</p><h2>{mode === "quick" ? "Fast product selection" : `${selectedDepartment?.name ?? "Choose department"} products`}</h2></div><div className="heading-actions"><span className="safe-pill">{physicalPrintingEnabled ? "Real printing" : "Simulated only"}</span>{mode === "quick" && canResetRanking && <button type="button" className="secondary-button compact-button" disabled={saving} onClick={() => void resetRanking()}>Reset ranking</button>}</div></div><div className="print-filters"><label className="search-box"><Icon name="search" /><input aria-label="Search printable products" placeholder="Search products…" value={search} onChange={(event) => setSearch(event.target.value)} /></label><select aria-label="Department filter" value={departmentId} onChange={(event) => setDepartmentId(event.target.value)}><option value="">{mode === "quick" ? "All departments" : "Choose department"}</option>{activeDepartments.map((item) => <option key={item.id} value={item.id}>{multiSite ? `${item.site_name} · ${item.name}` : item.name}</option>)}</select></div>{mode === "batch" && <div className="batch-actions"><button type="button" className="secondary-button" onClick={() => setQuantities(Object.fromEntries(items.map((item) => [item.id, 1])))}>Select all · quantity 1</button><button type="button" className="secondary-button" onClick={() => setQuantities({})}>Clear selection</button></div>}<div className="print-product-grid">{visible.map((item) => { const count = quantities[item.id] ?? 0; const preview = datePreviews[item.id]; return <article key={item.id} className={count ? "selected" : ""}><button type="button" className="print-product-name" onClick={() => quantity(item.id, count || 1)}><strong>{item.name}</strong><small>{mode === "batch" ? preview?.date_mark_on ? `${preview.date_mark_label} · ${new Date(`${preview.date_mark_on}T12:00:00`).toLocaleDateString("en-GB")}${preview.warning ? ` · ${preview.warning}` : ""}` : preview?.warning ?? "Calculating date…" : item.pinned_rank ? "Pinned" : item.score ? `${item.score} recent uses` : "Available"}</small><small className="allergen-line">{allergenSummary(item.contains_allergens)}</small></button><div className="quantity-control"><button type="button" aria-label={`Decrease ${item.name}`} onClick={() => quantity(item.id, count - 1)}>−</button><input aria-label={`${item.name} quantity`} inputMode="numeric" value={count} onChange={(event) => quantity(item.id, Number(event.target.value) || 0)} /><button type="button" aria-label={`Increase ${item.name}`} onClick={() => quantity(item.id, count + 1)}>+</button></div></article>; })}{!visible.length && <div className="empty-detail"><strong>No printable products found</strong><p>Change the department or search.</p></div>}</div></section><aside className="print-controls"><section className="admin-card"><p className="section-label">Label setup</p><h3>Server-calculated dates</h3><label>Label type<select value={labelType} onChange={(event) => setLabelType(event.target.value as StandardLabelType)}>{options?.label_types.map((item) => <option key={item} value={item}>{item.replace("_", " ")}</option>)}</select></label><label>Storage state<select value={storageState} onChange={(event) => setStorageState(event.target.value as StorageState)}>{options?.storage_states.map((item) => <option key={item} value={item}>{item}</option>)}</select></label><label>Prepared date<input type="date" required value={preparedOn} onChange={(event) => setPreparedOn(event.target.value)} /></label><label>Mapped printer<select required value={printerId} onChange={(event) => setPrinterId(event.target.value)}><option value="">Choose printer</option>{options?.printers.map((item) => <option key={item.id} value={item.id}>{item.name} · {item.available ? "ready" : item.queue_availability}</option>)}</select></label>{selectedPrinter && !selectedPrinter.available && <div className="inline-warning"><strong>Printer unavailable</strong><span>{selectedPrinter.bridge_status} bridge · {selectedPrinter.queue_availability} queue. No job can be created.</span></div>}<div className="print-summary"><span>{selected.length} products</span><strong>{labelCount} labels</strong></div><button className="primary-button" disabled={saving || !selected.length || !selectedPrinter?.available}>{saving ? "Creating…" : physicalPrintingEnabled ? "Print" : "Create simulated job"}</button><p className="muted">{physicalPrintingEnabled ? "Labels go straight to the printer. If one doesn't come out, tap Didn't print on that job in Print History, then Reprint." : "Real printing is off here, so this records a simulated job, and only once the printer reports ready."}</p></section></aside></form>;
}

function PrepListsWorkspace({ departments, organizationId, api, canPrint, canManage, physicalPrintingEnabled, onError, onToast }: { departments: Department[]; organizationId: string; api?: KitchenHubApi; canPrint: boolean; canManage: boolean; physicalPrintingEnabled: boolean; onError: (message: string) => void; onToast: (message: string) => void }) {
  const selectedSiteId = useSelectedSite();
  const activeDepartments = useMemo(
    () => departments.filter((item) => item.active && (!selectedSiteId || item.site_id === selectedSiteId)),
    [departments, selectedSiteId],
  );
  const multiSite = new Set(activeDepartments.map((item) => item.site_id)).size > 1;
  // Use the site you are signed in to. Taking the first department's site
  // showed another hotel's data whenever that department sorted first.
  const siteId = selectedSiteId || (activeDepartments[0]?.site_id ?? "");
  const [prepLists, setPrepLists] = useState<PrepList[]>([]);
  const [selected, setSelected] = useState<PrepList | null>(null);
  const [eligibleProducts, setEligibleProducts] = useState<PrepListEligibleProduct[]>([]);
  const [options, setOptions] = useState<PrintOptions | null>(null);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [baseCovers, setBaseCovers] = useState(50);
  const [active, setActive] = useState(true);

  const [itemProductId, setItemProductId] = useState("");
  const [itemStorageState, setItemStorageState] = useState<StorageState>("chilled");
  const [itemPortionsPerCover, setItemPortionsPerCover] = useState(1);
  const [itemTrayCapacity, setItemTrayCapacity] = useState(30);
  const [itemSortOrder, setItemSortOrder] = useState(10);
  const [editingProductId, setEditingProductId] = useState<string | null>(null);

  const [actualCovers, setActualCovers] = useState(50);
  const [preparedOn, setPreparedOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [printerId, setPrinterId] = useState("");
  const [preview, setPreview] = useState<PrepListPrintPreview | null>(null);
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set());

  const refresh = useCallback(async () => {
    if (!api || !siteId) return;
    setPrepLists(await api.listPrepLists(organizationId, siteId));
  }, [api, organizationId, siteId]);

  useEffect(() => { if (canPrint) void refresh().catch((reason: unknown) => onError(messageFor(reason))); }, [canPrint, refresh, onError]);

  useEffect(() => {
    if (!api || !siteId) return;
    void api.printOptions(organizationId, siteId).then((next) => {
      setOptions(next);
      setPrinterId((current) => next.printers.some((item) => item.id === current) ? current : next.printers[0]?.id ?? "");
    }).catch((reason: unknown) => onError(messageFor(reason)));
  }, [api, organizationId, siteId, onError]);

  useEffect(() => {
    if (!api || !siteId) return;
    void api.prepListEligibleProducts(organizationId, siteId, selected?.site_department_id ?? undefined)
      .then(setEligibleProducts).catch((reason: unknown) => onError(messageFor(reason)));
  }, [api, organizationId, siteId, selected?.site_department_id, onError]);

  useEffect(() => {
    setPreview(null);
    setSelectedProductIds(new Set(selected?.items.map((item) => item.product_id) ?? []));
    setActualCovers(selected?.base_covers ?? 50);
  }, [selected?.id]);

  function startNew() {
    setSelected(null); setName(""); setDepartmentId(""); setBaseCovers(50); setActive(true);
  }

  async function startEdit(prepList: PrepList) {
    setSelected(prepList); setName(prepList.name);
    setDepartmentId(prepList.site_department_id ?? ""); setBaseCovers(prepList.base_covers);
    setActive(prepList.active);
    // The listing endpoint returns every prep list with items: [] -- only the
    // detail endpoint carries them. Without this refetch the Items panel read
    // "No items yet" for any list whose items were not added in this session.
    if (!api) return;
    try {
      setSelected(await api.getPrepList(organizationId, prepList.id));
    } catch (reason: unknown) { onError(messageFor(reason)); }
  }

  async function savePrepList(event: FormEvent) {
    event.preventDefault();
    if (!api || !siteId || !canManage) return;
    setSaving(true);
    try {
      const departmentValue = departmentId || null;
      if (selected) {
        const updated = await api.updatePrepList(organizationId, selected.id, {
          expected_version: selected.version, name, site_department_id: departmentValue,
          base_covers: baseCovers, active,
        });
        setSelected(updated);
        onToast(`"${updated.name}" updated`);
      } else {
        const created = await api.createPrepList(organizationId, {
          site_id: siteId, name, site_department_id: departmentValue, base_covers: baseCovers,
        });
        setSelected(created);
        onToast(`"${created.name}" created`);
      }
      await refresh();
    } catch (reason) { onError(messageFor(reason)); } finally { setSaving(false); }
  }

  async function deletePrepList(prepList: PrepList) {
    if (!api || !canManage || !window.confirm(`Delete prep list "${prepList.name}"? This will also delete all items inside it. This cannot be undone.`)) return;
    setSaving(true);
    try {
      await api.deletePrepList(organizationId, prepList.id);
      if (selected?.id === prepList.id) startNew();
      await refresh();
      onToast(`"${prepList.name}" deleted`);
    } catch (reason) { onError(messageFor(reason)); } finally { setSaving(false); }
  }

  function startAddItem() {
    setEditingProductId(null); setItemProductId(""); setItemStorageState("chilled");
    setItemPortionsPerCover(1); setItemTrayCapacity(30);
    setItemSortOrder((selected?.items.length ?? 0) * 10 + 10);
  }

  function startEditItem(item: PrepList["items"][number]) {
    setEditingProductId(item.product_id); setItemProductId(item.product_id);
    setItemStorageState(item.storage_state); setItemPortionsPerCover(item.portions_per_cover);
    setItemTrayCapacity(item.tray_capacity); setItemSortOrder(item.sort_order);
  }

  async function saveItem(event: FormEvent) {
    event.preventDefault();
    if (!api || !selected || !canManage || !itemProductId) return;
    setSaving(true);
    try {
      const payload = {
        product_id: itemProductId, storage_state: itemStorageState,
        portions_per_cover: itemPortionsPerCover, tray_capacity: itemTrayCapacity,
        sort_order: itemSortOrder,
      };
      if (editingProductId) {
        await api.updatePrepListItem(organizationId, selected.id, editingProductId, payload);
      } else {
        await api.addPrepListItem(organizationId, selected.id, payload);
      }
      setSelected(await api.getPrepList(organizationId, selected.id));
      startAddItem();
    } catch (reason) { onError(messageFor(reason)); } finally { setSaving(false); }
  }

  async function removeItem(productId: string) {
    if (!api || !selected || !canManage) return;
    setSaving(true);
    try {
      await api.removePrepListItem(organizationId, selected.id, productId);
      setSelected(await api.getPrepList(organizationId, selected.id));
    } catch (reason) { onError(messageFor(reason)); } finally { setSaving(false); }
  }

  async function loadPreview() {
    if (!api || !selected) return;
    try {
      setPreview(await api.previewPrepListPrint(organizationId, selected.id, actualCovers, preparedOn));
    } catch (reason) { onError(messageFor(reason)); }
  }

  async function printSelected() {
    if (!api || !selected || !siteId || !printerId || !selectedProductIds.size) return;
    setSaving(true);
    try {
      const result = await api.printPrepList(organizationId, selected.id, {
        site_id: siteId, printer_id: printerId, idempotency_key: `prep-list-${crypto.randomUUID()}`,
        prepared_on: preparedOn, actual_covers: actualCovers,
        selected_product_ids: Array.from(selectedProductIds),
      });
      onToast(printResultToast(result));
      setPreview(null);
    } catch (reason) { onError(messageFor(reason)); } finally { setSaving(false); }
  }

  function toggleSelected(productId: string) {
    setSelectedProductIds((current) => {
      const next = new Set(current);
      if (next.has(productId)) next.delete(productId); else next.add(productId);
      return next;
    });
  }

  const sortedItems = useMemo(
    () => [...(selected?.items ?? [])].sort((a, b) => a.sort_order - b.sort_order || a.product_id.localeCompare(b.product_id)),
    [selected],
  );

  return <div className="team-layout"><section className="admin-card">
    <div className="admin-card-heading"><div><p className="section-label">Kitchen prep</p><h2>Prep lists</h2></div>{canManage && <button type="button" className="secondary-button compact-button" onClick={startNew}>New prep list</button>}</div>
    <div className="admin-list">
      {prepLists.map((item) => <div className={`admin-list-row${selected?.id === item.id ? " selected" : ""}`} key={item.id}>
        <button type="button" className="print-product-name" onClick={() => void startEdit(item)}><strong>{item.name}</strong><small>{item.active ? "Active" : "Inactive"} · base covers {item.base_covers}</small></button>
        {canManage && <button type="button" className="danger-button" disabled={saving} onClick={() => void deletePrepList(item)}>Delete</button>}
      </div>)}
      {!prepLists.length && <div className="empty-detail"><strong>No prep lists yet</strong><p>Create one to get started.</p></div>}
    </div>
  </section>

  <aside className="team-controls">
    {canManage && <section className="admin-card">
      <p className="section-label">{selected ? "Edit prep list" : "New prep list"}</p>
      <form onSubmit={savePrepList}>
        <label>Name<input required value={name} onChange={(event) => setName(event.target.value)} /></label>
        <label>Department<select value={departmentId} onChange={(event) => setDepartmentId(event.target.value)}><option value="">All departments</option>{activeDepartments.map((item) => <option key={item.id} value={item.id}>{multiSite ? `${item.site_name} · ${item.name}` : item.name}</option>)}</select></label>
        <label>Base covers<input type="number" min={1} required value={baseCovers} onChange={(event) => setBaseCovers(Number(event.target.value) || 1)} /></label>
        {selected && <label className="checkbox-label"><input type="checkbox" checked={active} onChange={(event) => setActive(event.target.checked)} /> Active</label>}
        <button className="primary-button" disabled={saving || !name.trim()}>{selected ? "Save changes" : "Create prep list"}</button>
      </form>
    </section>}

    {selected && <section className="admin-card">
      <p className="section-label">Items</p>
      <div className="admin-list">
        {sortedItems.map((item) => <div className="admin-list-row" key={item.product_id}>
          <div><strong>{eligibleProducts.find((product) => product.id === item.product_id)?.name ?? item.product_id}</strong>
            <small>{item.storage_state} · {item.portions_per_cover} portions/cover · tray {item.tray_capacity} · sort {item.sort_order}</small></div>
          {canManage && <div className="quantity-control"><button type="button" className="secondary-button compact-button" onClick={() => startEditItem(item)}>Edit</button><button type="button" className="danger-button" disabled={saving} onClick={() => void removeItem(item.product_id)}>Remove</button></div>}
        </div>)}
        {!sortedItems.length && <div className="empty-detail"><strong>No items yet</strong></div>}
      </div>
      {canManage && <form onSubmit={saveItem} className="print-filters">
        <select required value={itemProductId} disabled={!!editingProductId} onChange={(event) => setItemProductId(event.target.value)}>
          <option value="">Choose product</option>
          {eligibleProducts.map((product) => <option key={product.id} value={product.id}>{product.name} — {allergenSummary(product.contains_allergens)}</option>)}
        </select>
        <select value={itemStorageState} onChange={(event) => setItemStorageState(event.target.value as StorageState)}>
          {(["chilled", "frozen", "ambient", "defrosted"] as StorageState[]).map((state) => <option key={state} value={state}>{state}</option>)}
        </select>
        <label>Portions/cover<input type="number" min={0.01} step={0.01} required value={itemPortionsPerCover} onChange={(event) => setItemPortionsPerCover(Number(event.target.value) || 1)} /></label>
        <label>Tray capacity<input type="number" min={1} required value={itemTrayCapacity} onChange={(event) => setItemTrayCapacity(Number(event.target.value) || 1)} /></label>
        <label>Sort<input type="number" value={itemSortOrder} onChange={(event) => setItemSortOrder(Number(event.target.value) || 10)} /></label>
        <button className="primary-button" disabled={saving || !itemProductId}>{editingProductId ? "Save item" : "Add item"}</button>
        {editingProductId && <button type="button" className="secondary-button" onClick={startAddItem}>Cancel edit</button>}
      </form>}
    </section>}

    {selected && canPrint && <section className="admin-card">
      <p className="section-label">Print prep list</p>
      <label>Actual covers<input type="number" min={1} value={actualCovers} onChange={(event) => setActualCovers(Number(event.target.value) || 1)} /></label>
      <label>Prepared date<input type="date" value={preparedOn} onChange={(event) => setPreparedOn(event.target.value)} /></label>
      <label>Mapped printer<select required value={printerId} onChange={(event) => setPrinterId(event.target.value)}><option value="">Choose printer</option>{options?.printers.map((item) => <option key={item.id} value={item.id}>{item.name} · {item.available ? "ready" : item.queue_availability}</option>)}</select></label>
      <button type="button" className="secondary-button" onClick={() => void loadPreview()}>Calculate preview</button>
      {preview && <div className="admin-list">
        {preview.items.map((item) => <div className="admin-list-row" key={item.product_id}>
          <label className="checkbox-label"><input type="checkbox" disabled={!!item.warning} checked={selectedProductIds.has(item.product_id)} onChange={() => toggleSelected(item.product_id)} />
            <div><strong>{item.product_name ?? item.product_id}</strong>
              <small>{item.warning ? item.warning : `prep ${item.prep_quantity} · labels ${item.label_quantity}${item.date_mark_on ? ` · ${item.date_mark_label} ${item.date_mark_on}` : ""}`}</small></div>
          </label>
        </div>)}
        <div className="print-summary"><span>{preview.total_prep_quantity} total prep</span><strong>{preview.total_label_quantity} total labels</strong></div>
      </div>}
      <button className="primary-button" disabled={saving || !printerId || !selectedProductIds.size} onClick={() => void printSelected()}>{saving ? "Creating…" : physicalPrintingEnabled ? "Print selected" : "Create simulated job"}</button>
      <p className="muted">{physicalPrintingEnabled ? "Labels go straight to the printer." : "Real printing is off here, so this records a simulated job."} Items with a warning (missing or inactive product) are left out automatically.</p>
    </section>}
  </aside></div>;
}

function BuffetWorkspace({ departments, organizationId, api, canPrint, canManage, physicalPrintingEnabled, onError, onToast }: { departments: Department[]; organizationId: string; api?: KitchenHubApi; canPrint: boolean; canManage: boolean; physicalPrintingEnabled: boolean; onError: (message: string) => void; onToast: (message: string) => void }) {
  const siteId = useSelectedSite();
  const [menus, setMenus] = useState<BuffetMenu[]>([]);
  const [selected, setSelected] = useState<BuffetMenu | null>(null);
  const [eligibleProducts, setEligibleProducts] = useState<BuffetEligibleProduct[]>([]);
  const [options, setOptions] = useState<PrintOptions | null>(null);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState("");
  const [active, setActive] = useState(true);
  const [newItemProductId, setNewItemProductId] = useState("");

  const [printerId, setPrinterId] = useState("");
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [preview, setPreview] = useState<BuffetLabelPreview | null>(null);
  const [previewingProductId, setPreviewingProductId] = useState<string | null>(null);

  const productName = useCallback(
    (productId: string) => eligibleProducts.find((item) => item.id === productId)?.name ?? productId,
    [eligibleProducts],
  );

  const refresh = useCallback(async () => {
    if (!api || !canPrint || !siteId) return;
    setMenus(await api.listBuffetMenus(organizationId, siteId));
  }, [api, canPrint, organizationId, siteId]);

  useEffect(() => { void refresh().catch((reason: unknown) => onError(messageFor(reason))); }, [refresh, onError]);

  useEffect(() => {
    if (!api || !canManage) return;
    void api.listBuffetEligibleProducts(organizationId).then(setEligibleProducts)
      .catch((reason: unknown) => onError(messageFor(reason)));
  }, [api, canManage, organizationId, onError]);

  useEffect(() => {
    if (!api || !siteId) return;
    void api.printOptions(organizationId, siteId).then((next) => {
      setOptions(next);
      setPrinterId((current) => next.printers.some((item) => item.id === current) ? current : next.printers[0]?.id ?? "");
    }).catch((reason: unknown) => onError(messageFor(reason)));
  }, [api, organizationId, siteId, onError]);

  useEffect(() => {
    setPreview(null); setPreviewingProductId(null);
    setQuantities(Object.fromEntries((selected?.items ?? []).map((item) => [item.product_id, 1])));
  }, [selected?.id]);

  function startNew() { setSelected(null); setName(""); setActive(true); }

  function startEdit(menu: BuffetMenu) { setSelected(menu); setName(menu.name); setActive(menu.active); }

  async function saveMenu(event: FormEvent) {
    event.preventDefault();
    if (!api || !siteId || !canManage) return;
    setSaving(true);
    try {
      if (selected) {
        const updated = await api.updateBuffetMenu(organizationId, selected.id, {
          expected_version: selected.version, name, active,
        });
        setSelected(updated);
        onToast(`"${updated.name}" updated`);
      } else {
        const created = await api.createBuffetMenu(organizationId, { site_id: siteId, name });
        setSelected(created);
        onToast(`"${created.name}" created`);
      }
      await refresh();
    } catch (reason) { onError(messageFor(reason)); } finally { setSaving(false); }
  }

  async function deleteMenu(menu: BuffetMenu) {
    if (!api || !canManage || !window.confirm(`Delete buffet menu "${menu.name}"? This will also delete all its items. This cannot be undone.`)) return;
    setSaving(true);
    try {
      await api.deleteBuffetMenu(organizationId, menu.id);
      if (selected?.id === menu.id) startNew();
      await refresh();
      onToast(`"${menu.name}" deleted`);
    } catch (reason) { onError(messageFor(reason)); } finally { setSaving(false); }
  }

  async function addItem(event: FormEvent) {
    event.preventDefault();
    if (!api || !selected || !canManage || !newItemProductId) return;
    setSaving(true);
    try {
      await api.addBuffetMenuItem(organizationId, selected.id, {
        product_id: newItemProductId, sort_order: (selected.items.length + 1) * 10,
      });
      setSelected(await api.getBuffetMenu(organizationId, selected.id));
      setNewItemProductId("");
    } catch (reason) { onError(messageFor(reason)); } finally { setSaving(false); }
  }

  async function removeItem(productId: string) {
    if (!api || !selected || !canManage) return;
    setSaving(true);
    try {
      await api.removeBuffetMenuItem(organizationId, selected.id, productId);
      setSelected(await api.getBuffetMenu(organizationId, selected.id));
    } catch (reason) { onError(messageFor(reason)); } finally { setSaving(false); }
  }

  async function previewItem(productId: string) {
    if (!api || !selected) return;
    try {
      setPreview(await api.previewBuffetItem(organizationId, selected.id, productId));
      setPreviewingProductId(productId);
    } catch (reason) { onError(messageFor(reason)); }
  }

  async function printItems() {
    if (!api || !selected || !siteId || !printerId) return;
    const selections = selected.items
      .map((item) => ({ product_id: item.product_id, quantity: quantities[item.product_id] ?? 0 }))
      .filter((item) => item.quantity > 0);
    if (!selections.length) return;
    setSaving(true);
    try {
      const result = await api.printBuffetMenuItems(organizationId, selected.id, {
        site_id: siteId, printer_id: printerId,
        idempotency_key: `buffet-${crypto.randomUUID()}`, selections,
      });
      onToast(printResultToast(result));
    } catch (reason) { onError(messageFor(reason)); } finally { setSaving(false); }
  }

  const availableProducts = eligibleProducts.filter(
    (product) => !selected?.items.some((item) => item.product_id === product.id),
  );
  const previewUrl = preview ? `data:image/svg+xml;charset=utf-8,${encodeURIComponent(preview.svg)}` : null;

  return <div className="catalogue-layout">
    <section className="detail-list">
      <div className="detail-intro detail-action-row">
        <div><p className="section-label">Buffet menus</p><p>Ordered product lists for buffet-style label printing. Allergens are always read live from the product.</p></div>
        {canManage && <button className="secondary-button compact-button" onClick={startNew}><Icon name="plus" size={15} /> New menu</button>}
      </div>
      {menus.map((menu) => <article key={menu.id} className={selected?.id === menu.id ? "active-row" : undefined} onClick={() => startEdit(menu)}>
        <div><strong>{menu.name}</strong><span>{menu.items.length} item{menu.items.length === 1 ? "" : "s"}{!menu.active && " · inactive"}</span></div>
        {canManage && <button className="danger-button" type="button" onClick={(event) => { event.stopPropagation(); void deleteMenu(menu); }}>Delete</button>}
      </article>)}
      {menus.length === 0 && <div className="empty-detail"><Icon name="label" /><strong>No buffet menus</strong><p>Create one to start printing buffet labels.</p></div>}
    </section>
    <section className="detail-panel">
      {canManage && <form onSubmit={saveMenu}>
        <h2>{selected ? `Edit "${selected.name}"` : "New buffet menu"}</h2>
        <label>Name<input required maxLength={200} value={name} onChange={(event) => setName(event.target.value)} /></label>
        {selected && <label className="checkbox-row"><input type="checkbox" checked={active} onChange={(event) => setActive(event.target.checked)} /> Active</label>}
        <button className="primary-button" disabled={saving || !name.trim()}>{saving ? "Saving…" : selected ? "Save changes" : "Create menu"}</button>
      </form>}
      {selected && <>
        <div className="admin-list">
          <h3>Items</h3>
          {selected.items.map((item) => <div className="admin-list-row" key={item.product_id}>
            <div><strong>{productName(item.product_id)}</strong></div>
            <div>
              <button className="secondary-button" type="button" onClick={() => void previewItem(item.product_id)}>Preview</button>
              {canManage && <button className="danger-button" type="button" onClick={() => void removeItem(item.product_id)}>Remove</button>}
            </div>
          </div>)}
          {selected.items.length === 0 && <p className="muted">No items yet.</p>}
        </div>
        {canManage && <form onSubmit={addItem}>
          <label>Add product<select required value={newItemProductId} onChange={(event) => setNewItemProductId(event.target.value)}><option value="">Choose product</option>{availableProducts.map((product) => <option key={product.id} value={product.id}>{product.name} — {allergenSummary(product.contains_allergens)}</option>)}</select></label>
          <button className="secondary-button" disabled={saving || !newItemProductId}>Add to menu</button>
        </form>}
        {previewUrl && <div className="label-paper"><img alt={`${preview!.item_name} buffet label preview`} src={previewUrl} style={{ maxWidth: "100%" }} />{preview!.warnings.map((warning) => <p key={warning} className="inline-warning">{warning}</p>)}</div>}
        {canPrint && selected.items.length > 0 && <section className="admin-card">
          <p className="section-label">Print buffet labels</p>
          <label>Printer<select value={printerId} onChange={(event) => setPrinterId(event.target.value)}><option value="">Choose printer</option>{options?.printers.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
          {selected.items.map((item) => <label key={item.product_id}>{productName(item.product_id)}<input type="number" min={0} max={100} value={quantities[item.product_id] ?? 0} onChange={(event) => setQuantities((current) => ({ ...current, [item.product_id]: Number(event.target.value) }))} /></label>)}
          <button className="primary-button" type="button" disabled={saving || !printerId} onClick={() => void printItems()}>{saving ? "Printing…" : physicalPrintingEnabled ? "Print selected" : "Create simulated job"}</button>
        </section>}
      </>}
    </section>
  </div>;
}

function PpdsLabelsWorkspace({ departments, references, organizationId, api, canPrint, canManage, physicalPrintingEnabled, onError, onToast }: { departments: Department[]; references: ReferenceData | null; organizationId: string; api?: KitchenHubApi; canPrint: boolean; canManage: boolean; physicalPrintingEnabled: boolean; onError: (message: string) => void; onToast: (message: string) => void }) {
  const selectedSiteId = useSelectedSite();
  const activeDepartments = useMemo(
    () => departments.filter((item) => item.active && (!selectedSiteId || item.site_id === selectedSiteId)),
    [departments, selectedSiteId],
  );
  const multiSite = new Set(activeDepartments.map((item) => item.site_id)).size > 1;
  // Use the site you are signed in to. Taking the first department's site
  // showed another hotel's data whenever that department sorted first.
  const siteId = selectedSiteId || (activeDepartments[0]?.site_id ?? "");
  const allAllergens = references?.allergens ?? [];
  const leafAllergens = useMemo(
    () => allAllergens.filter((item) => !allAllergens.some((other) => other.parent_id === item.id)),
    [allAllergens],
  );
  const [templates, setTemplates] = useState<PpdsLabelTemplate[]>([]);
  const [selected, setSelected] = useState<PpdsLabelTemplate | null>(null);
  const [options, setOptions] = useState<PrintOptions | null>(null);
  const [saving, setSaving] = useState(false);

  const [foodName, setFoodName] = useState("");
  const [labelMode, setLabelMode] = useState<PpdsLabelMode>("ppds");
  const [ingredients, setIngredients] = useState("");
  const [allergenIds, setAllergenIds] = useState<Set<string>>(new Set());
  const [netQuantity, setNetQuantity] = useState("");
  const [dateMarkType, setDateMarkType] = useState<PpdsDateMarkType>("none");
  const [shelfLifeValue, setShelfLifeValue] = useState(1);
  const [shelfLifeUnit, setShelfLifeUnit] = useState<PpdsShelfLifeUnit>("days");
  const [storageInstructions, setStorageInstructions] = useState("");
  const [reheatingInstructions, setReheatingInstructions] = useState("");
  const [additionalWarnings, setAdditionalWarnings] = useState("");
  const [active, setActive] = useState(true);

  const [businessName, setBusinessName] = useState("");
  const [businessAddress, setBusinessAddress] = useState("");
  const [businessVersion, setBusinessVersion] = useState(1);

  const [printerId, setPrinterId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [preview, setPreview] = useState<PpdsLabelPreview | null>(null);

  const refresh = useCallback(async () => {
    if (!api || !siteId) return;
    setTemplates(await api.listPpdsTemplates(organizationId, siteId));
  }, [api, organizationId, siteId]);

  useEffect(() => { if (canPrint) void refresh().catch((reason: unknown) => onError(messageFor(reason))); }, [canPrint, refresh, onError]);

  useEffect(() => {
    if (!api || !siteId) return;
    void api.printOptions(organizationId, siteId).then((next) => {
      setOptions(next);
      setPrinterId((current) => next.printers.some((item) => item.id === current) ? current : next.printers[0]?.id ?? "");
    }).catch((reason: unknown) => onError(messageFor(reason)));
  }, [api, organizationId, siteId, onError]);

  function startNew() {
    setSelected(null); setFoodName(""); setLabelMode("ppds"); setIngredients("");
    setAllergenIds(new Set()); setNetQuantity(""); setDateMarkType("none");
    setShelfLifeValue(1); setShelfLifeUnit("days"); setStorageInstructions("");
    setReheatingInstructions(""); setAdditionalWarnings(""); setActive(true); setPreview(null);
  }

  function startEdit(template: PpdsLabelTemplate) {
    setSelected(template); setFoodName(template.food_name); setLabelMode(template.label_mode);
    setIngredients(template.ingredients); setAllergenIds(new Set(template.allergen_ids));
    setNetQuantity(template.net_quantity ?? ""); setDateMarkType(template.date_mark_type);
    setShelfLifeValue(template.shelf_life_value ?? 1);
    setShelfLifeUnit(template.shelf_life_unit ?? "days");
    setStorageInstructions(template.storage_instructions ?? "");
    setReheatingInstructions(template.reheating_instructions ?? "");
    setAdditionalWarnings(template.additional_warnings ?? ""); setActive(template.active);
    setPreview(null);
  }

  function toggleAllergen(id: string) {
    setAllergenIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function saveTemplate(event: FormEvent) {
    event.preventDefault();
    if (!api || !siteId || !canManage) return;
    setSaving(true);
    try {
      const payload = {
        food_name: foodName, label_mode: labelMode, ingredients,
        allergen_ids: Array.from(allergenIds), net_quantity: netQuantity || null,
        date_mark_type: dateMarkType,
        shelf_life_value: dateMarkType === "none" ? null : shelfLifeValue,
        shelf_life_unit: dateMarkType === "none" ? null : shelfLifeUnit,
        storage_instructions: storageInstructions || null,
        reheating_instructions: reheatingInstructions || null,
        additional_warnings: additionalWarnings || null,
      };
      if (selected) {
        const updated = await api.updatePpdsTemplate(organizationId, selected.id, {
          ...payload, expected_version: selected.version, active,
        });
        setSelected(updated);
        onToast(`"${updated.food_name}" updated`);
      } else {
        const created = await api.createPpdsTemplate(organizationId, { ...payload, site_id: siteId });
        setSelected(created);
        onToast(`"${created.food_name}" created`);
      }
      await refresh();
    } catch (reason) { onError(messageFor(reason)); } finally { setSaving(false); }
  }

  async function deleteTemplate(template: PpdsLabelTemplate) {
    if (!api || !canManage || !window.confirm(`Delete PPDS/takeaway template "${template.food_name}"? This cannot be undone.`)) return;
    setSaving(true);
    try {
      await api.deletePpdsTemplate(organizationId, template.id);
      if (selected?.id === template.id) startNew();
      await refresh();
      onToast(`"${template.food_name}" deleted`);
    } catch (reason) { onError(messageFor(reason)); } finally { setSaving(false); }
  }

  async function saveBusinessIdentity(event: FormEvent) {
    event.preventDefault();
    if (!api || !siteId || !canManage) return;
    setSaving(true);
    try {
      await api.setSiteFoodBusinessIdentity(organizationId, siteId, {
        expected_version: businessVersion,
        food_business_name: businessName || null, food_business_address: businessAddress || null,
      });
      setBusinessVersion((v) => v + 1);
      onToast("Food business identity saved");
    } catch (reason) { onError(messageFor(reason)); } finally { setSaving(false); }
  }

  async function loadPreview() {
    if (!api || !selected) return;
    try {
      setPreview(await api.previewPpdsTemplate(organizationId, selected.id, new Date().toISOString()));
    } catch (reason) { onError(messageFor(reason)); }
  }

  async function printTemplate() {
    if (!api || !selected || !siteId || !printerId) return;
    setSaving(true);
    try {
      const result = await api.printPpdsTemplate(organizationId, selected.id, {
        site_id: siteId, printer_id: printerId, idempotency_key: `ppds-${crypto.randomUUID()}`,
        quantity, prepared_at: new Date().toISOString(),
      });
      onToast(printResultToast(result));
    } catch (reason) { onError(messageFor(reason)); } finally { setSaving(false); }
  }

  return <div className="team-layout"><section className="admin-card">
    <div className="admin-card-heading"><div><p className="section-label">Regulated packaged food</p><h2>PPDS / Takeaway labels</h2></div>{canManage && <button type="button" className="secondary-button compact-button" onClick={startNew}>New template</button>}</div>
    <div className="admin-list">
      {templates.map((item) => <div className={`admin-list-row${selected?.id === item.id ? " selected" : ""}`} key={item.id}>
        <button type="button" className="print-product-name" onClick={() => startEdit(item)}><strong>{item.food_name}</strong><small>{item.label_mode === "ppds" ? "PPDS" : "Prepared to order"} · {item.active ? "Active" : "Inactive"}</small></button>
        {canManage && <button type="button" className="danger-button" disabled={saving} onClick={() => void deleteTemplate(item)}>Delete</button>}
      </div>)}
      {!templates.length && <div className="empty-detail"><strong>No PPDS/takeaway templates yet</strong></div>}
    </div>
  </section>

  <aside className="team-controls">
    {canManage && <section className="admin-card">
      <p className="section-label">Site food business identity</p>
      <p className="muted">Required before any PPDS preview or print will work for this site.</p>
      <form onSubmit={saveBusinessIdentity}>
        <label>Food business name<input value={businessName} onChange={(event) => setBusinessName(event.target.value)} /></label>
        <label>Full physical postal address<textarea value={businessAddress} onChange={(event) => setBusinessAddress(event.target.value)} /></label>
        <button className="primary-button" disabled={saving}>Save identity</button>
      </form>
    </section>}

    {canManage && <section className="admin-card">
      <p className="section-label">{selected ? "Edit template" : "New template"}</p>
      <form onSubmit={saveTemplate}>
        <label>Food name<input required value={foodName} onChange={(event) => setFoodName(event.target.value)} /></label>
        <label>Mode<select value={labelMode} onChange={(event) => setLabelMode(event.target.value as PpdsLabelMode)}><option value="ppds">PPDS</option><option value="prepared_to_order">Prepared To Order</option></select></label>
        <label>Full ingredients{labelMode === "ppds" ? " * (descending weight order, allergens in UPPERCASE)" : ""}<textarea required={labelMode === "ppds"} value={ingredients} onChange={(event) => setIngredients(event.target.value)} /></label>
        <fieldset><legend>Allergens (leaf types only)</legend>
          {leafAllergens.map((item) => <label key={item.id} className="checkbox-label"><input type="checkbox" checked={allergenIds.has(item.id)} onChange={() => toggleAllergen(item.id)} /> {item.name}</label>)}
        </fieldset>
        <label>Net quantity<input placeholder="e.g. 250g" value={netQuantity} onChange={(event) => setNetQuantity(event.target.value)} /></label>
        <label>Date mark<select value={dateMarkType} onChange={(event) => setDateMarkType(event.target.value as PpdsDateMarkType)}><option value="none">None</option><option value="use_by">Use By</option><option value="best_before">Best Before</option></select></label>
        {dateMarkType !== "none" && <><label>Shelf life value<input type="number" min={1} value={shelfLifeValue} onChange={(event) => setShelfLifeValue(Number(event.target.value) || 1)} /></label>
        <label>Shelf life unit<select value={shelfLifeUnit} onChange={(event) => setShelfLifeUnit(event.target.value as PpdsShelfLifeUnit)}><option value="hours">Hours</option><option value="days">Days</option></select></label></>}
        <label>Storage instructions<textarea value={storageInstructions} onChange={(event) => setStorageInstructions(event.target.value)} /></label>
        <label>Use / reheating instructions<textarea value={reheatingInstructions} onChange={(event) => setReheatingInstructions(event.target.value)} /></label>
        <label>Additional warnings<textarea value={additionalWarnings} onChange={(event) => setAdditionalWarnings(event.target.value)} /></label>
        {selected && <label className="checkbox-label"><input type="checkbox" checked={active} onChange={(event) => setActive(event.target.checked)} /> Active</label>}
        <button className="primary-button" disabled={saving || !foodName.trim()}>{selected ? "Save changes" : "Create template"}</button>
      </form>
    </section>}

    {selected && canPrint && <section className="admin-card">
      <p className="section-label">Preview &amp; print</p>
      <button type="button" className="secondary-button" onClick={() => void loadPreview()}>Calculate preview</button>
      {preview && <div className="label-paper"><img alt={`${preview.food_name} PPDS label preview`} src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(preview.svg)}`} style={{ maxWidth: "100%" }} />
        {preview.warnings.length > 0 && <ul>{preview.warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul>}
      </div>}
      <label>Mapped printer<select required value={printerId} onChange={(event) => setPrinterId(event.target.value)}><option value="">Choose printer</option>{options?.printers.map((item) => <option key={item.id} value={item.id}>{item.name} · {item.available ? "ready" : item.queue_availability}</option>)}</select></label>
      <label>Quantity<input type="number" min={1} max={100} value={quantity} onChange={(event) => setQuantity(Number(event.target.value) || 1)} /></label>
      <button className="primary-button" disabled={saving || !printerId} onClick={() => void printTemplate()}>{saving ? "Creating…" : physicalPrintingEnabled ? "Print" : "Create simulated job"}</button>
      <p className="muted">{physicalPrintingEnabled ? "Labels go straight to the printer. The date comes from this template, not Shelf-life review, so check it before printing." : "Real printing is off here, so this records a simulated job."} Preview and printing stay blocked until this site's food business identity is saved.</p>
    </section>}
  </aside></div>;
}

function DepartmentAdministration({ departments, setDepartments, organizationId, api, demoMode, canManage, canManageSites, onError, onToast }: { departments: Department[]; setDepartments: Dispatch<SetStateAction<Department[]>>; organizationId: string; api?: KitchenHubApi; demoMode: boolean; canManage: boolean; canManageSites: boolean; onError: (message: string) => void; onToast: (message: string) => void }) {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"active" | "inactive" | "all">("active");
  const [editing, setEditing] = useState<Department | null>(null);
  const [name, setName] = useState("");
  const [sortOrder, setSortOrder] = useState("0");
  const [siteRows, setSiteRows] = useState<SiteSettings[]>([]);
  const [newSiteName, setNewSiteName] = useState("");
  const [siteTimezone, setSiteTimezone] = useState("Europe/London");
  const [savingSite, setSavingSite] = useState(false);
  const [editingSiteId, setEditingSiteId] = useState<string | null>(null);
  const [editSiteName, setEditSiteName] = useState("");
  const [editSiteCode, setEditSiteCode] = useState("");
  const [savingSiteEdit, setSavingSiteEdit] = useState(false);
  const [siteId, setSiteId] = useState(departments[0]?.site_id ?? "");
  const [saving, setSaving] = useState(false);
  const [attachSiteId, setAttachSiteId] = useState("");
  const [attachDepartmentId, setAttachDepartmentId] = useState("");
  const [attaching, setAttaching] = useState(false);
  const siteMap = new Map<string, string>(departments.map((item) => [item.site_id, item.site_name]));
  for (const site of siteRows) siteMap.set(site.id, site.name);
  const sites: [string, string][] = [...siteMap.entries()];

  useEffect(() => {
    if (demoMode || !api || !canManageSites) return;
    void api.listSites(organizationId).then(setSiteRows).catch((reason: unknown) => onError(messageFor(reason)));
  }, [api, demoMode, canManageSites, organizationId, onError]);

  // Departments are organization-level and unique by code, so a second hotel
  // cannot create its own "Kitchen" - it joins the one that already exists.
  const organizationDepartments = [
    ...new Map(departments.map((item) => [item.department_id, item.name])).entries(),
  ].sort((left, right) => left[1].localeCompare(right[1]));
  // Switched off at every site it's linked to: adding it switches it on for
  // the chosen site only.
  const switchedOffDepartments = new Set(
    organizationDepartments
      .map(([id]) => id)
      .filter((id) => !departments.some((item) => item.department_id === id && item.active)),
  );
  const alreadyLinked = new Set(
    departments
      .filter((item) => item.active)
      .map((item) => `${item.site_id}:${item.department_id}`),
  );
  const attachable = attachSiteId
    ? organizationDepartments.filter(
        ([departmentId]) => !alreadyLinked.has(`${attachSiteId}:${departmentId}`),
      )
    : organizationDepartments;

  async function attach(event: FormEvent) {
    event.preventDefault();
    if (!attachSiteId || !attachDepartmentId) return;
    setAttaching(true);
    try {
      if (demoMode) {
        const source = departments.find((item) => item.department_id === attachDepartmentId)!;
        setDepartments((current) => [
          ...current,
          {
            ...source,
            id: `preview-link-${crypto.randomUUID()}`,
            site_id: attachSiteId,
            site_name: sites.find(([id]) => id === attachSiteId)?.[1] ?? "",
            product_count: 0,
            active: true,
          },
        ]);
      } else {
        await api!.attachDepartment(organizationId, attachSiteId, attachDepartmentId);
        setDepartments(await api!.departments(organizationId));
      }
      setAttachDepartmentId("");
      onToast("Department added to the site");
    } catch (reason) {
      onError(messageFor(reason));
    } finally {
      setAttaching(false);
    }
  }

  async function saveSite(event: FormEvent) {
    event.preventDefault();
    if (!newSiteName.trim()) return;
    setSavingSite(true);
    try {
      const code = newSiteName.trim().toLocaleLowerCase().normalize("NFKD").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 64) || "site";
      const created = demoMode
        ? { id: `preview-site-${crypto.randomUUID()}`, organization_id: organizationId, name: newSiteName.trim(), code, timezone: siteTimezone, active: true, version: 1, food_business_name: null, food_business_address: null, default_printer_id: null, label_width_mm: 62, label_height_mm: 100 }
        : await api!.createSite(organizationId, { name: newSiteName.trim(), code, timezone: siteTimezone });
      setSiteRows((current) => [...current, created].sort((left, right) => left.name.localeCompare(right.name)));
      setNewSiteName("");
      onToast(`Site "${created.name}" created — add its departments below`);
    } catch (reason) { onError(messageFor(reason)); } finally { setSavingSite(false); }
  }

  function beginEditSite(site: SiteSettings) {
    setEditingSiteId(site.id); setEditSiteName(site.name); setEditSiteCode(site.code);
  }

  async function saveSiteEdit(event: FormEvent) {
    event.preventDefault();
    const site = siteRows.find((item) => item.id === editingSiteId);
    if (!site || !editSiteName.trim() || !editSiteCode.trim()) return;
    setSavingSiteEdit(true);
    try {
      const updated = demoMode
        ? { ...site, name: editSiteName.trim(), code: editSiteCode.trim(), version: site.version + 1 }
        : await api!.updateSiteDetails(organizationId, site.id, {
            name: editSiteName.trim(), code: editSiteCode.trim(), expected_version: site.version,
          });
      setSiteRows((current) => current.map((item) => item.id === updated.id ? updated : item)
        .sort((left, right) => left.name.localeCompare(right.name)));
      setEditingSiteId(null);
      onToast(`Site renamed to "${updated.name}"`);
    } catch (reason) { onError(messageFor(reason)); } finally { setSavingSiteEdit(false); }
  }

  const visible = departments.filter((item) => {
    const matchesSearch = item.name.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase());
    const matchesStatus = status === "all" || (status === "active" ? item.active : !item.active);
    return matchesSearch && matchesStatus;
  }).sort((left, right) => left.sort_order - right.sort_order || left.name.localeCompare(right.name));

  function beginEdit(department: Department) {
    setEditing(department); setName(department.name); setSortOrder(String(department.sort_order)); setSiteId(department.site_id);
  }

  function clearEditor() { setEditing(null); setName(""); setSortOrder("0"); }

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!siteId || !name.trim()) return;
    setSaving(true);
    try {
      if (editing) {
        const updated = demoMode
          ? { ...editing, name: name.trim(), sort_order: Number(sortOrder), version: editing.version + 1 }
          : await api!.updateDepartment(organizationId, editing, { name: name.trim(), sort_order: Number(sortOrder), active: editing.active });
        setDepartments((current) => current.map((item) => item.department_id === updated.department_id ? { ...item, ...updated } : item));
        onToast("Department updated · product links preserved by stable ID");
      } else {
        const code = name.trim().toLocaleLowerCase().normalize("NFKD").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 64) || "department";
        const created = demoMode
          ? { id: `preview-site-dept-${crypto.randomUUID()}`, organization_id: organizationId, site_id: siteId, site_name: sites.find(([id]) => id === siteId)?.[1] ?? "Site", department_id: `preview-dept-${crypto.randomUUID()}`, name: name.trim(), code, sort_order: Number(sortOrder), active: true, version: 1, product_count: 0 }
          : await api!.createDepartment(organizationId, { site_id: siteId, name: name.trim(), code, sort_order: Number(sortOrder) });
        setDepartments((current) => [...current.filter((item) => item.id !== created.id), created]);
        onToast(`${created.name} added to ${created.site_name}`);
      }
      clearEditor();
    } catch (reason) { onError(messageFor(reason)); } finally { setSaving(false); }
  }

  // Showing or hiding a department is per site: the same department can be
  // on at one hotel and off at another, so this keys on the site row (item.id),
  // not the department.
  async function toggle(department: Department) {
    setSaving(true);
    const active = !department.active;
    try {
      if (!demoMode) await api!.setDepartmentVisibleAtSite(organizationId, department, active);
      setDepartments((current) => current.map((item) => item.id === department.id ? { ...item, active } : item));
      onToast(`${department.name} ${active ? "switched on" : "switched off"} at ${department.site_name}`);
    } catch (reason) { onError(messageFor(reason)); } finally { setSaving(false); }
  }

  async function remove(department: Department) {
    if (department.product_count > 0 || !window.confirm(`Permanently delete ${department.name}? This action is audited.`)) return;
    setSaving(true);
    try {
      if (!demoMode) await api!.deleteDepartment(organizationId, department);
      setDepartments((current) => current.filter((item) => item.department_id !== department.department_id));
      clearEditor(); onToast("Unused department deleted");
    } catch (reason) { onError(messageFor(reason)); } finally { setSaving(false); }
  }

  return <div className="team-layout"><section className="admin-card"><div className="admin-card-heading"><div><p className="section-label">Department directory</p><h2>{visible.length} departments</h2></div><span className="safe-pill">Stable ID links</span></div><div className="catalogue-toolbar"><label className="search-box"><Icon name="search" /><input aria-label="Search departments" placeholder="Search departments…" value={search} onChange={(event) => setSearch(event.target.value)} /></label><select aria-label="Department status" value={status} onChange={(event) => setStatus(event.target.value as typeof status)}><option value="active">Active only</option><option value="inactive">Inactive only</option><option value="all">All statuses</option></select></div><div className="department-table">{visible.map((department) => <article key={department.id}><div><strong>{department.name}</strong><span>{department.site_name} · order {department.sort_order} · {department.product_count} product{department.product_count === 1 ? "" : "s"}</span></div><span className={`member-state ${department.active ? "active" : ""}`}>{department.active ? "Active" : "Inactive"}</span><button className="row-action" disabled={!canManage || saving} onClick={() => beginEdit(department)}>Edit</button><button className="row-action" disabled={!canManage || saving} onClick={() => void toggle(department)}>{department.active ? "Switch off here" : "Switch on here"}</button><button className="danger-button compact-button" disabled={!canManage || saving || department.product_count > 0} title={department.product_count > 0 ? "Products still reference this department" : "Delete unused department"} onClick={() => void remove(department)}>Delete</button></article>)}{!visible.length && <div className="empty-detail"><Icon name="search" /><strong>No departments found</strong><p>Change the search or status filter.</p></div>}</div></section><aside className="team-controls">{canManageSites && <section className="admin-card"><p className="section-label">Sites</p><h3>{sites.length} site{sites.length === 1 ? "" : "s"}</h3><ul className="plain-list">{sites.map(([id, name]) => { const row = siteRows.find((item) => item.id === id); return <li key={id}>{editingSiteId === id ? <form onSubmit={saveSiteEdit} className="inline-edit-form"><input required maxLength={200} value={editSiteName} onChange={(event) => setEditSiteName(event.target.value)} /><input required maxLength={64} pattern="[a-z0-9][a-z0-9_-]*" value={editSiteCode} onChange={(event) => setEditSiteCode(event.target.value.toLocaleLowerCase())} /><button className="row-action" disabled={savingSiteEdit}>{savingSiteEdit ? "Saving…" : "Save"}</button><button type="button" className="row-action" onClick={() => setEditingSiteId(null)}>Cancel</button></form> : <><span>{name}</span>{row && <button className="row-action" onClick={() => beginEditSite(row)}>Rename</button>}</>}</li>; })}</ul><form onSubmit={saveSite}><label>New site name<input required maxLength={200} placeholder="e.g. Manor Hotel" value={newSiteName} onChange={(event) => setNewSiteName(event.target.value)} /></label><label>Timezone<input required value={siteTimezone} onChange={(event) => setSiteTimezone(event.target.value)} /></label><button className="primary-button" disabled={savingSite || !newSiteName.trim()}>{savingSite ? "Creating…" : "Create site"}</button></form><p className="muted">A new site has no departments yet — add its first department below once it's created.</p></section>}{canManage && <section className="admin-card"><p className="section-label">Existing department</p><h3>Add one to another site</h3><form onSubmit={(event) => void attach(event)}><label>Site<select required value={attachSiteId} onChange={(event) => { setAttachSiteId(event.target.value); setAttachDepartmentId(""); }}><option value="">Choose site</option>{sites.map(([id, siteName]) => <option key={id} value={id}>{siteName}</option>)}</select></label><label>Department<select required value={attachDepartmentId} onChange={(event) => setAttachDepartmentId(event.target.value)}><option value="">Choose department</option>{attachable.map(([id, departmentName]) => <option key={id} value={id}>{departmentName}{switchedOffDepartments.has(id) ? " (switched off — on here only)" : ""}</option>)}</select></label><button className="primary-button" disabled={attaching || !attachSiteId || !attachDepartmentId}>{attaching ? "Adding…" : "Add to site"}</button></form><p className="muted">{attachSiteId && !attachable.length ? "This site already has every department." : "Department names are unique across the organisation, so a second hotel joins the existing department rather than creating another with the same name."}</p></section>}<section className="admin-card"><p className="section-label">{editing ? "Edit department" : "Create department"}</p><h3>{editing ? "Rename, order or status" : "Add to a site"}</h3><form onSubmit={save}><label>Site<select disabled={Boolean(editing)} required value={siteId} onChange={(event) => setSiteId(event.target.value)}><option value="">Choose site</option>{sites.map(([id, siteName]) => <option key={id} value={id}>{siteName}</option>)}</select></label><label>Department name<input required maxLength={100} pattern="[^,]+" value={name} onChange={(event) => setName(event.target.value)} /></label><label>Sort order<input type="number" required value={sortOrder} onChange={(event) => setSortOrder(event.target.value)} /></label><button className="primary-button" disabled={!canManage || saving || !name.trim() || !siteId}>{saving ? "Saving…" : editing ? "Save department" : "Create department"}</button>{editing && <button type="button" className="secondary-button" onClick={clearEditor}>Cancel edit</button>}</form><p className="muted">Names are unique without regard to case. Renames never rewrite product rows.</p></section></aside></div>;
}

function SharedDeviceLock({ members, displayName, email, onResume, onUnlock, onCancel, demoMode }: { members: { id: string; display_name: string }[]; displayName: string | null; email: string; onResume: (password: string) => Promise<void>; onUnlock: (memberId: string, pin: string) => Promise<void>; onCancel?: () => void; demoMode: boolean }) {
  const [memberId, setMemberId] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [password, setPassword] = useState("");
  const [resumeError, setResumeError] = useState<string | null>(null);
  const [resuming, setResuming] = useState(false);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true); setError(null);
    try { await onUnlock(memberId, pin); setPin(""); }
    catch (reason) { setError(messageFor(reason)); setPin(""); }
    finally { setSubmitting(false); }
  }
  async function submitResume(event: FormEvent) {
    event.preventDefault();
    setResuming(true); setResumeError(null);
    try { await onResume(password); setPassword(""); }
    catch (reason) { setResumeError(messageFor(reason)); setPassword(""); }
    finally { setResuming(false); }
  }
  return <main className="gate-screen lock-screen"><section className="gate-card login-card"><Mark /><p className="eyebrow">Shared device locked</p><h2>Welcome back{displayName ? `, ${displayName}` : ""}</h2><p className="muted">Enter your password to resume exactly where you left off, still signed in as {email}.</p><form onSubmit={submitResume}><PasswordField id="resume-password" label="Password" autoFocus autoComplete="current-password" value={password} onChange={setPassword} />{resumeError && <p className="form-error">{resumeError}</p>}<button className="primary-button wide" disabled={resuming || !password}>{resuming ? "Resuming…" : "Resume my session"}</button></form><div className="lock-divider"><span>or someone else is using this kitchen</span></div><p className="muted">Tap your name and enter your 4–6 digit PIN. This creates a short-lived AAL1 operator session for printing and browsing — editing products, allergens, shelf-life and admin settings stays locked until that person signs out and signs back in with their full account.</p><form onSubmit={submit}><label htmlFor="switch-member">Staff member<select id="switch-member" required value={memberId} onChange={(event) => setMemberId(event.target.value)}><option value="">Choose your name</option>{members.map((member) => <option key={member.id} value={member.id}>{member.display_name}</option>)}</select></label><PasswordField id="switch-pin" label="Staff PIN" inputMode="numeric" autoComplete="off" pattern="[0-9]{4,6}" minLength={4} maxLength={6} value={pin} onChange={(value) => setPin(value.replace(/\D/g, "").slice(0, 6))} />{error && <p className="form-error">{error}</p>}<button className="primary-button wide" disabled={submitting || !memberId || pin.length < 4}>{submitting ? "Switching…" : "Unlock & switch"}</button></form>{demoMode && <p className="login-note">Preview PINs: Alex 1001 · Sam 1002 · Jamie 1003</p>}{onCancel && <button className="secondary-button wide" onClick={onCancel}>Sign out instead</button>}</section></main>;
}

function generateSignInPassword(): string {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
  const bytes = crypto.getRandomValues(new Uint32Array(12));
  return Array.from(bytes, (value) => alphabet[value % alphabet.length]).join("");
}

function RolePermissionTable({ roles }: { roles: ManagedRole[] }) {
  const columns = rolesForTable(roles);
  const unlisted = unlistedPermissions(columns);
  if (!columns.length) return null;
  return (
    <section className="role-permission-table">
      <p className="section-label">What each role can do</p>
      <p className="muted">A tick means the role can do it at the sites it's assigned to. <span className="mfa-tag">MFA</span> means it only works after signing in with email and authenticator — never with a PIN.</p>
      <div className="role-permission-scroll">
        <table>
          <thead>
            <tr><th scope="col">Action</th>{columns.map((role) => <th key={role.id} scope="col">{role.name}</th>)}</tr>
          </thead>
          <tbody>
            {PERMISSION_GROUPS.map((group) => [
              <tr key={group.heading} className="group-row"><th scope="rowgroup" colSpan={columns.length + 1}>{group.heading}</th></tr>,
              ...group.rows.map((row) => (
                <tr key={row.code}>
                  <th scope="row">{row.label}{row.needsMfa && <span className="mfa-tag">MFA</span>}</th>
                  {columns.map((role) => {
                    const allowed = role.permissions.includes(row.code);
                    return <td key={role.id} className={allowed ? "yes" : "no"} aria-label={`${role.name}: ${allowed ? "yes" : "no"}`}>{allowed ? "✓" : "—"}</td>;
                  })}
                </tr>
              )),
            ])}
          </tbody>
        </table>
      </div>
      {unlisted.length > 0 && <p className="muted">Also granted: {unlisted.join(", ")}</p>}
    </section>
  );
}

function TeamAdministration({ profile, organizationId, api, demoMode, sites, onError, onToast }: { profile: SessionInfo; organizationId: string; api?: KitchenHubApi; demoMode: boolean; sites: [string, string][]; onError: (message: string) => void; onToast: (message: string) => void }) {
  const [data, setData] = useState<IdentityAdministration | null>(demoMode ? structuredClone(demoIdentityAdministration) : null);
  const [roleName, setRoleName] = useState("");
  const [protectedLevel, setProtectedLevel] = useState("20");
  const [permissionCodes, setPermissionCodes] = useState<string[]>([]);
  const [memberId, setMemberId] = useState("");
  const [roleId, setRoleId] = useState("");
  const [assignSiteIds, setAssignSiteIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [newMember, setNewMember] = useState<{ display_name: string; employee_reference: string; member_type: "named" | "pin_only"; pin: string }>({ display_name: "", employee_reference: "", member_type: "pin_only", pin: "" });
  const [memberSearch, setMemberSearch] = useState("");
  const [editingMemberId, setEditingMemberId] = useState("");
  const [editMemberName, setEditMemberName] = useState("");
  const [editMemberReference, setEditMemberReference] = useState("");
  const [editMemberActive, setEditMemberActive] = useState(true);
  const [editMemberPin, setEditMemberPin] = useState("");
  const [editMemberPinError, setEditMemberPinError] = useState<string | null>(null);
  const [editingRoleId, setEditingRoleId] = useState("");
  const [editingRoleName, setEditingRoleName] = useState("");
  const [editingRoleActive, setEditingRoleActive] = useState(true);
  const [editingPermissions, setEditingPermissions] = useState<string[]>([]);
  const [orgCode, setOrgCode] = useState<string | null>(demoMode ? "demo-kitchen" : null);
  const [linkSites, setLinkSites] = useState<SiteSettings[]>([]);
  const [signingOutDeviceFor, setSigningOutDeviceFor] = useState("");
  const [linkingAuthFor, setLinkingAuthFor] = useState("");
  const [linkAuthEmail, setLinkAuthEmail] = useState("");
  const [linkAuthPassword, setLinkAuthPassword] = useState("");
  const [linkingAuthSaving, setLinkingAuthSaving] = useState(false);
  const [createdCredential, setCreatedCredential] = useState<{ memberId: string; memberName: string; email: string; password: string; reset?: boolean } | null>(null);
  const [resettingPasswordFor, setResettingPasswordFor] = useState("");

  useEffect(() => {
    if (demoMode || !api) return;
    void api.identityAdministration(organizationId).then(setData).catch((reason: unknown) => onError(messageFor(reason)));
    void api.getOrganizationSettings(organizationId).then((settings) => setOrgCode(settings.code)).catch(() => {});
    void api.listSites(organizationId).then(setLinkSites).catch(() => {});
  }, [api, demoMode, onError, organizationId]);

  async function signOutDevice(member: MemberRecord) {
    setSigningOutDeviceFor(member.id);
    try {
      if (!demoMode) await api!.signOutMemberDevice(organizationId, member.id);
      onToast(`${member.display_name}’s device was signed out — they’ll need their PIN again next time.`);
    } catch (reason) { onError(messageFor(reason)); } finally { setSigningOutDeviceFor(""); }
  }

  function beginLinkAuth(member: MemberRecord) {
    setLinkingAuthFor(member.id);
    setLinkAuthEmail("");
    setLinkAuthPassword(generateSignInPassword());
  }

  function cancelLinkAuth() {
    setLinkingAuthFor("");
    setLinkAuthEmail("");
    setLinkAuthPassword("");
  }

  async function linkAuth(event: FormEvent, member: MemberRecord) {
    event.preventDefault();
    const email = linkAuthEmail.trim();
    const password = linkAuthPassword.trim();
    if (!email || password.length < 8) return;
    setLinkingAuthSaving(true);
    try {
      // Creates the Supabase sign-in account directly with this password if
      // one doesn't already exist for the email -- no separate trip to the
      // Supabase dashboard needed first. If an account for that email
      // already existed, it's linked as-is and the password typed here is
      // never used -- account_created tells us which happened.
      const accountCreated = demoMode ? true : (await api!.linkMemberAuth(organizationId, member.id, email, password)).account_created;
      setData((current) => current ? { ...current, auth_linked_member_ids: [...current.auth_linked_member_ids, member.id] } : current);
      if (accountCreated) {
        setCreatedCredential({ memberId: member.id, memberName: member.display_name, email, password });
      } else {
        onToast(`${member.display_name} is now linked to the existing Supabase account for ${email} — the password you set here wasn't used, they already know that account's own password.`);
      }
      cancelLinkAuth();
    } catch (reason) { onError(messageFor(reason)); } finally { setLinkingAuthSaving(false); }
  }

  async function resetPassword(member: MemberRecord) {
    if (!window.confirm(`Reset ${member.display_name}'s password? They'll be signed out of Kitchen Hub everywhere straight away and will need the new password to sign back in.`)) return;
    // Generated here and shown once below -- the server never returns it.
    const password = generateSignInPassword();
    setResettingPasswordFor(member.id);
    try {
      const email = demoMode ? "chef@example.com" : (await api!.resetMemberPassword(organizationId, member.id, password)).email;
      setCreatedCredential({ memberId: member.id, memberName: member.display_name, email: email ?? "", password, reset: true });
    } catch (reason) { onError(messageFor(reason)); } finally { setResettingPasswordFor(""); }
  }

  async function copyCredential() {
    if (!createdCredential) return;
    try {
      await navigator.clipboard.writeText(`Email: ${createdCredential.email}\nPassword: ${createdCredential.password}`);
      onToast("Credentials copied to clipboard");
    } catch {
      onError("Could not copy automatically — select and copy the text shown.");
    }
  }

  async function copySignInLink(site?: SiteSettings) {
    if (!orgCode) return;
    const link = pinSignInLink(window.location.origin, orgCode, site?.code);
    try {
      await navigator.clipboard.writeText(link);
      onToast(site ? `${site.name} sign-in link copied — it only lists people who work there` : "Sign-in link copied — share it with your chefs");
    } catch {
      onError(`Could not copy automatically. The link is ${link}`);
    }
  }

  async function copyMemberSignInLink(member: MemberRecord, site: SiteSettings) {
    if (!orgCode) return;
    const link = pinSignInLink(window.location.origin, orgCode, site.code, member.id);
    try {
      await navigator.clipboard.writeText(link);
      onToast(`${member.display_name}'s ${site.name} link copied — it opens straight to their PIN`);
    } catch {
      onError(`Could not copy automatically. The link is ${link}`);
    }
  }

  if (!data) return <LoadingPanel />;
  const staffData = data;
  // The sites a member's personal PIN link can point at: every site for an
  // organisation-wide role, otherwise just the sites they hold a role at.
  function memberLinkSites(member: MemberRecord): SiteSettings[] {
    const assignments = staffData.assignments.filter((item) => item.member_id === member.id);
    if (assignments.some((item) => item.scope_type === "organization")) return linkSites;
    const siteIds = new Set(assignments.map((item) => item.site_id));
    return linkSites.filter((site) => siteIds.has(site.id));
  }
  const actorAssignments = data.assignments.filter((item) => item.member_id === profile.member_id);
  // Role management (creating/editing roles, and choosing what's assignable
  // at all) is an org-wide concern -- roles themselves aren't site-scoped --
  // so it only ever looks at the actor's org-wide assignments, mirroring the
  // server's highest_organization_role_level.
  const actorLevel = Math.max(0, ...actorAssignments.filter((item) => !item.site_id).map((item) => data.roles.find((role) => role.id === item.role_id)?.protected_level ?? 0));
  // A specific assignment's own hierarchy check, though, is scoped: an
  // org-wide grant always counts, and a site-scoped grant counts too but
  // only for that same site -- mirrors the server's _actor_hierarchy_level.
  function actorLevelAt(siteId: string | null): number {
    const relevant = actorAssignments.filter((item) => !item.site_id || item.site_id === siteId);
    return Math.max(0, ...relevant.map((item) => staffData.roles.find((role) => role.id === item.role_id)?.protected_level ?? 0));
  }
  // The role dropdown in the assign form is gated on the scope actually
  // picked, not the actor's org-wide level -- a site-scoped Head Chef with
  // no org-wide grant must still see roles they can assign at their own
  // site. When multiple sites are picked, a role only qualifies if it's
  // assignable at every one of them (the server checks each independently).
  const assignLevel = assignSiteIds.length ? Math.min(...assignSiteIds.map((id) => actorLevelAt(id))) : actorLevel;
  const manageableRoles = data.roles.filter((role) => role.active && role.protected_level < assignLevel);
  const delegablePermissions = data.permissions.filter((permission) => profile.permissions.includes(permission.code));
  const memberRoles = (targetMemberId: string) => data.assignments.filter((item) => item.member_id === targetMemberId).map((item) => data.roles.find((role) => role.id === item.role_id)).filter((role): role is NonNullable<typeof role> => Boolean(role));

  function replaceMember(member: MemberRecord) {
    setData((current) => current ? { ...current, members: current.members.map((item) => item.id === member.id ? member : item) } : current);
  }

  function replaceRole(role: ManagedRole) {
    setData((current) => current ? { ...current, roles: current.roles.map((item) => item.id === role.id ? role : item) } : current);
  }

  function togglePermission(code: string) {
    setPermissionCodes((current) => current.includes(code) ? current.filter((item) => item !== code) : [...current, code]);
  }

  async function createRole(event: FormEvent) {
    event.preventDefault();
    if (!roleName.trim() || !permissionCodes.length) return;
    setSaving(true);
    try {
      const input: ManagedRoleInput = { name: roleName.trim(), protected_level: Number(protectedLevel), permission_codes: permissionCodes };
      const role = demoMode
        ? { id: `preview-role-${crypto.randomUUID()}`, organization_id: organizationId, name: input.name, protected_level: input.protected_level, permissions: input.permission_codes, active: true, version: 1 }
        : await api!.createRole(organizationId, input);
      setData((current) => current ? { ...current, roles: [...current.roles, role].sort((left, right) => right.protected_level - left.protected_level) } : current);
      setRoleName("");
      setPermissionCodes([]);
      onToast("Role created");
    } catch (reason) { onError(messageFor(reason)); } finally { setSaving(false); }
  }

  async function assignRole(event: FormEvent) {
    event.preventDefault();
    const currentData = data;
    if (!currentData || !memberId || !roleId) return;
    // No sites picked means organisation-wide (one assignment, every site);
    // one or more sites picked means one assignment per site -- this is how
    // a member like Dave ends up with exactly "Head Chef @ Manor" and "Head
    // Chef @ Windmill", rather than everywhere or just one place.
    const siteIds: (string | null)[] = assignSiteIds.length ? assignSiteIds : [null];
    setSaving(true);
    const added: OrganizationRoleAssignment[] = [];
    let alreadyCount = 0;
    const failed: string[] = [];
    for (const siteId of siteIds) {
      // Match on scope + site too, not just member/role -- a member can hold
      // the same role at more than one site (or org-wide and at a site), and
      // those are distinct assignments, not duplicates of each other.
      const existing = currentData.assignments.find(
        (item) => item.member_id === memberId && item.role_id === roleId && item.site_id === siteId,
      );
      if (existing) { alreadyCount += 1; continue; }
      try {
        const assignment = demoMode
          ? {
              id: `preview-assignment-${crypto.randomUUID()}`, organization_id: organizationId, member_id: memberId,
              role_id: roleId, created_by: profile.member_id,
              scope_type: siteId ? ("site" as const) : ("organization" as const), site_id: siteId,
            }
          : await api!.assignOrganizationRole(organizationId, memberId, roleId, siteId);
        added.push(assignment);
      } catch (reason) {
        failed.push(siteId ? (sites.find(([id]) => id === siteId)?.[1] ?? siteId) : "organisation-wide");
        onError(messageFor(reason));
      }
    }
    if (added.length) setData((current) => current ? { ...current, assignments: [...current.assignments, ...added] } : current);
    if (!failed.length) {
      onToast(
        added.length
          ? `Role assigned${added.length > 1 ? ` at ${added.length} sites` : ""}${alreadyCount ? ` (already held at ${alreadyCount} site${alreadyCount === 1 ? "" : "s"})` : ""}`
          : "Role was already assigned"
      );
      setAssignSiteIds([]);
    }
    setSaving(false);
  }

  async function createMember(event: FormEvent) {
    event.preventDefault();
    if (!newMember.display_name.trim() || !/^[0-9]{4,6}$/.test(newMember.pin)) return;
    setSaving(true);
    try {
      const member = demoMode ? {
        id: `preview-member-${crypto.randomUUID()}`, organization_id: organizationId,
        display_name: newMember.display_name.trim(), employee_reference: newMember.employee_reference.trim() || null,
        member_type: newMember.member_type, active: true, version: 1, protected: false,
      } : await api!.createMember(organizationId, {
        ...newMember, display_name: newMember.display_name.trim(), employee_reference: newMember.employee_reference.trim() || null,
      });
      setData((current) => current ? { ...current, members: [...current.members, member].sort((a, b) => a.display_name.localeCompare(b.display_name)) } : current);
      setNewMember({ display_name: "", employee_reference: "", member_type: "pin_only", pin: "" });
      onToast("Member created with secure PIN credential");
    } catch (reason) { onError(messageFor(reason)); } finally { setSaving(false); }
  }

  function beginMemberEdit(member: MemberRecord) {
    setEditingMemberId(member.id);
    setEditMemberName(member.display_name);
    setEditMemberReference(member.employee_reference ?? "");
    setEditMemberActive(member.active);
    setEditMemberPin("");
    setEditMemberPinError(null);
  }

  function cancelMemberEdit() {
    setEditingMemberId("");
    setEditMemberPin("");
    setEditMemberPinError(null);
  }

  async function saveMemberEdit(event: FormEvent) {
    event.preventDefault();
    const member = staffData.members.find((item) => item.id === editingMemberId);
    if (!member || !editMemberName.trim()) return;
    if (editMemberPin && !/^[0-9]{4,6}$/.test(editMemberPin)) {
      setEditMemberPinError("PIN must contain 4 to 6 digits, or be left blank to keep the current PIN.");
      return;
    }
    setEditMemberPinError(null);
    setSaving(true);
    try {
      const updated = demoMode ? { ...member, display_name: editMemberName.trim(), employee_reference: editMemberReference.trim() || null, active: editMemberActive, version: member.version + 1 }
        : await api!.updateMember(organizationId, member.id, { display_name: editMemberName.trim(), employee_reference: editMemberReference.trim() || null, active: editMemberActive, expected_version: member.version, pin: editMemberPin || null });
      replaceMember(updated);
      onToast(editMemberPin ? "Member and PIN updated" : "Member updated · PIN unchanged");
      cancelMemberEdit();
    } catch (reason) { onError(messageFor(reason)); } finally { setSaving(false); }
  }

  async function toggleMember(member: MemberRecord) {
    if (!member.active && !window.confirm(`Reactivate ${member.display_name}?`)) return;
    if (member.active && !window.confirm(`Deactivate ${member.display_name}? Existing switch sessions will stop authorizing requests.`)) return;
    setSaving(true);
    try {
      const updated = demoMode ? { ...member, active: !member.active, version: member.version + 1 }
        : await api!.updateMember(organizationId, member.id, { display_name: member.display_name, employee_reference: member.employee_reference, active: !member.active, expected_version: member.version, pin: null });
      replaceMember(updated); onToast(updated.active ? "Member reactivated" : "Member deactivated");
    } catch (reason) { onError(messageFor(reason)); } finally { setSaving(false); }
  }

  function beginRoleEdit(role: ManagedRole) {
    setEditingRoleId(role.id); setEditingRoleName(role.name); setEditingRoleActive(role.active); setEditingPermissions([...role.permissions]);
  }

  async function saveRole(event: FormEvent) {
    event.preventDefault();
    const current = staffData.roles.find((role) => role.id === editingRoleId);
    if (!current) return;
    setSaving(true);
    try {
      const updated = demoMode ? { ...current, name: editingRoleName.trim(), active: editingRoleActive, permissions: editingPermissions, version: current.version + 1 }
        : await api!.updateRole(organizationId, current.id, { name: editingRoleName.trim(), active: editingRoleActive, permission_codes: editingPermissions, expected_version: current.version });
      replaceRole(updated); setEditingRoleId(""); onToast("Role and permissions updated");
    } catch (reason) { onError(messageFor(reason)); } finally { setSaving(false); }
  }

  async function resetPermissions(role: ManagedRole) {
    if (!window.confirm(`Reset ${role.name} to its recorded permission defaults?`)) return;
    setSaving(true);
    try {
      const defaults = demoIdentityAdministration.roles.find((item) => item.id === role.id)?.permissions ?? role.permissions;
      const updated = demoMode ? { ...role, permissions: [...defaults], version: role.version + 1 } : await api!.resetRolePermissions(organizationId, role);
      replaceRole(updated); beginRoleEdit(updated); onToast("Role permissions reset to defaults");
    } catch (reason) { onError(messageFor(reason)); } finally { setSaving(false); }
  }

  async function deleteRole(role: ManagedRole) {
    if (!window.confirm(`Delete unused role ${role.name}?`)) return;
    setSaving(true);
    try {
      if (!demoMode) await api!.deleteRole(organizationId, role.id);
      setData((current) => current ? { ...current, roles: current.roles.filter((item) => item.id !== role.id) } : current);
      setEditingRoleId(""); onToast("Unused role deleted");
    } catch (reason) { onError(messageFor(reason)); } finally { setSaving(false); }
  }

  async function revokeAssignment(assignmentId: string) {
    if (!window.confirm("Revoke this role assignment?")) return;
    setSaving(true);
    try {
      if (!demoMode) await api!.revokeOrganizationRole(organizationId, assignmentId);
      setData((current) => current ? { ...current, assignments: current.assignments.filter((item) => item.id !== assignmentId) } : current);
      onToast("Role assignment revoked");
    } catch (reason) { onError(messageFor(reason)); } finally { setSaving(false); }
  }

  const visibleMembers = data.members.filter((member) => {
    const query = memberSearch.trim().toLocaleLowerCase();
    if (!query) return true;
    return member.display_name.toLocaleLowerCase().includes(query)
      || (member.employee_reference ?? "").toLocaleLowerCase().includes(query);
  });

  return <div className="team-layout">
    <section className="team-members admin-card">
      <div className="admin-card-heading"><div><p className="section-label">Members</p><h2>{data.members.length} team members</h2></div><span className="safe-pill">Organisation scope</span></div>
      <div className="pin-sign-in-link-row">
        <div>
          <strong>Chef sign-in link</strong>
          <span>Share this with your team — they tap it once on their own phone, pick their name, and sign in with just their PIN. No email or password needed. A site’s own link only lists the people who work there.</span>
        </div>
        <div className="pin-link-buttons">
          <button className="secondary-button compact-button" disabled={!orgCode} onClick={() => void copySignInLink()}>{linkSites.length ? "Copy link · everyone" : "Copy link"}</button>
          {linkSites.map((site) => <button key={site.id} type="button" className="secondary-button compact-button" disabled={!orgCode} onClick={() => void copySignInLink(site)}>Copy link · {site.name}</button>)}
        </div>
      </div>
      <label className="search-box member-search"><Icon name="search" /><input aria-label="Search members" placeholder="Search by name or employee reference…" value={memberSearch} onChange={(event) => setMemberSearch(event.target.value)} /></label>
      <div className="member-table">
        {visibleMembers.map((member) => <article key={member.id} className={editingMemberId === member.id ? "active-row" : undefined}>
          <span className="avatar">{member.display_name.split(" ").map((part) => part[0]).slice(0, 2).join("")}</span>
          <div>
            <strong>{member.display_name}{member.protected ? " · Protected" : ""}</strong>
            <span>{member.employee_reference ?? "No employee reference"} · {member.member_type === "pin_only" ? "PIN-only" : "Named account"}</span>
            <div className="role-chip-row">
              {data.assignments.filter((item) => item.member_id === member.id).map((assignment) => {
                const role = data.roles.find((item) => item.id === assignment.role_id);
                if (!role) return null;
                const siteName = assignment.site_id ? sites.find(([id]) => id === assignment.site_id)?.[1] : null;
                const label = siteName ? `${role.name} — ${siteName}` : role.name;
                return <small key={assignment.id}>{label}<button type="button" aria-label={`Revoke ${label} from ${member.display_name}`} disabled={saving || member.id === profile.member_id || role.protected_level >= actorLevelAt(assignment.site_id)} onClick={() => void revokeAssignment(assignment.id)}>×</button></small>;
              })}
              {!memberRoles(member.id).length && <small className="unassigned">No role</small>}
            </div>
          </div>
          <div className="member-row-actions">
            <span className={`member-state ${member.active ? "active" : ""}`}>{member.active ? "Active" : "Inactive"}</span>
            <button className="row-action" disabled={saving || (member.protected && member.id !== profile.member_id)} onClick={() => beginMemberEdit(member)}>Edit</button>
            {!data.auth_linked_member_ids.includes(member.id) && (
              <button className="row-action" title="Creates a sign-in account for this member with an email and password you set here — no separate Supabase step needed" disabled={saving} onClick={() => beginLinkAuth(member)}>Create sign-in account</button>
            )}
            {data.auth_linked_member_ids.includes(member.id) && (
              <button className="row-action" title={member.id === profile.member_id ? "You can't reset your own password here" : "Sets a new sign-in password for this member, shown to you once, and signs them out everywhere"} disabled={saving || resettingPasswordFor === member.id || member.id === profile.member_id || member.protected || !member.active} onClick={() => void resetPassword(member)}>{resettingPasswordFor === member.id ? "Resetting…" : "Reset password"}</button>
            )}
            <button className="row-action" title="Signs this member out of any phone they're currently signed into with their PIN" disabled={saving || signingOutDeviceFor === member.id} onClick={() => void signOutDevice(member)}>{signingOutDeviceFor === member.id ? "Signing out…" : "Sign out device"}</button>
            {orgCode && member.active && memberLinkSites(member).map((site) => <button key={site.id} className="row-action" title="Copies this person's own sign-in link — it opens straight to their PIN pad at this site" onClick={() => void copyMemberSignInLink(member, site)}>PIN link · {site.name}</button>)}
            <button className="row-action" disabled={saving || member.protected || member.id === profile.member_id} onClick={() => void toggleMember(member)}>{member.active ? "Deactivate" : "Activate"}</button>
          </div>
          {linkingAuthFor === member.id && (
            <form className="member-auth-link-form" onSubmit={(event) => void linkAuth(event, member)}>
              <input type="email" required placeholder="chef@example.com" aria-label={`Sign-in email for ${member.display_name}`} value={linkAuthEmail} onChange={(event) => setLinkAuthEmail(event.target.value)} />
              <input type="text" required minLength={8} placeholder="Password" aria-label={`Sign-in password for ${member.display_name}`} value={linkAuthPassword} onChange={(event) => setLinkAuthPassword(event.target.value)} />
              <button className="secondary-button compact-button" type="button" onClick={() => setLinkAuthPassword(generateSignInPassword())}>New password</button>
              <button className="primary-button compact-button" type="submit" disabled={linkingAuthSaving || !linkAuthEmail.trim() || linkAuthPassword.trim().length < 8}>{linkingAuthSaving ? "Creating…" : "Create account"}</button>
              <button className="secondary-button compact-button" type="button" onClick={cancelLinkAuth}>Cancel</button>
            </form>
          )}
          {createdCredential && createdCredential.memberId === member.id && (
            <div className="member-auth-credential-panel" role="status">
              {createdCredential.reset
                ? <p><strong>{createdCredential.memberName}'s password was reset.</strong> They've been signed out everywhere. Share the new password with them now — it won't be shown again. They'll be asked to choose their own when they sign in.</p>
                : <p><strong>{createdCredential.memberName}'s sign-in account was created.</strong> Share these with them now — the password won't be shown again. They'll be asked to choose their own at first sign-in.</p>}
              <dl>
                <dt>Email</dt><dd>{createdCredential.email || "—"}</dd>
                <dt>Password</dt><dd><code>{createdCredential.password}</code></dd>
              </dl>
              <div className="member-auth-credential-actions">
                <button className="secondary-button compact-button" type="button" onClick={() => void copyCredential()}>Copy</button>
                <button className="primary-button compact-button" type="button" onClick={() => setCreatedCredential(null)}>Done — I've saved this</button>
              </div>
            </div>
          )}
        </article>)}
        {!visibleMembers.length && <div className="empty-detail"><Icon name="search" /><strong>No members found</strong><p>Change the search.</p></div>}
      </div>
      <section className="role-directory">
        <p className="section-label">Roles</p>
        {data.roles.map((role) => <article key={role.id}><div><strong>{role.name}</strong><span>Level {role.protected_level} · {role.active ? "Active" : "Inactive"} · {role.permissions.length} permissions</span></div><button className="secondary-button compact-button" disabled={role.protected_level >= actorLevel} onClick={() => beginRoleEdit(role)}>Manage</button></article>)}
      </section>
      <RolePermissionTable roles={data.roles} />
    </section>
    <aside className="team-controls">
      {editingMemberId ? <section className="admin-card">
        <p className="section-label">Edit member</p>
        <h3>Details, status and PIN</h3>
        <form onSubmit={saveMemberEdit}>
          <label>Display name<input required maxLength={100} value={editMemberName} onChange={(event) => setEditMemberName(event.target.value)} /></label>
          <label>Employee reference<input maxLength={100} value={editMemberReference} onChange={(event) => setEditMemberReference(event.target.value)} /></label>
          <label className="checkbox-row"><input type="checkbox" checked={editMemberActive} onChange={(event) => setEditMemberActive(event.target.checked)} /> Active</label>
          <label>New PIN<input inputMode="numeric" placeholder="Leave blank to keep the current PIN" pattern="[0-9]{4,6}" value={editMemberPin} onChange={(event) => { setEditMemberPin(event.target.value.replace(/\D/g, "").slice(0, 6)); setEditMemberPinError(null); }} /></label>
          {editMemberPinError && <p className="inline-warning">{editMemberPinError}</p>}
          <button className="primary-button" disabled={saving || !editMemberName.trim()}>{saving ? "Saving…" : "Save member"}</button>
          <button type="button" className="secondary-button" disabled={saving} onClick={cancelMemberEdit}>Cancel</button>
        </form>
      </section> : <section className="admin-card">
        <p className="section-label">Add member</p>
        <h3>Secure staff credential</h3>
        <form onSubmit={createMember}>
          <label>Display name<input required maxLength={100} value={newMember.display_name} onChange={(event) => setNewMember({ ...newMember, display_name: event.target.value })} /></label>
          <label>Employee reference<input maxLength={100} value={newMember.employee_reference} onChange={(event) => setNewMember({ ...newMember, employee_reference: event.target.value })} /></label>
          <label>Account type<select value={newMember.member_type} onChange={(event) => setNewMember({ ...newMember, member_type: event.target.value as "pin_only" })}><option value="pin_only">PIN-only shared device</option><option value="named">Named account + PIN</option></select></label>
          <label>Initial PIN<input required inputMode="numeric" pattern="[0-9]{4,6}" value={newMember.pin} onChange={(event) => setNewMember({ ...newMember, pin: event.target.value.replace(/\D/g, "").slice(0, 6) })} /></label>
          <button className="primary-button" disabled={saving || !newMember.display_name.trim() || newMember.pin.length < 4}>{saving ? "Creating…" : "Create member"}</button>
        </form>
      </section>}
      <section className="admin-card">
        <p className="section-label">Assign role</p>
        <h3>Organisation access</h3>
        <form onSubmit={assignRole}>
          <label>Team member<select value={memberId} onChange={(event) => setMemberId(event.target.value)}><option value="">Choose member</option>{data.members.filter((member) => member.active).map((member) => <option key={member.id} value={member.id}>{member.display_name}</option>)}</select></label>
          <label>Role<select value={roleId} onChange={(event) => setRoleId(event.target.value)}><option value="">Choose lower role</option>{manageableRoles.map((role) => <option key={role.id} value={role.id}>{role.name} · level {role.protected_level}</option>)}</select></label>
          <fieldset className="site-picker">
            <legend>Site</legend>
            <label className="checkbox-row"><input type="checkbox" checked={!assignSiteIds.length} onChange={() => setAssignSiteIds([])} /> Organisation-wide (every site)</label>
            {sites.map(([id, name]) => <label key={id} className="checkbox-row"><input type="checkbox" checked={assignSiteIds.includes(id)} onChange={() => setAssignSiteIds((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id])} /> {name}</label>)}
          </fieldset>
          <button className="primary-button" disabled={saving || !memberId || !roleId}>{assignSiteIds.length > 1 ? `Assign role at ${assignSiteIds.length} sites` : "Assign role"}</button>
        </form>
      </section>
      {editingRoleId ? <section className="admin-card">
        <p className="section-label">Edit role</p>
        <h3>Permissions and status</h3>
        <form onSubmit={saveRole}>
          <label>Role name<input required maxLength={60} value={editingRoleName} onChange={(event) => setEditingRoleName(event.target.value)} /></label>
          <label className="checkbox-row"><input type="checkbox" checked={editingRoleActive} onChange={(event) => setEditingRoleActive(event.target.checked)} /> Active role</label>
          <div className="permission-picker">{delegablePermissions.map((permission) => <label key={permission.id}><input type="checkbox" checked={editingPermissions.includes(permission.code)} onChange={() => setEditingPermissions((current) => current.includes(permission.code) ? current.filter((code) => code !== permission.code) : [...current, permission.code])} /><span><strong>{permission.code}</strong><small>{permission.description}</small></span></label>)}</div>
          <button className="primary-button" disabled={saving || !editingRoleName.trim()}>Save role</button>
          <button type="button" className="secondary-button" disabled={saving} onClick={() => void resetPermissions(data.roles.find((item) => item.id === editingRoleId)!)}>Reset permissions</button>
          <button type="button" className="danger-button" disabled={saving || data.assignments.some((item) => item.role_id === editingRoleId)} onClick={() => void deleteRole(data.roles.find((item) => item.id === editingRoleId)!)}>Delete unused role</button>
        </form>
      </section> : <section className="admin-card">
        <p className="section-label">Create role</p>
        <h3>Delegated permission set</h3>
        <form onSubmit={createRole}>
          <label>Role name<input value={roleName} onChange={(event) => setRoleName(event.target.value)} maxLength={60} placeholder="e.g. Kitchen operator" /></label>
          <label>Protection level<input type="number" min="0" max={Math.max(0, actorLevel - 1)} value={protectedLevel} onChange={(event) => setProtectedLevel(event.target.value)} /></label>
          <div className="permission-picker">{delegablePermissions.map((permission) => <label key={permission.id}><input type="checkbox" checked={permissionCodes.includes(permission.code)} onChange={() => togglePermission(permission.code)} /><span><strong>{permission.code}</strong><small>{permission.description}</small></span></label>)}</div>
          <button className="primary-button" disabled={saving || !roleName.trim() || !permissionCodes.length || Number(protectedLevel) >= actorLevel}>Create lower role</button>
        </form>
      </section>}
    </aside>
  </div>;
}

function ProductInspector({
  api,
  organizationId,
  demoMode,
  product,
  detail,
  categoryName,
  activeTab,
  onTab,
  onEdit,
  onArchive,
  onReactivate,
  onDuplicate,
  canDuplicate,
  canEdit,
  canEditAllergens,
  canEditShelfLife,
  canApproveShelfLife,
  canPrint,
  physicalPrintingEnabled,
  onAddAllergen,
  onEditAllergen,
  onNewShelfLife,
  onApproveShelfLife,
  onRetireShelfLife,
  onEditShelfLife,
  onToast,
}: {
  api?: KitchenHubApi;
  organizationId: string;
  demoMode: boolean;
  product: Product | null;
  detail: ProductDetail | null;
  categoryName: string;
  activeTab: "overview" | "allergens" | "shelf-life";
  onTab: (tab: "overview" | "allergens" | "shelf-life") => void;
  onEdit: () => void;
  onArchive: () => void;
  onReactivate: () => void;
  onDuplicate: () => void;
  canDuplicate: boolean;
  canEdit: boolean;
  canEditAllergens: boolean;
  canEditShelfLife: boolean;
  canApproveShelfLife: boolean;
  canPrint: boolean;
  physicalPrintingEnabled: boolean;
  onAddAllergen: () => void;
  onEditAllergen: (allergen: ProductAllergen) => void;
  onNewShelfLife: () => void;
  onApproveShelfLife: (rule: ShelfLifeRule) => void;
  onRetireShelfLife: (rule: ShelfLifeRule) => void;
  onEditShelfLife: (rule: ShelfLifeRule) => void;
  onToast: (message: string) => void;
}) {
  if (!product) return <aside className="inspector empty-inspector"><Icon name="catalogue" size={30} /><h2>Select a product</h2><p>Choose a product to inspect its label information.</p></aside>;
  const primaryRule = detail?.shelf_life_rules.find((rule) => rule.approval_status === "approved");
  return (
    <aside className="inspector" id="product-inspector">
      <div className="inspector-heading"><div><p className="eyebrow">{categoryName}</p><h2>{product.name}</h2></div><div className="header-actions"><button className="secondary-button compact-button" onClick={onDuplicate} disabled={!canDuplicate} title={canDuplicate ? "Copy this product's departments, allergens and shelf-life rules into a new draft product" : "Duplicating requires MFA and create permission"}>Duplicate</button>{product.active
              ? <button className="danger-button compact-button" onClick={onArchive} disabled={!canEdit} title={canEdit ? undefined : "Product changes require MFA and edit permission — sign in again if you switched in with a PIN"}>Archive</button>
              : <button className="secondary-button compact-button" onClick={onReactivate} disabled={!canEdit} title={canEdit ? "Make this archived product active again" : "Product changes require MFA and edit permission — sign in again if you switched in with a PIN"}>Reactivate</button>}<button className="icon-button" onClick={onEdit} disabled={!canEdit} aria-label="Edit product" title={canEdit ? "Edit product" : "Product changes require MFA and edit permission — sign in again if you switched in with a PIN"}><Icon name="edit" /></button></div></div>
      {!product.active && (
        <div className="archived-notice">
          <Icon name="warning" />
          <div>
            <strong>Archived — not available to print</strong>
            <p>
              {detail && detail.allergens.length === 0
                ? "No allergen declaration has been recorded. If this is made active, its label prints as containing no allergens."
                : "Reactivate to return it to the catalogue."}
            </p>
          </div>
        </div>
      )}
      <div className="detail-tabs" role="tablist">
        <button className={activeTab === "overview" ? "active" : ""} onClick={() => onTab("overview")}>Overview</button>
        <button className={activeTab === "allergens" ? "active" : ""} onClick={() => onTab("allergens")}>Allergens <span>{detail?.allergens.length ?? 0}</span></button>
        <button className={activeTab === "shelf-life" ? "active" : ""} onClick={() => onTab("shelf-life")}>Shelf life <span>{detail?.shelf_life_rules.length ?? 0}</span></button>
      </div>
      {activeTab === "overview" && <>
        <section className="detail-block"><p className="section-label">Departments</p><div className="allergen-chips department-chips">{detail?.departments.map((item) => <span key={item.site_department_id} className={!item.active ? "may" : ""}>{item.name}{!item.active ? " · inactive" : ""}</span>)}{detail && !detail.departments.length && <p className="skeleton-line">No department assigned</p>}</div></section>
        <section className="detail-block"><p className="section-label">Storage</p><div className="storage-row"><span className="round-icon"><Icon name="clock" /></span><div><strong>{primaryRule ? titleCase(primaryRule.storage_state) : "Not set"}</strong>{product.storage_instructions && <p>{product.storage_instructions}</p>}</div></div></section>
        <section className="detail-block"><div className="section-title"><p className="section-label">Allergen declaration</p><button onClick={() => onTab("allergens")}>View detail</button></div><AllergenChips detail={detail} /></section>
        <section className="detail-block label-preview-section" id="label-preview-section"><div className="section-title"><p className="section-label">Label preview</p><span className="safe-pill">Preview only</span></div><LabelPreview product={product} detail={detail} api={api} organizationId={organizationId} demoMode={demoMode} canPrint={canPrint} physicalPrintingEnabled={physicalPrintingEnabled} onToast={onToast} /></section>
      </>}
      {activeTab === "allergens" && (
        <AllergenDetail
          detail={detail}
          canEdit={canEditAllergens}
          onAdd={onAddAllergen}
          onEdit={onEditAllergen}
        />
      )}
      {activeTab === "shelf-life" && (
        <ShelfLifeDetail
          detail={detail}
          canEdit={canEditShelfLife}
          canApprove={canApproveShelfLife}
          onAdd={onNewShelfLife}
          onApprove={onApproveShelfLife}
          onRetire={onRetireShelfLife}
          onEdit={onEditShelfLife}
        />
      )}
    </aside>
  );
}

function AllergenChips({ detail }: { detail: ProductDetail | null }) {
  if (!detail) return <p className="skeleton-line">Loading declarations…</p>;
  if (!detail.allergens.length) return <p className="clear-state"><Icon name="shield" /> No allergens declared</p>;
  return <div className="allergen-chips">{detail.allergens.map((item) => <span key={item.allergen_id} className={item.relation === "may_contain" ? "may" : ""}>{item.relation === "may_contain" ? "May contain " : ""}{item.name}</span>)}</div>;
}

function AllergenDetail({ detail, canEdit, onAdd, onEdit }: {
  detail: ProductDetail | null;
  canEdit: boolean;
  onAdd: () => void;
  onEdit: (allergen: ProductAllergen) => void;
}) {
  return <section className="detail-list"><div className="detail-intro detail-action-row"><div><p className="section-label">Regulated allergens</p><p>Declarations are shown separately from supplier evidence.</p></div><button className="secondary-button compact-button" onClick={onAdd} disabled={!canEdit} title={canEdit ? undefined : "Allergen changes require MFA and allergen permission"}><Icon name="plus" size={15} /> Add declaration</button></div>{detail?.allergens.map((item) => <article key={item.allergen_id}><span className={`relation-dot ${item.relation}`} /><div><strong>{item.name}</strong><span>{item.relation === "contains" ? "Contains" : "May contain"}</span><p>{item.evidence ?? "No evidence note recorded"}</p></div><button className="row-action" onClick={() => onEdit(item)} disabled={!canEdit} aria-label={`Edit ${item.name} declaration`}><Icon name="edit" size={16} /></button></article>)}{detail && !detail.allergens.length && <div className="empty-detail"><Icon name="shield" /><strong>No allergens declared</strong><p>Add a declaration to make the label preview safer and more complete.</p></div>}</section>;
}

function ShelfLifeDetail({ detail, canEdit, canApprove, onAdd, onApprove, onRetire, onEdit }: {
  detail: ProductDetail | null;
  canEdit: boolean;
  canApprove: boolean;
  onAdd: () => void;
  onApprove: (rule: ShelfLifeRule) => void;
  onRetire: (rule: ShelfLifeRule) => void;
  onEdit: (rule: ShelfLifeRule) => void;
}) {
  return <section className="detail-list"><div className="detail-intro detail-action-row"><div><p className="section-label">Shelf-life rules</p><p>Only approved rules can drive label dates.</p></div><button className="secondary-button compact-button" onClick={onAdd} disabled={!canEdit} title={canEdit ? undefined : "Shelf-life changes require MFA and shelf-life permission"}><Icon name="plus" size={15} /> New draft</button></div>{detail?.shelf_life_rules.map((rule) => <article key={rule.id} className={rule.approval_status === "retired" ? "retired-rule" : undefined}><span className="round-icon compact"><Icon name="clock" size={17} /></span><div><strong>{rule.label_type_name} · {titleCase(rule.storage_state)}</strong><span>{durationLabel(rule.duration_value, rule.duration_unit)} · {rule.date_mark_type.replaceAll("_", " ")}</span><p><span className={`rule-status ${rule.approval_status}`}>{rule.approval_status}</span> {rule.calculation_method.replaceAll("_", " ")}</p></div>{rule.approval_status !== "retired" && <div className="rule-actions">{rule.approval_status === "draft" && <button className="rule-approve" onClick={() => onApprove(rule)} disabled={!canApprove} title={canApprove ? "Approve rule" : "Approval requires MFA and shelf_life.approve"}>Approve</button>}{rule.approval_status === "approved" && <button className="secondary-button compact-button" onClick={() => onEdit(rule)} disabled={!canApprove} title={canApprove ? "Edit this rule's duration without retiring it first" : "Editing requires MFA and shelf_life.approve"}>Edit</button>}<button className="rule-retire" onClick={() => onRetire(rule)} disabled={!canApprove} title={canApprove ? "Retire rule" : "Retirement requires MFA and shelf_life.approve"}>Retire</button></div>}</article>)}{detail && !detail.shelf_life_rules.length && <div className="empty-detail"><Icon name="clock" /><strong>No shelf-life rule</strong><p>Create a draft for review; production use remains locked until approval.</p></div>}</section>;
}

function LabelPreview({ product, detail, api, organizationId, demoMode, canPrint, physicalPrintingEnabled, onToast }: {
  product: Product;
  detail: ProductDetail | null;
  api?: KitchenHubApi;
  organizationId: string;
  demoMode: boolean;
  canPrint: boolean;
  physicalPrintingEnabled: boolean;
  onToast: (message: string) => void;
}) {
  const [labelType, setLabelType] = useState<StandardLabelType>("use_by");
  const [storageState, setStorageState] = useState<StorageState>("chilled");
  const [preparedOn, setPreparedOn] = useState(todayForInput());
  const [preview, setPreview] = useState<LabelPreviewData | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [printOptions, setPrintOptions] = useState<PrintOptions | null>(null);
  const [printerId, setPrinterId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [printing, setPrinting] = useState(false);
  const [printError, setPrintError] = useState<string | null>(null);

  const primaryDepartment = detail?.departments.find((item) => item.active) ?? detail?.departments[0];
  const siteId = primaryDepartment?.site_id ?? "";

  useEffect(() => {
    setPreview(null);
    setPreviewError(null);
    if (demoMode || !api || !detail) return;
    let current = true;
    void api.labelPreview(organizationId, product.id, labelType, storageState, preparedOn)
      .then((result) => { if (current) setPreview(result); })
      .catch((reason: unknown) => { if (current) setPreviewError(messageFor(reason)); });
    return () => { current = false; };
  }, [api, demoMode, detail, labelType, organizationId, preparedOn, product.id, storageState]);

  useEffect(() => {
    setPrintOptions(null);
    setPrintError(null);
    if (demoMode || !api || !canPrint || !siteId) return;
    let current = true;
    void api.printOptions(organizationId, siteId).then((next) => {
      if (!current) return;
      setPrintOptions(next);
      setPrinterId((existing) => next.printers.some((item) => item.id === existing) ? existing : next.printers[0]?.id ?? "");
    }).catch((reason: unknown) => { if (current) setPrintError(messageFor(reason)); });
    return () => { current = false; };
  }, [api, canPrint, demoMode, organizationId, siteId]);

  const selectedPrinter = printOptions?.printers.find((item) => item.id === printerId);

  async function submitPrint() {
    if (!api || !siteId || !printerId || !selectedPrinter?.available) return;
    setPrinting(true);
    setPrintError(null);
    try {
      const result = await api.createOperationalPrintJob(organizationId, "quick", {
        site_id: siteId, printer_id: printerId, prepared_on: preparedOn,
        idempotency_key: `catalogue-print-${crypto.randomUUID()}`,
        site_department_id: primaryDepartment?.site_department_id ?? null,
        selections: [{ product_id: product.id, quantity, label_type: labelType, storage_state: storageState }],
      });
      onToast(printResultToast(result));
      // On a phone the label sits below the whole product list; once it has
      // printed, go back to the search at the top for the next product. A
      // failed print stays put so the error can be read and retried.
      if (window.matchMedia("(max-width: 820px)").matches) window.scrollTo({ top: 0, behavior: "auto" });
    } catch (reason) {
      setPrintError(messageFor(reason));
    } finally {
      setPrinting(false);
    }
  }

  const fallbackRule = detail?.shelf_life_rules.find((item) => item.storage_state === storageState);
  const dateMark = previewDateMark(fallbackRule);
  const previewUrl = preview ? `data:image/svg+xml;charset=utf-8,${encodeURIComponent(preview.svg)}` : null;
  return (
    <div className="label-frame">
      <div className="label-preview-controls">
        <label>Label type<select value={labelType} onChange={(event) => setLabelType(event.target.value as StandardLabelType)}>{["prep", "cooked", "defrosted", "use_by"].map((item) => <option key={item} value={item}>{titleCase(item.replaceAll("_", " "))}</option>)}</select></label>
        <label>Storage<select value={storageState} onChange={(event) => setStorageState(event.target.value as StorageState)}>{["ambient", "chilled", "frozen", "defrosted"].map((item) => <option key={item} value={item}>{titleCase(item)}</option>)}</select></label>
        <label>Prepared date<input type="date" value={preparedOn} onChange={(event) => setPreparedOn(event.target.value)} /></label>
      </div>
      {previewUrl ? <img className="rendered-label-preview" src={previewUrl} alt={`${titleCase(labelType)} label preview for ${product.name}`} /> : <div className="label-paper">
        <div className="label-top"><strong>NCC</strong><span>PREP LABEL</span></div>
        <h3>{product.name}</h3>
        <div className="label-dates"><span><small>PREPARED</small><strong>{preparedOn.split("-").reverse().join("/")}</strong></span><span><small>{dateMark.label}</small><strong>{dateMark.value}</strong></span></div>
        <p><b>ALLERGENS:</b> {detail?.allergens.filter((item) => item.relation === "contains").map((item) => item.name.toUpperCase()).join(", ") || "NONE DECLARED"}</p>
        <footer><span>Keep refrigerated · 1–5°C</span><span>AM</span></footer>
        <div className="preview-stamp">SHADOW PREVIEW</div>
      </div>}
      {previewError && <p className="label-preview-error">{previewError}</p>}
      {preview && <div className="label-preview-meta"><span>{preview.width_dots}×{preview.height_dots} dots · {preview.dpi} dpi</span><span>{preview.rule_approval_status} rule · {preview.rule_source.replaceAll("_", " ")}</span>{preview.warnings.map((warning) => <strong key={warning}>{warning}</strong>)}</div>}
      {/* On a phone this bar stays pinned above the bottom menu while the label is on screen. */}
      <div className="label-print-bar">
        {canPrint && <div className="label-preview-controls">
          <label>Printer<select value={printerId} onChange={(event) => setPrinterId(event.target.value)} disabled={!printOptions}><option value="">Choose printer</option>{printOptions?.printers.map((item) => <option key={item.id} value={item.id}>{item.name} · {item.available ? "ready" : item.queue_availability}</option>)}</select></label>
          <div className="quantity-stepper"><span className="quantity-stepper-label" id="label-quantity-label">Quantity</span><div className="quantity-stepper-controls"><button type="button" aria-label="One label fewer" disabled={quantity <= 1} onClick={() => setQuantity((current) => Math.max(1, current - 1))}>−</button><input type="number" inputMode="numeric" min={1} max={MAX_LABEL_QUANTITY} aria-labelledby="label-quantity-label" value={quantity} onChange={(event) => setQuantity(clampLabelQuantity(event.target.value, quantity))} onFocus={(event) => event.target.select()} /><button type="button" aria-label="One label more" disabled={quantity >= MAX_LABEL_QUANTITY} onClick={() => setQuantity((current) => Math.min(MAX_LABEL_QUANTITY, current + 1))}>+</button></div></div>
        </div>}
        {selectedPrinter && !selectedPrinter.available && <div className="inline-warning"><strong>Printer unavailable</strong><span>{selectedPrinter.bridge_status} bridge · {selectedPrinter.queue_availability} queue. No job can be created.</span></div>}
        {printError && <p className="label-preview-error">{printError}</p>}
        <button
          className="primary-button"
          onClick={() => void submitPrint()}
          disabled={!canPrint || demoMode || printing || !printerId || !selectedPrinter?.available}
          title={!canPrint ? "Printing requires MFA and print permission" : demoMode ? "Not available in local preview" : undefined}
        >
          <Icon name="label" /> {printing ? "Creating…" : physicalPrintingEnabled ? "Print" : "Create simulated job"}
        </button>
      </div>
    </div>
  );
}

// Add product: the shelf lives a kitchen almost always uses, as use-by dates.
const NEW_PRODUCT_SHELF_LIVES: { state: StorageState; days: number; on: boolean }[] = [
  { state: "chilled", days: 3, on: true },
  { state: "frozen", days: 90, on: false },
  { state: "defrosted", days: 2, on: false },
  { state: "ambient", days: 90, on: false },
];

function ProductEditor({ mode, product, productDepartments, categories, departments, allergens, labelTypes, defaultSiteId, canEditAllergens, canEditShelfLife, canApproveShelfLife, canCreateCategory, onClose, onSave, onCreateCategory }: {
  mode: "create" | "edit";
  product: Product | null;
  productDepartments: ProductDetail["departments"];
  categories: Category[];
  departments: Department[];
  allergens: ReferenceData["allergens"];
  labelTypes: ReferenceData["label_types"];
  defaultSiteId: string;
  canEditAllergens: boolean;
  canEditShelfLife: boolean;
  canApproveShelfLife: boolean;
  canCreateCategory: boolean;
  onClose: () => void;
  onSave: (input: ProductInput) => Promise<void>;
  onCreateCategory: (name: string) => Promise<Category>;
}) {
  const [name, setName] = useState(product?.name ?? "");
  const [categoryId, setCategoryId] = useState(() => product
    ? product.category_id ?? ""
    : categories.find((item) => item.name.trim().toLocaleLowerCase() === "kitchen")?.id ?? "");
  const [unit, setUnit] = useState<UnitOfMeasure>(product?.unit_of_measure ?? "item");
  const [declared, setDeclared] = useState<Record<string, NewProductAllergen["relation"]>>({});
  const [shelfLives, setShelfLives] = useState(() => NEW_PRODUCT_SHELF_LIVES.map((item) => ({ ...item, days: String(item.days) })));
  const useByLabel = labelTypes.find((item) => item.code === "use_by");
  const showAllergens = mode === "create" && canEditAllergens;
  const showShelfLives = mode === "create" && canEditShelfLife && Boolean(useByLabel);
  const shelfLivesValid = shelfLives.every((item) => !item.on || /^\d{1,4}$/.test(item.days));
  const [departmentIds, setDepartmentIds] = useState<string[]>(productDepartments.map((item) => item.site_department_id));
  const [saving, setSaving] = useState(false);
  const [addingCategory, setAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [categoryError, setCategoryError] = useState("");

  // A product belongs to exactly one site, fixed at creation -- picking a
  // site here narrows which departments can be chosen below (a product can
  // never link to another site's department), and is locked once the
  // product exists.
  const availableSites = Array.from(
    new Map(departments.map((item) => [item.site_id, item.site_name])).entries(),
  );
  // New products start at the site you're working at.
  const [siteId, setSiteId] = useState(product?.site_id
    ?? (availableSites.some(([id]) => id === defaultSiteId) ? defaultSiteId
      : availableSites.length === 1 ? availableSites[0][0] : ""));

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!siteId || !shelfLivesValid) return;
    setSaving(true);
    await onSave({
      name: name.trim(), category_id: categoryId || null,
      // No longer asked for; an existing product keeps what it had.
      storage_instructions: product?.storage_instructions ?? null,
      unit_of_measure: unit, site_department_ids: departmentIds, site_id: siteId,
      ...(mode === "create" ? {
        allergens: showAllergens
          ? Object.entries(declared).map(([allergen_id, relation]) => ({ allergen_id, relation }))
          : [],
        shelf_lives: showShelfLives && useByLabel
          ? shelfLives.filter((item) => item.on).map((item) => ({ label_type_id: useByLabel.id, storage_state: item.state, duration_value: Number(item.days) }))
          : [],
      } : {}),
    });
    setSaving(false);
  }

  function toggleAllergen(allergenId: string, relation: NewProductAllergen["relation"]) {
    // One declaration per allergen: ticking one box clears the other.
    setDeclared((current) => {
      const next = { ...current };
      if (next[allergenId] === relation) delete next[allergenId];
      else next[allergenId] = relation;
      return next;
    });
  }

  const orderedAllergens = [...allergens.filter((item) => !item.parent_id)]
    .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name))
    .flatMap((parent) => [parent, ...allergens.filter((item) => item.parent_id === parent.id)
      .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name))]);

  async function submitNewCategory(event: FormEvent) {
    event.preventDefault();
    setCategoryError("");
    try {
      const created = await onCreateCategory(newCategoryName);
      setCategoryId(created.id);
      setAddingCategory(false);
      setNewCategoryName("");
    } catch (reason) {
      setCategoryError(messageFor(reason));
    }
  }

  const availableDepartments = departments.filter((item) => (item.active || departmentIds.includes(item.id)) && item.site_id === siteId);
  const siteName = availableSites.find(([id]) => id === siteId)?.[1];
  return <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}><section className="product-editor" role="dialog" aria-modal="true" aria-labelledby="editor-title"><header><div><p className="eyebrow">Catalogue</p><h2 id="editor-title">{mode === "create" ? "Add a new product" : "Edit product"}</h2></div><button className="icon-button" onClick={onClose} aria-label="Close editor"><Icon name="close" /></button></header><form onSubmit={submit}><label htmlFor="product-name">Product name<input id="product-name" autoFocus required minLength={1} maxLength={200} value={name} onChange={(event) => setName(event.target.value)} placeholder="e.g. Roasted tomato soup" /></label>{mode === "create" ? <label htmlFor="product-site">Site<select id="product-site" required value={siteId} onChange={(event) => { setSiteId(event.target.value); setDepartmentIds([]); }}><option value="">Choose a site</option>{availableSites.map(([id, name]) => <option key={id} value={id}>{name}</option>)}</select></label> : <p className="editor-note"><Icon name="shield" /><span>Site: <strong>{siteName ?? "—"}</strong> — fixed once a product is created.</span></p>}<div className="form-row"><label htmlFor="product-category">Category<select id="product-category" value={categoryId} onChange={(event) => setCategoryId(event.target.value)}><option value="">Uncategorised</option>{categories.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label><label htmlFor="product-unit">Unit<select id="product-unit" value={unit} onChange={(event) => setUnit(event.target.value as UnitOfMeasure)}>{["item", "portion", "tray", "kg", "g", "l", "ml"].map((item) => <option key={item}>{item}</option>)}</select></label></div>{canCreateCategory && (addingCategory ? <div className="inline-add-row"><input aria-label="New category name" autoFocus maxLength={100} placeholder="New category name" value={newCategoryName} onChange={(event) => setNewCategoryName(event.target.value)} /><button type="button" className="secondary-button compact-button" disabled={!newCategoryName.trim()} onClick={(event) => void submitNewCategory(event)}>Add</button><button type="button" className="secondary-button compact-button" onClick={() => { setAddingCategory(false); setNewCategoryName(""); setCategoryError(""); }}>Cancel</button>{categoryError && <span className="field-error">{categoryError}</span>}</div> : <button type="button" className="link-button" onClick={() => setAddingCategory(true)}>+ New category</button>)}<fieldset><legend>Departments · choose one or more{siteName ? ` at ${siteName}` : ""}</legend><div className="choice-grid">{availableDepartments.map((department) => <label key={department.id}><input type="checkbox" checked={departmentIds.includes(department.id)} onChange={() => setDepartmentIds((current) => current.includes(department.id) ? current.filter((id) => id !== department.id) : [...current, department.id])} /><span><strong>{department.name}</strong><small>{department.site_name}{department.active ? "" : " · inactive historical link"}</small></span></label>)}{siteId && !availableDepartments.length && <p className="muted">No departments at this site yet.</p>}</div></fieldset>{showAllergens && <fieldset className="new-product-allergens"><legend>Allergens · tick what this product has</legend><div className="allergen-tick-head" aria-hidden="true"><span /><span>Contains</span><span>May contain</span></div>{orderedAllergens.map((allergen) => <div key={allergen.id} className={allergen.parent_id ? "allergen-tick-row child" : "allergen-tick-row"}><span>{allergen.name}</span><label className="allergen-tick contains"><input type="checkbox" aria-label={`${allergen.name}: contains`} checked={declared[allergen.id] === "contains"} onChange={() => toggleAllergen(allergen.id, "contains")} /></label><label className="allergen-tick may"><input type="checkbox" aria-label={`${allergen.name}: may contain`} checked={declared[allergen.id] === "may_contain"} onChange={() => toggleAllergen(allergen.id, "may_contain")} /></label></div>)}</fieldset>}{showShelfLives && <fieldset className="new-product-shelf-lives"><legend>Shelf life · use by</legend><div className="shelf-life-boxes">{shelfLives.map((item, index) => <div key={item.state} className={item.on ? "shelf-life-box on" : "shelf-life-box"}><label className="shelf-life-box-toggle"><input type="checkbox" checked={item.on} onChange={() => setShelfLives((current) => current.map((row, at) => at === index ? { ...row, on: !row.on } : row))} /><strong>{titleCase(item.state)}</strong></label><label className="shelf-life-days"><input aria-label={`${titleCase(item.state)} shelf life in days`} type="number" inputMode="numeric" min="0" max="3650" step="1" disabled={!item.on} value={item.days} onChange={(event) => setShelfLives((current) => current.map((row, at) => at === index ? { ...row, days: event.target.value } : row))} /><span>days</span></label></div>)}</div><p className="muted small-note">{canApproveShelfLife ? "Ticked shelf lives are approved as you add the product." : "Ticked shelf lives are saved for a Head or Sous Chef to approve."}</p></fieldset>}<div className="editor-note"><Icon name="shield" /><span>Department relationships use stable IDs. Existing inactive relationships remain visible until explicitly removed.</span></div><footer><button type="button" className="secondary-button" onClick={onClose}>Cancel</button><button className="primary-button" disabled={saving || !name.trim() || !siteId || !departmentIds.length || !shelfLivesValid}>{saving ? "Saving…" : mode === "create" ? "Add product" : "Save changes"}</button></footer></form></section></div>;
}

function AllergenEditor({ allergens, existing, onClose, onSave, onRemove }: {
  allergens: ReferenceData["allergens"];
  existing: ProductAllergen | null;
  onClose: () => void;
  onSave: (allergenId: string, input: AllergenInput) => Promise<void>;
  onRemove: (allergenId: string) => Promise<void>;
}) {
  const [allergenId, setAllergenId] = useState(existing?.allergen_id ?? allergens[0]?.id ?? "");
  const [relation, setRelation] = useState<AllergenInput["relation"]>(existing?.relation ?? "contains");
  const [evidence, setEvidence] = useState(existing?.evidence ?? "");
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    await onSave(allergenId, { relation, evidence: evidence.trim() || null });
    setSaving(false);
  }

  async function remove() {
    if (!existing) return;
    setSaving(true);
    await onRemove(existing.allergen_id);
    setSaving(false);
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="product-editor control-editor" role="dialog" aria-modal="true" aria-labelledby="allergen-editor-title">
        <header><div><p className="eyebrow">Food safety</p><h2 id="allergen-editor-title">{existing ? "Edit allergen declaration" : "Add allergen declaration"}</h2></div><button className="icon-button" onClick={onClose} aria-label="Close allergen editor"><Icon name="close" /></button></header>
        <form onSubmit={submit}>
          <label htmlFor="allergen-name">Allergen<select id="allergen-name" value={allergenId} disabled={Boolean(existing)} onChange={(event) => setAllergenId(event.target.value)}>{allergens.map((item) => <option key={item.id} value={item.id}>{item.parent_id ? "↳ " : ""}{item.name}</option>)}</select></label>
          <fieldset><legend>Declaration</legend><div className="choice-grid"><label><input type="radio" name="allergen-relation" checked={relation === "contains"} onChange={() => setRelation("contains")} /><span><strong>Contains</strong><small>Ingredient or direct component</small></span></label><label><input type="radio" name="allergen-relation" checked={relation === "may_contain"} onChange={() => setRelation("may_contain")} /><span><strong>May contain</strong><small>Cross-contact or supplier warning</small></span></label></div></fieldset>
          <label htmlFor="allergen-evidence">Evidence note<textarea id="allergen-evidence" maxLength={2000} rows={4} value={evidence} onChange={(event) => setEvidence(event.target.value)} placeholder="e.g. Cream in recipe or supplier specification dated…" /></label>
          <div className="editor-note warning-note"><Icon name="shield" /><span>This declaration changes the product’s allergen display and label preview. The action is recorded in the audit trail.</span></div>
          <footer className={existing ? "split-footer" : undefined}>{existing && <button type="button" className="danger-button" onClick={remove} disabled={saving}>Remove declaration</button>}<span className="footer-spacer" /><button type="button" className="secondary-button" onClick={onClose} disabled={saving}>Cancel</button><button className="primary-button" disabled={saving || !allergenId}>{saving ? "Saving…" : "Save declaration"}</button></footer>
        </form>
      </section>
    </div>
  );
}

function CategoryManager({ api, organizationId, onClose, onChanged, onToast }: {
  api: KitchenHubApi;
  organizationId: string;
  onClose: () => void;
  onChanged: () => Promise<void>;
  onToast: (message: string) => void;
}) {
  const [categories, setCategories] = useState<Category[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState("");
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  const reload = useCallback(async () => {
    try {
      setCategories(await api.listCategories(organizationId, "all"));
    } catch (reason) {
      setError(messageFor(reason));
    }
  }, [api, organizationId]);

  useEffect(() => { void reload(); }, [reload]);

  async function save(category: Category, changes: { name?: string; active?: boolean }) {
    setBusyId(category.id);
    setError(null);
    try {
      await api.updateCategory(organizationId, category, {
        name: changes.name ?? category.name,
        sort_order: category.sort_order,
        active: changes.active ?? category.active,
      });
      setEditingId(null);
      await reload();
      await onChanged();
      onToast(changes.active === false ? "Category hidden" : "Category updated");
    } catch (reason) {
      // The server is the authority on whether a category is still in use, and
      // its message names how many products are still in it.
      setError(messageFor(reason));
    } finally {
      setBusyId(null);
    }
  }

  async function create(event: FormEvent) {
    event.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    setError(null);
    try {
      await api.createCategory(organizationId, { name });
      setNewName("");
      await reload();
      await onChanged();
      onToast("Category added");
    } catch (reason) {
      setError(messageFor(reason));
    } finally {
      setCreating(false);
    }
  }

  async function remove(category: Category) {
    if (!window.confirm(
      `Delete ${category.name}? This cannot be undone. Hiding it keeps the history instead.`
    )) return;
    setBusyId(category.id);
    setError(null);
    try {
      await api.deleteCategory(organizationId, category.id);
      await reload();
      await onChanged();
      onToast("Category deleted");
    } catch (reason) {
      // A category holding any product - archived ones included - is refused by
      // the server, which names how many are in the way.
      setError(messageFor(reason));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="product-editor" role="dialog" aria-modal="true" aria-labelledby="category-manager-title">
        <header>
          <div><p className="eyebrow">Catalogue</p><h2 id="category-manager-title">Categories</h2></div>
          <button className="icon-button" onClick={onClose} aria-label="Close categories"><Icon name="close" /></button>
        </header>
        <div className="category-manager">
          <p className="muted">
            Hiding a category removes it from the catalogue without deleting anything. A category
            can only be hidden once no active products are still in it.
          </p>
          {error && <p className="form-error" role="alert">{error}</p>}
          {!categories && <p className="muted">Loading…</p>}
          {categories?.map((item) => (
            <div className={item.active ? "category-row" : "category-row category-row-hidden"} key={item.id}>
              {editingId === item.id ? (
                <form
                  className="inline-edit-form"
                  onSubmit={(event) => { event.preventDefault(); void save(item, { name: draftName }); }}
                >
                  <input
                    autoFocus
                    required
                    maxLength={100}
                    value={draftName}
                    onChange={(event) => setDraftName(event.target.value)}
                  />
                  <button className="row-action" disabled={busyId === item.id || !draftName.trim()}>Save</button>
                  <button type="button" className="row-action" onClick={() => setEditingId(null)}>Cancel</button>
                </form>
              ) : (
                <>
                  <div>
                    <strong>{item.name}</strong>
                    {!item.active && <span className="member-state">Hidden</span>}
                  </div>
                  <button
                    className="row-action"
                    disabled={busyId === item.id}
                    onClick={() => { setEditingId(item.id); setDraftName(item.name); }}
                  >
                    Rename
                  </button>
                  <button
                    className="row-action"
                    disabled={busyId === item.id}
                    onClick={() => void save(item, { active: !item.active })}
                  >
                    {item.active ? "Hide" : "Show"}
                  </button>
                  <button
                    className="danger-button compact-button"
                    disabled={busyId === item.id}
                    title="Only possible while no product, archived ones included, is in it"
                    onClick={() => void remove(item)}
                  >
                    Delete
                  </button>
                </>
              )}
            </div>
          ))}
          <form className="category-add" onSubmit={(event) => void create(event)}>
            <input
              aria-label="New category name"
              maxLength={100}
              placeholder="New category name"
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
            />
            <button className="primary-button compact-button" disabled={creating || !newName.trim()}>
              {creating ? "Adding…" : "Add"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

function ShelfLifeEditor({ labelTypes, onClose, onSave }: {
  labelTypes: ReferenceData["label_types"];
  onClose: () => void;
  onSave: (input: ShelfLifeDraftInput) => Promise<void>;
}) {
  const [labelTypeId, setLabelTypeId] = useState(labelTypes[0]?.id ?? "");
  const [storageState, setStorageState] = useState<StorageState>("chilled");
  const [durationValue, setDurationValue] = useState("3");
  const [durationUnit, setDurationUnit] = useState<DurationUnit>("days");
  const [calculationMethod, setCalculationMethod] = useState<CalculationMethod>("local_end_of_day");
  const [dateMarkType, setDateMarkType] = useState<DateMarkType>("use_by");
  const [saving, setSaving] = useState(false);
  const parsedDuration = Number(durationValue);
  const validDuration = Number.isInteger(parsedDuration) && parsedDuration >= 0;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!validDuration) return;
    setSaving(true);
    await onSave({
      label_type_id: labelTypeId,
      storage_state: storageState,
      duration_value: parsedDuration,
      duration_unit: durationUnit,
      calculation_method: calculationMethod,
      date_mark_type: dateMarkType,
      site_id: null,
      valid_from: null,
      valid_until: null,
    });
    setSaving(false);
  }

  function changeCalculation(next: CalculationMethod) {
    setCalculationMethod(next);
    if (next !== "elapsed") setDurationUnit("days");
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="product-editor control-editor" role="dialog" aria-modal="true" aria-labelledby="shelf-life-editor-title">
        <header><div><p className="eyebrow">Shelf life</p><h2 id="shelf-life-editor-title">Create a rule draft</h2></div><button className="icon-button" onClick={onClose} aria-label="Close shelf-life editor"><Icon name="close" /></button></header>
        <form onSubmit={submit}>
          <div className="form-row"><label htmlFor="rule-label-type">Label type<select id="rule-label-type" value={labelTypeId} onChange={(event) => setLabelTypeId(event.target.value)}>{labelTypes.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label><label htmlFor="rule-storage">Storage state<select id="rule-storage" value={storageState} onChange={(event) => setStorageState(event.target.value as StorageState)}>{["ambient", "chilled", "frozen", "defrosted"].map((item) => <option key={item} value={item}>{titleCase(item)}</option>)}</select></label></div>
          <div className="form-row"><label htmlFor="rule-duration">Duration<input id="rule-duration" type="number" min="0" step="1" required value={durationValue} onChange={(event) => setDurationValue(event.target.value)} /></label><label htmlFor="rule-unit">Unit<select id="rule-unit" value={durationUnit} onChange={(event) => setDurationUnit(event.target.value as DurationUnit)}><option value="days">Days</option>{calculationMethod === "elapsed" && <option value="hours">Hours</option>}</select></label></div>
          <label htmlFor="rule-calculation">Calculation<select id="rule-calculation" value={calculationMethod} onChange={(event) => changeCalculation(event.target.value as CalculationMethod)}><option value="local_end_of_day">Local end of day</option><option value="calendar_days">Calendar days</option><option value="elapsed">Exact elapsed time</option></select></label>
          <label htmlFor="rule-date-mark">Date mark<select id="rule-date-mark" value={dateMarkType} onChange={(event) => setDateMarkType(event.target.value as DateMarkType)}><option value="use_by">Use by</option><option value="best_before">Best before</option><option value="prepared_on">Prepared on</option><option value="none">No date mark</option></select></label>
          <div className="editor-note"><Icon name="clock" /><span>This creates a draft only. It cannot drive production labels until a separate approval step is completed.</span></div>
          <footer><button type="button" className="secondary-button" onClick={onClose} disabled={saving}>Cancel</button><button className="primary-button" disabled={saving || !labelTypeId || !validDuration}>{saving ? "Creating…" : "Create draft"}</button></footer>
        </form>
      </section>
    </div>
  );
}

function ShelfLifeRevisionEditor({ rule, title, note, saveLabel, onClose, onSave }: {
  rule: Pick<ShelfLifeRule, "version" | "storage_state" | "duration_value" | "duration_unit" | "calculation_method" | "date_mark_type" | "valid_from" | "valid_until"> & { label_type_name?: string };
  title?: string;
  note?: string;
  saveLabel?: string;
  onClose: () => void;
  onSave: (input: ShelfLifeRevisionInput) => Promise<void>;
}) {
  const [durationValue, setDurationValue] = useState(String(rule.duration_value));
  const [durationUnit, setDurationUnit] = useState<DurationUnit>(rule.duration_unit);
  const [calculationMethod, setCalculationMethod] = useState<CalculationMethod>(rule.calculation_method);
  const [dateMarkType, setDateMarkType] = useState<DateMarkType>(rule.date_mark_type);
  const [saving, setSaving] = useState(false);
  const parsedDuration = Number(durationValue);
  const validDuration = Number.isInteger(parsedDuration) && parsedDuration >= 0;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!validDuration) return;
    setSaving(true);
    await onSave({
      expected_version: rule.version,
      duration_value: parsedDuration,
      duration_unit: durationUnit,
      calculation_method: calculationMethod,
      date_mark_type: dateMarkType,
      valid_from: rule.valid_from,
      valid_until: rule.valid_until,
    });
    setSaving(false);
  }

  function changeCalculation(next: CalculationMethod) {
    setCalculationMethod(next);
    if (next !== "elapsed") setDurationUnit("days");
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="product-editor control-editor" role="dialog" aria-modal="true" aria-labelledby="shelf-life-revision-title">
        <header><div><p className="eyebrow">Shelf life</p><h2 id="shelf-life-revision-title">{title ?? `Edit ${rule.label_type_name ?? "shelf life"} · ${titleCase(rule.storage_state)}`}</h2></div><button className="icon-button" onClick={onClose} aria-label="Close shelf-life editor"><Icon name="close" /></button></header>
        <form onSubmit={submit}>
          <label htmlFor="revision-duration">Duration<input id="revision-duration" type="number" min={0} value={durationValue} onChange={(event) => setDurationValue(event.target.value)} required /></label>
          <label htmlFor="revision-unit">Unit<select id="revision-unit" value={durationUnit} disabled={calculationMethod !== "elapsed"} onChange={(event) => setDurationUnit(event.target.value as DurationUnit)}><option value="days">Days</option><option value="hours">Hours</option></select></label>
          <label htmlFor="revision-calculation">Calculation<select id="revision-calculation" value={calculationMethod} onChange={(event) => changeCalculation(event.target.value as CalculationMethod)}><option value="local_end_of_day">Local end of day</option><option value="calendar_days">Calendar days</option><option value="elapsed">Exact elapsed time</option></select></label>
          <label htmlFor="revision-date-mark">Date mark<select id="revision-date-mark" value={dateMarkType} onChange={(event) => setDateMarkType(event.target.value as DateMarkType)}><option value="use_by">Use by</option><option value="best_before">Best before</option><option value="prepared_on">Prepared on</option><option value="none">No date mark</option></select></label>
          <div className="editor-note"><Icon name="clock" /><span>{note ?? "Replaces this rule in one step: the current rule is retired and a new one is approved with these values. Fully audited, same as the manual retire-then-approve sequence."}</span></div>
          <footer><button type="button" className="secondary-button" onClick={onClose} disabled={saving}>Cancel</button><button className="primary-button" disabled={saving || !validDuration}>{saving ? "Saving…" : saveLabel ?? "Save changes"}</button></footer>
        </form>
      </section>
    </div>
  );
}

const ADMIN_NAV_ITEMS: { view: WorkspaceView; label: string; icon: IconName; gate: "departments" | "print" | "team" | "bridge" | "shelfLife" }[] = [
  { view: "departments", label: "Departments", icon: "prep", gate: "departments" },
  { view: "print-history", label: "Print History", icon: "clock", gate: "print" },
  { view: "shelf-life-review", label: "Shelf-life review", icon: "clock", gate: "shelfLife" },
  { view: "team", label: "Team & roles", icon: "shield", gate: "team" },
  { view: "bridge", label: "Print Bridge", icon: "label", gate: "bridge" },
  { view: "printer-admin", label: "Printers", icon: "label", gate: "bridge" },
  { view: "settings", label: "Settings", icon: "shield", gate: "bridge" },
  { view: "system-info", label: "System Info", icon: "shield", gate: "print" },
];

const MOBILE_MORE_ITEMS: { view: WorkspaceView; label: string; icon: IconName; gate: "departments" | "print" | "team" | "bridge" | "shelfLife" }[] = [
  { view: "dashboard", label: "Dashboard", icon: "spark", gate: "print" },
  { view: "prep-lists", label: "Prep Lists", icon: "prep", gate: "print" },
  { view: "ppds-labels", label: "PPDS Labels", icon: "label", gate: "print" },
  { view: "buffet", label: "Buffet Menus", icon: "label", gate: "print" },
  ...ADMIN_NAV_ITEMS,
];

function MobileNav({ siteName, canChangeSite, onChangeSite, activeView, canManageTeam, canManageBridge, canViewDepartments, canPrint, canApproveShelfLife, onView, onSwitch, onSignOut }: { siteName: string; canChangeSite: boolean; onChangeSite: () => void; activeView: WorkspaceView; canManageTeam: boolean; canManageBridge: boolean; canViewDepartments: boolean; canPrint: boolean; canApproveShelfLife: boolean; onView: (view: WorkspaceView) => void; onSwitch: () => void; onSignOut?: () => void }) {
  const [moreOpen, setMoreOpen] = useState(false);
  const gateAllowed = { print: canPrint, team: canManageTeam, bridge: canManageBridge, shelfLife: canApproveShelfLife, departments: canViewDepartments };
  const moreActive = MOBILE_MORE_ITEMS.some((item) => item.view === activeView);

  function chooseMore(view: WorkspaceView) {
    onView(view);
    setMoreOpen(false);
  }

  return (
    <>
      <nav className="mobile-nav">
        <button className={activeView === "catalogue" ? "active" : ""} onClick={() => onView("catalogue")}><Icon name="catalogue" /><span>Catalogue</span></button>
        <button className={activeView === "quick" ? "active" : ""} onClick={() => onView("quick")} disabled={!canPrint}><Icon name="label" /><span>Quick Print</span></button>
        <button className={activeView === "batch" ? "active" : ""} onClick={() => onView("batch")} disabled={!canPrint}><Icon name="catalogue" /><span>Batch Print</span></button>
        <button className={moreActive ? "active" : ""} onClick={() => setMoreOpen(true)}><Icon name="shield" /><span>More</span></button>
      </nav>
      {moreOpen && (
        <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && setMoreOpen(false)}>
          <section className="more-sheet" role="dialog" aria-modal="true" aria-labelledby="mobile-more-title">
            <header><h2 id="mobile-more-title">More</h2><button className="icon-button" onClick={() => setMoreOpen(false)} aria-label="Close menu"><Icon name="close" /></button></header>
            {canChangeSite && (
              <button className="more-sheet-site" onClick={() => { setMoreOpen(false); onChangeSite(); }}>
                <Icon name="label" /><span><small>Site</small><strong>{siteName || "Choose site"}</strong></span><em>Change</em>
              </button>
            )}
            <div className="more-sheet-grid">
              {MOBILE_MORE_ITEMS.map((item) => (
                <button key={item.view} className={activeView === item.view ? "active" : ""} disabled={!gateAllowed[item.gate]} onClick={() => chooseMore(item.view)}>
                  <Icon name={item.icon} /><span>{item.label}</span>
                </button>
              ))}
              <button onClick={() => { setMoreOpen(false); onSwitch(); }}><Icon name="shield" /><span>Switch user</span></button>
              {onSignOut && <button onClick={() => { setMoreOpen(false); onSignOut(); }}><Icon name="close" /><span>Sign out</span></button>}
            </div>
          </section>
        </div>
      )}
    </>
  );
}

function toProductDepartment(department: Department): ProductDetail["departments"][number] {
  return { site_department_id: department.id, department_id: department.department_id, site_id: department.site_id, name: department.name, active: department.active };
}

function LoadingPanel() { return <div className="loading-panel"><span className="spinner" /><strong>Loading the catalogue…</strong></div>; }
function LoadingScreen({ message }: { message: string }) { return <main className="gate-screen"><div className="loading-card"><Mark /><span className="spinner" /><p>{message}</p></div></main>; }
function FatalState({ message, onSignOut }: { message: string; onSignOut: () => void }) { return <main className="gate-screen"><section className="gate-card setup-card"><Icon name="warning" size={28} /><h1>We couldn’t open Kitchen Hub.</h1><p>{message}</p><button className="primary-button" onClick={onSignOut}>Return to sign in</button></section></main>; }

function allergenSummary(containsAllergens: string[] | undefined): string {
  if (!containsAllergens) return "Loading details…";
  if (!containsAllergens.length) return "No allergens declared";
  return containsAllergens.slice(0, 2).join(" · ") + (containsAllergens.length > 2 ? ` +${containsAllergens.length - 2}` : "");
}
function titleCase(value: string): string { return value.charAt(0).toUpperCase() + value.slice(1).replaceAll("_", " "); }
function initialsFromName(name: string): string {
  const initials = name.trim().split(/\s+/).map((part) => part[0]).slice(0, 2).join("");
  return (initials || "?").toUpperCase();
}
function initialsFromEmail(email: string): string {
  const local = email.split("@")[0] ?? "";
  const segments = local.split(/[._-]+/).filter(Boolean);
  const initials = segments.length > 1
    ? segments.slice(0, 2).map((segment) => segment[0]).join("")
    : local.slice(0, 2);
  return (initials || "?").toUpperCase();
}
function durationLabel(value: number, unit: DurationUnit): string { return `${value} ${value === 1 ? unit.slice(0, -1) : unit}`; }
function startOfLocalDay(dateInput: string): string { return new Date(`${dateInput}T00:00:00`).toISOString(); }
function endOfLocalDay(dateInput: string): string { return new Date(`${dateInput}T23:59:59.999`).toISOString(); }
function previewDateMark(rule: ProductDetail["shelf_life_rules"][number] | undefined): { label: string; value: string } {
  if (!rule) return { label: "SHELF LIFE", value: "NOT APPROVED" };
  if (rule.date_mark_type === "none") return { label: "DATE MARK", value: "NONE" };
  if (rule.date_mark_type === "prepared_on") return { label: "PREPARED ON", value: formatShortDate(new Date()) };
  const date = new Date();
  if (rule.duration_unit === "hours") date.setHours(date.getHours() + rule.duration_value);
  else date.setDate(date.getDate() + rule.duration_value);
  return { label: rule.date_mark_type.replaceAll("_", " "), value: formatShortDate(date) };
}
function formatShortDate(date: Date): string { return new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "2-digit" }).format(date).toUpperCase(); }
function todayForInput(date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
function messageFor(reason: unknown): string {
  if (reason instanceof ApiError) return `${reason.message}${reason.requestId ? ` · Request ${reason.requestId}` : ""}`;
  if (reason instanceof Error) return reason.message;
  return "Kitchen Hub could not complete the request.";
}
